import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, getDoc, updateDoc, deleteDoc, deleteField, query, where, orderBy, onSnapshot, writeBatch, increment, runTransaction } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Swal from 'sweetalert2';
import SplitBillModal from './SplitBillModal';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const FULFILLMENT_STATUS_ORDER = {
  pending: 0,
  sent_to_kitchen: 1,
  in_preparation: 2,
  ready: 3,
  served: 4,
  out_for_delivery: 5,
  complete: 6,
};

const FULFILLMENT_STATUSES = [
  { value: 'pending', label: 'Pending', variant: 'warning', icon: 'fa-clock', color: '#856404', bg: '#fff3cd', border: '#ffeeba' },
  { value: 'sent_to_kitchen', label: 'Sent to Kitchen', variant: 'primary', icon: 'fa-utensils', color: '#004085', bg: '#cce5ff', border: '#b8daff' },
  { value: 'in_preparation', label: 'In Preparation', variant: 'info', icon: 'fa-fire', color: '#0c5460', bg: '#d1ecf1', border: '#bee5eb' },
  { value: 'ready', label: 'Ready', variant: 'success', icon: 'fa-check', color: '#155724', bg: '#d4edda', border: '#c3e6cb' },
  { value: 'served', label: 'Served', variant: 'teal', icon: 'fa-utensil-spoon', color: '#fff', bg: '#20c997', border: '#20c997' },
  { value: 'out_for_delivery', label: 'Out for Delivery', variant: 'cyan', icon: 'fa-shipping-fast', color: '#fff', bg: '#17a2b8', border: '#17a2b8' },
  { value: 'complete', label: 'Complete', variant: 'dark', icon: 'fa-check-circle', color: '#fff', bg: '#343a40', border: '#343a40' },
];

const getFulfillmentStatusMeta = (status) => {
  return FULFILLMENT_STATUSES.find(s => s.value === status) || FULFILLMENT_STATUSES[0];
};

const statusOrder = (s) => FULFILLMENT_STATUS_ORDER[s || 'pending'] ?? 0;

const getMinFulfillmentStatus = (items) => {
  if (!items || items.length === 0) return 'pending';
  let minOrder = Infinity;
  let minStatus = 'complete';
  for (const item of items) {
    const s = item.fulfillmentStatus || 'pending';
    const o = statusOrder(s);
    if (o < minOrder) {
      minOrder = o;
      minStatus = s;
    }
  }
  return minStatus;
};

// Hash a PIN with SHA-256 using the provided salt
const hashPin = async (pin, salt) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + salt);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Format elapsed time since a timestamp (Firestore Timestamp or Date)
const formatElapsed = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const PosPage = () => {
  const { posId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  const [user, setUser] = useState(null);
  const [pos, setPos] = useState(null);
  const [session, setSession] = useState(null);
  const [location, setLocation] = useState(null);
  const [orderTypes, setOrderTypes] = useState([]);
  const [selectedOrderType, setSelectedOrderType] = useState('pos');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closingCash, setClosingCash] = useState({ 1: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0 });
  const [employees, setEmployees] = useState([]);
  const [selectedCloseEmployeeId, setSelectedCloseEmployeeId] = useState('');
  const [closeEmployeePin, setCloseEmployeePin] = useState('');
  const [showBackModal, setShowBackModal] = useState(false);
  const [backEmployeeId, setBackEmployeeId] = useState('');
  const [backEmployeePin, setBackEmployeePin] = useState('');
  const [sessionSales, setSessionSales] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const customerDropdownRef = useRef(null);

  // Terminal payment state
  const [terminalPaymentIntentId, setTerminalPaymentIntentId] = useState(null);
  const [showTerminalWaitingModal, setShowTerminalWaitingModal] = useState(false);
  const [terminalStatusMessage, setTerminalStatusMessage] = useState('');
  const [terminalReaderOnline, setTerminalReaderOnline] = useState(false);
  const [checkingTerminal, setCheckingTerminal] = useState(false);
  const terminalPollIntervalRef = useRef(null);
  const isFinalizingSaleRef = useRef(false);

  // Card payment mode: 'terminal' | 'manual'
  const [cardEntryMode, setCardEntryMode] = useState('terminal');

  // Stripe checkout state
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState(null);
  const [stripeCheckoutSessionId, setStripeCheckoutSessionId] = useState(null);
  const [showStripeWaitingModal, setShowStripeWaitingModal] = useState(false);
  const [pendingSaleId, setPendingSaleId] = useState(null);
  const [pendingSaleListener, setPendingSaleListener] = useState(null);
  const stripePollIntervalRef = useRef(null);

  // Cart totals with tax, tip, discount (match SalesPage.js)
  const [tipIsPercent, setTipIsPercent] = useState(true);
  const [tipInput, setTipInput] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [showTipDiscount, setShowTipDiscount] = useState(false);

  // Promo code
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { promoId, code, name, discountAmount }
  const [promoError, setPromoError] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  // Open Tabs state
  const [openTabs, setOpenTabs] = useState([]);
  const [showOpenTabsModal, setShowOpenTabsModal] = useState(false);
  const [selectedOpenTab, setSelectedOpenTab] = useState(null);
  const [showTabDetailModal, setShowTabDetailModal] = useState(false);
  const [showCreateTabModal, setShowCreateTabModal] = useState(false);
  const [tabNameInput, setTabNameInput] = useState('');
  const [tabPaymentMethod, setTabPaymentMethod] = useState('cash');
  const [tabCreating, setTabCreating] = useState(false);
  const [tabCompleting, setTabCompleting] = useState(false);
  const [pendingTabIdForCard, setPendingTabIdForCard] = useState(null); // tracks tab being created when card is pending on terminal
  const [showAddToTabModal, setShowAddToTabModal] = useState(false);
  const [tabCart, setTabCart] = useState([]);
  const [tabTipInput, setTabTipInput] = useState('');
  const [tabTipIsPercent, setTabTipIsPercent] = useState(true);
  const [tabDiscountInput, setTabDiscountInput] = useState('');
  const [tabSelectedCustomer, setTabSelectedCustomer] = useState(null);

  // Floor plan / tables state
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [showTablePickerModal, setShowTablePickerModal] = useState(false);
  const [pickerSectionId, setPickerSectionId] = useState(null);
  const [openTabsViewMode, setOpenTabsViewMode] = useState('list'); // 'list' | 'floorplan'

  // Custom Item modal state
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [customItemDescription, setCustomItemDescription] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [customItemQty, setCustomItemQty] = useState(1);
  const [customItemInstructions, setCustomItemInstructions] = useState('');
  const [customItemWorkzoneId, setCustomItemWorkzoneId] = useState('');

  // Workzones state
  const [workzones, setWorkzones] = useState([]);
  const [showTabCustomerDropdown, setShowTabCustomerDropdown] = useState(false);
  const [tabCustomerSearch, setTabCustomerSearch] = useState('');
  const tabCustomerDropdownRef = useRef(null);

  // Customer-facing tip selection modal for completing tabs
  const [showCustomerTipModal, setShowCustomerTipModal] = useState(false);
  const [customerTipPercent, setCustomerTipPercent] = useState(0);
  const [customerTipAmount, setCustomerTipAmount] = useState(0);
  const [customerTipMode, setCustomerTipMode] = useState('percent'); // 'percent' | 'amount' | 'none'

  // Open tabs search/filter
  const [tabSearchQuery, setTabSearchQuery] = useState('');

  // Split checks state
  const [splitChecks, setSplitChecks] = useState([]);
  const [showSplitBillModal, setShowSplitBillModal] = useState(false);
  const [splitMode, setSplitMode] = useState('equal'); // 'equal' | 'items' | 'amount'
  const [splitEqualParts, setSplitEqualParts] = useState(2);
  const [splitItemAssignments, setSplitItemAssignments] = useState([]);
  const [splitAmounts, setSplitAmounts] = useState(['']);
  const [pendingSplitIdForCard, setPendingSplitIdForCard] = useState(null);
  const [showSplitTipModal, setShowSplitTipModal] = useState(false);
  const [selectedSplitForTip, setSelectedSplitForTip] = useState(null);
  const [splitTipInput, setSplitTipInput] = useState('');
  const [splitTipIsPercent, setSplitTipIsPercent] = useState(true);
  const splitPresentationCancelRequested = useRef(false);

  // Tab add-item flow: options + qty + instructions
  const [tabAddProduct, setTabAddProduct] = useState(null);
  const [showTabAddOptions, setShowTabAddOptions] = useState(false);
  const [tabAddSelectedOptions, setTabAddSelectedOptions] = useState({});
  const [tabAddExtraFees, setTabAddExtraFees] = useState({});
  const [showTabAddQty, setShowTabAddQty] = useState(false);
  const [tabAddQty, setTabAddQty] = useState(1);
  const [tabAddInstructions, setTabAddInstructions] = useState('');
  const [tabAddProductSearch, setTabAddProductSearch] = useState('');

  // POS Calculator (built-in, replaces modal)
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  const [calcMode, setCalcMode] = useState(null); // 'qty' | 'price' | 'discount'
  const [calcValue, setCalcValue] = useState('');

  // Product Options
  const [productOptions, setProductOptions] = useState([]);

  // Helper: resolve workzoneId from a product's category for the current POS location
  const getWorkzoneId = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.workzonesByLocation?.[pos?.locationId] || null;
  };
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsProduct, setOptionsProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({}); // { optionId: Set([name1, name2]) }
  const [optionsExtraFees, setOptionsExtraFees] = useState({}); // { name: fee }
  const [optionsQty, setOptionsQty] = useState(1);
  const [optionsInstructions, setOptionsInstructions] = useState('');

  // Screensaver / Idle lock
  const [screenSaverActive, setScreenSaverActive] = useState(false);
  const [screenSaverPin, setScreenSaverPin] = useState('');
  const [screenSaverSelectedEmployeeId, setScreenSaverSelectedEmployeeId] = useState('');
  const [screenSaverError, setScreenSaverError] = useState('');
  const [screenSaverVerifiedEmp, setScreenSaverVerifiedEmp] = useState(null);
  const [showTimeclockChoice, setShowTimeclockChoice] = useState(false);
  const [timeclockLoading, setTimeclockLoading] = useState(false);
  const lastInteractionTimeRef = useRef(Date.now());
  const screenSaverTimerRef = useRef(null);

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadPosData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (tabCustomerDropdownRef.current && !tabCustomerDropdownRef.current.contains(event.target)) {
        setShowTabCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-fill tab name when customer is selected
  useEffect(() => {
    const customer = tabSelectedCustomer || selectedCustomer;
    if (customer?.name && showCreateTabModal) {
      setTabNameInput(customer.name);
    }
  }, [tabSelectedCustomer, selectedCustomer, showCreateTabModal]);

  // Cleanup polling intervals and listeners on unmount
  useEffect(() => {
    return () => {
      if (terminalPollIntervalRef.current) {
        clearInterval(terminalPollIntervalRef.current);
      }
      if (stripePollIntervalRef.current) {
        clearInterval(stripePollIntervalRef.current);
      }
      if (pendingSaleListener) {
        pendingSaleListener();
      }
    };
  }, [pendingSaleListener]);

  // Load open tabs and tables when POS is ready (tabs are visible across all POSs at this location)
  useEffect(() => {
    if (user && pos && !loading) {
      loadOpenTabs();
      loadTables();
    }
  }, [user, pos, loading]);

  // Idle screensaver timer (10 minutes = 600,000 ms)
  // Uses DOM query for modals so we don't worry about stale closures
  const isAnyModalOrProcessActive = () => {
    const hasVisibleModal = !!document.querySelector('.modal.d-block');
    return (
      hasVisibleModal ||
      !!terminalPollIntervalRef.current ||
      !!stripePollIntervalRef.current ||
      !!pendingSaleListener
    );
  };

  useEffect(() => {
    if (!session || loading || screenSaverActive) return;

    const IDLE_MS = 10 * 60 * 1000; // 10 minutes
    const CHECK_INTERVAL_MS = 5000; // check every 5 seconds

    const resetTimer = () => {
      lastInteractionTimeRef.current = Date.now();
    };

    const checkIdle = () => {
      if (screenSaverActive) return;
      const elapsed = Date.now() - lastInteractionTimeRef.current;
      if (elapsed >= IDLE_MS && !isAnyModalOrProcessActive()) {
        setScreenSaverActive(true);
        setScreenSaverPin('');
        setScreenSaverSelectedEmployeeId('');
        setScreenSaverError('');
        setShowTimeclockChoice(false);
        setScreenSaverVerifiedEmp(null);
        try {
          sessionStorage.setItem('screenSaverActive', 'true');
        } catch (e) {}
      }
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'mousemove', 'scroll'];
    events.forEach(evt => window.addEventListener(evt, resetTimer));

    resetTimer();
    screenSaverTimerRef.current = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      events.forEach(evt => window.removeEventListener(evt, resetTimer));
      if (screenSaverTimerRef.current) clearInterval(screenSaverTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, loading, screenSaverActive]);

  const handleScreenSaverGo = async () => {
    setScreenSaverError('');
    if (!screenSaverSelectedEmployeeId) {
      setScreenSaverError('Please select an employee');
      return;
    }
    const emp = employees.find(e => e.id === screenSaverSelectedEmployeeId);
    if (!emp) {
      setScreenSaverError('Employee not found');
      return;
    }
    // Verify passkey if the employee has one
    if (emp.pinHash) {
      if (!screenSaverPin || screenSaverPin.length < 4) {
        setScreenSaverError('Please enter the passkey (4-6 digits)');
        return;
      }
      const inputHash = await hashPin(screenSaverPin, emp.pinSalt);
      if (inputHash !== emp.pinHash) {
        setScreenSaverError('Incorrect passkey');
        return;
      }
    }
    // Verified — fetch latest employee data (clockedIn status may have changed)
    // then show choice between Unlock POS or Timeclock
    try {
      const db = getFirestore();
      const empDoc = await getDoc(doc(db, 'users', user.uid, 'employees', emp.id));
      const freshEmp = empDoc.exists() ? { id: empDoc.id, ...empDoc.data() } : emp;
      setScreenSaverVerifiedEmp(freshEmp);
      setShowTimeclockChoice(true);
      setScreenSaverError('');
    } catch (e) {
      console.warn('Could not refresh employee status:', e);
      setScreenSaverVerifiedEmp(emp);
      setShowTimeclockChoice(true);
      setScreenSaverError('');
    }
  };

  const unlockPos = () => {
    setScreenSaverActive(false);
    setScreenSaverPin('');
    setScreenSaverSelectedEmployeeId('');
    setScreenSaverError('');
    setScreenSaverVerifiedEmp(null);
    setShowTimeclockChoice(false);
    lastInteractionTimeRef.current = Date.now();
    try {
      sessionStorage.removeItem('screenSaverActive');
    } catch (e) {}
  };

  const handleBackButton = () => {
    setShowBackModal(true);
    setBackEmployeeId('');
    setBackEmployeePin('');
  };

  const verifyBackEmployee = async () => {
    if (!backEmployeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }
    const emp = employees.find(e => e.id === backEmployeeId);
    if (!emp) {
      Swal.fire('Error', 'Employee not found', 'error');
      return;
    }
    // Verify passkey if set
    if (emp.pinHash) {
      if (!backEmployeePin || backEmployeePin.length < 4) {
        Swal.fire('Error', 'Please enter the passkey (4-6 digits)', 'error');
        return;
      }
      const inputHash = await hashPin(backEmployeePin, emp.pinSalt);
      if (inputHash !== emp.pinHash) {
        Swal.fire('Error', 'Incorrect passkey', 'error');
        return;
      }
    }
    // Verified — clear session state and navigate
    try {
      sessionStorage.removeItem('activePosSession');
      sessionStorage.removeItem('screenSaverActive');
    } catch (e) {}
    setShowBackModal(false);
    navigate('/admin/pos');
  };

  // Timeclock helpers
  const getTodayISO = () => new Date().toISOString().split('T')[0];

  const getEmployeeScheduleForToday = async (uid, employeeId) => {
    try {
      const db = getFirestore();
      const today = getTodayISO();
      const schedSnap = await getDocs(
        query(
          collection(db, 'users', uid, 'schedules'),
          where('employeeId', '==', employeeId),
          where('date', '==', today)
        )
      );
      if (schedSnap.empty) return null;
      const sched = { id: schedSnap.docs[0].id, ...schedSnap.docs[0].data() };
      // Look up shift details
      if (sched.shiftId) {
        const shiftDoc = await getDoc(doc(db, 'users', uid, 'shifts', sched.shiftId));
        if (shiftDoc.exists()) {
          const shift = shiftDoc.data();
          return {
            scheduleTimeIn: shift.startHour || null,
            scheduleTimeOut: shift.endHour || null,
          };
        }
      }
      return { scheduleTimeIn: null, scheduleTimeOut: null };
    } catch (e) {
      console.error('Error fetching schedule:', e);
      return { scheduleTimeIn: null, scheduleTimeOut: null };
    }
  };

  const handleClockIn = async () => {
    if (!user || !session || !screenSaverVerifiedEmp) return;
    setTimeclockLoading(true);
    try {
      const db = getFirestore();
      const emp = screenSaverVerifiedEmp;
      const now = new Date();
      const today = getTodayISO();

      const scheduleInfo = await getEmployeeScheduleForToday(user.uid, emp.id);

      const timesheetRef = doc(collection(db, 'users', user.uid, 'employeeTimesheets'));
      await setDoc(timesheetRef, {
        employeeId: emp.id,
        employeeName: emp.name,
        date: today,
        posSessionId: session.id,
        scheduleTimeIn: scheduleInfo?.scheduleTimeIn || null,
        scheduleTimeOut: scheduleInfo?.scheduleTimeOut || null,
        clockIn: now,
        clockOut: null,
        regularHours: 0,
        overtimeHours: 0,
        createdAt: now,
      });

      // Mark employee as clocked in
      await updateDoc(doc(db, 'users', user.uid, 'employees', emp.id), {
        clockedIn: true,
        currentTimesheetId: timesheetRef.id,
        lastClockIn: now,
        updatedAt: now,
      });

      Swal.fire({
        title: 'Clocked In',
        text: `${emp.name} clocked in at ${now.toLocaleTimeString()}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      setShowTimeclockChoice(false);
      setScreenSaverPin('');
      setScreenSaverVerifiedEmp(null);
    } catch (e) {
      console.error('Clock in error:', e);
      Swal.fire('Error', 'Failed to clock in', 'error');
    } finally {
      setTimeclockLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!user || !session || !screenSaverVerifiedEmp) return;
    setTimeclockLoading(true);
    try {
      const db = getFirestore();
      const emp = screenSaverVerifiedEmp;
      const now = new Date();
      const today = getTodayISO();

      // Find the most recent open timesheet for this employee (any date)
      // This supports overnight shifts where clockOut happens the next calendar day
      const tsQuery = query(
        collection(db, 'users', user.uid, 'employeeTimesheets'),
        where('employeeId', '==', emp.id),
        where('clockOut', '==', null),
        orderBy('clockIn', 'desc')
      );
      const tsSnap = await getDocs(tsQuery);

      let timesheetId = null;

      if (!tsSnap.empty) {
        timesheetId = tsSnap.docs[0].id;
        // Just record clockOut — backend will calculate hours with location rules
        await updateDoc(doc(db, 'users', user.uid, 'employeeTimesheets', timesheetId), {
          clockOut: now,
          updatedAt: now,
        });
      } else {
        // Fallback: create a timesheet with only clockOut if none found
        const timesheetRef = doc(collection(db, 'users', user.uid, 'employeeTimesheets'));
        await setDoc(timesheetRef, {
          employeeId: emp.id,
          employeeName: emp.name,
          date: today,
          posSessionId: session.id,
          scheduleTimeIn: null,
          scheduleTimeOut: null,
          clockIn: null,
          clockOut: now,
          rawHours: 0,
          regularHours: 0,
          overtimeHours: 0,
          doubleTimeHours: 0,
          createdAt: now,
        });
        timesheetId = timesheetRef.id;
      }

      // Trigger backend calculation with location-specific overtime rules
      if (timesheetId) {
        try {
          const functions = getFunctions();
          const calcFn = httpsCallable(functions, 'calculateTimesheetHours');
          await calcFn({ timesheetId });
        } catch (calcErr) {
          console.error('Backend calculation error:', calcErr);
          // Don't block the user; clock-out succeeded, calc can be retried
        }
      }

      // Mark employee as clocked out
      await updateDoc(doc(db, 'users', user.uid, 'employees', emp.id), {
        clockedIn: false,
        currentTimesheetId: null,
        lastClockOut: now,
        updatedAt: now,
      });

      Swal.fire({
        title: 'Clocked Out',
        text: `${emp.name} clocked out at ${now.toLocaleTimeString()}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      setShowTimeclockChoice(false);
      setScreenSaverPin('');
      setScreenSaverVerifiedEmp(null);
    } catch (e) {
      console.error('Clock out error:', e);
      Swal.fire('Error', 'Failed to clock out', 'error');
    } finally {
      setTimeclockLoading(false);
    }
  };

  const loadPosData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      
      // Load POS
      const posDoc = await getDoc(doc(db, 'users', uid, 'pos', posId));
      if (!posDoc.exists()) {
        Swal.fire('Error', 'POS not found', 'error');
        navigate('/admin/pos');
        return;
      }
      const posData = { id: posDoc.id, ...posDoc.data() };
      setPos(posData);
      
      // Load / validate session
      let activeSession = null;
      let resolvedSessionId = sessionId;

      if (resolvedSessionId) {
        const sessionDoc = await getDoc(doc(db, 'users', uid, 'posSessions', resolvedSessionId));
        if (sessionDoc.exists()) {
          const s = { id: sessionDoc.id, ...sessionDoc.data() };
          if (s.status === 'open') {
            activeSession = s;
          }
        }
      }

      // If no valid session in URL, try sessionStorage
      if (!activeSession) {
        try {
          const saved = sessionStorage.getItem('activePosSession');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.posId === posId && parsed.sessionId) {
              const sessionDoc = await getDoc(doc(db, 'users', uid, 'posSessions', parsed.sessionId));
              if (sessionDoc.exists()) {
                const s = { id: sessionDoc.id, ...sessionDoc.data() };
                if (s.status === 'open') {
                  activeSession = s;
                  resolvedSessionId = parsed.sessionId;
                }
              }
            }
          }
        } catch (e) {
          console.warn('Error reading saved session:', e);
        }
      }

      // If still no valid session, try to find an open one for this POS
      if (!activeSession) {
        const sessionsSnap = await getDocs(
          query(collection(db, 'users', uid, 'posSessions'), where('posId', '==', posId), where('status', '==', 'open'))
        );
        if (!sessionsSnap.empty) {
          activeSession = { id: sessionsSnap.docs[0].id, ...sessionsSnap.docs[0].data() };
          resolvedSessionId = activeSession.id;
        }
      }

      if (!activeSession) {
        setLoading(false);
        Swal.fire({
          title: 'No Active Session',
          text: 'Please start a session from the Point of Sales page.',
          icon: 'warning',
          confirmButtonText: 'Go Back',
        }).then(() => {
          navigate('/admin/pos');
        });
        return;
      }

      setSession(activeSession);

      // Persist active session so reloads stay in the POS
      try {
        sessionStorage.setItem('activePosSession', JSON.stringify({ posId, sessionId: resolvedSessionId }));
      } catch (e) {
        console.warn('Error saving session:', e);
      }

      // Restore screensaver state if it was active before reload
      try {
        if (sessionStorage.getItem('screenSaverActive') === 'true') {
          setScreenSaverActive(true);
          setScreenSaverPin('');
          setScreenSaverSelectedEmployeeId('');
          setScreenSaverError('');
        }
      } catch (e) {
        console.warn('Error restoring screensaver state:', e);
      }
      
      // Load location
      if (posData.locationId) {
        const locDoc = await getDoc(doc(db, 'users', uid, 'locations', posData.locationId));
        if (locDoc.exists()) {
          setLocation({ id: locDoc.id, ...locDoc.data() });
        }

        // Load workzones for this location
        const wzSnap = await getDocs(
          query(collection(db, 'users', uid, 'locations', posData.locationId, 'workzones'), orderBy('order'))
        );

        // Order types for this location (native POS requires picking one when
        // configured — drives reporting + KDS tickets)
        try {
          const otSnap = await getDocs(collection(db, 'users', uid, 'locations', posData.locationId, 'orderTypes'));
          const ots = otSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.enabled !== false);
          setOrderTypes(ots);
          if (ots.length > 0) setSelectedOrderType(ots[0].name || 'pos');
        } catch (e) { console.warn('orderTypes load failed:', e.message); }
        setWorkzones(wzSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Load categories & products (same structure as SalesPage.js)
        const catSnap = await getDocs(collection(db, 'users', uid, 'categories'));
        const catsAll = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Location scoping (matches native POS + kiosk): a category/product
        // with availableLocations set sells ONLY there; empty/missing = all.
        const locId = pos?.locationId;
        const visibleAtLoc = (locs) => !Array.isArray(locs) || locs.length === 0 || locs.includes(locId);
        const cats = catsAll.filter(c => visibleAtLoc(c.availableLocations));

        const allProducts = [];
        for (const cat of cats) {
          const prodSnap = await getDocs(
            query(collection(db, 'users', uid, 'categories', cat.id, 'products'), orderBy('order'))
          );
          prodSnap.docs.forEach(d => {
            const pd = d.data();
            if (!visibleAtLoc(pd.availableLocations)) return;
            allProducts.push({ id: d.id, categoryId: cat.id, category: cat.name || cat.id, ...pd });
          });
        }
        setProducts(allProducts);

        // Store full category objects for image display
        setCategories(cats.filter(c => c.name || c.id));
      }
      
      // Load product options
      const optSnap = await getDocs(collection(db, 'users', uid, 'productOptions'));
      setProductOptions(optSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load customers
      const custSnapshot = await getDocs(collection(db, 'users', uid, 'customers'));
      setCustomers(custSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      
      // Load session sales
      if (sessionId) {
        const salesSnapshot = await getDocs(
          query(
            collection(db, 'users', uid, 'posSessions', sessionId, 'sales'),
            orderBy('createdAt', 'desc')
          )
        );
        setSessionSales(salesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }

      // Check for any pending Stripe checkout sales from this session that may need recovery
      try {
        const pendingQuery = query(
          collection(db, 'users', uid, 'pendingSales'),
          where('sessionId', '==', activeSession.id)
        );
        const pendingSnap = await getDocs(pendingQuery);
        const pendingDocs = pendingSnap.docs
          .filter(d => d.data().status === 'pending_payment' && d.data().posId === posId)
          .sort((a, b) => {
            const aTime = a.data().createdAt?.toMillis ? a.data().createdAt.toMillis() : 0;
            const bTime = b.data().createdAt?.toMillis ? b.data().createdAt.toMillis() : 0;
            return bTime - aTime;
          });

        if (pendingDocs.length > 0) {
          const pendingDoc = pendingDocs[0];
          const pendingData = pendingDoc.data();

          const recoverResult = await Swal.fire({
            title: 'Incomplete Checkout',
            html: `A Stripe checkout was started for <b>${pendingData.confirmationNumber || 'this sale'}</b> but hasn't been completed.<br>Would you like to check its status?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Check Status',
            cancelButtonText: 'Dismiss',
            confirmButtonColor: '#ff6b35',
          });

          if (recoverResult.isConfirmed) {
            const functions = getFunctions();
            const recoverFn = httpsCallable(functions, 'recoverPendingSale');
            const recoverResponse = await recoverFn({ pendingSaleId: pendingDoc.id });
            const { status, saleId } = recoverResponse.data;

            if (status === 'completed') {
              await reloadSessionSales();
              Swal.fire({
                title: 'Sale Recovered!',
                text: `The checkout was completed. Sale #${saleId?.slice(-6) || ''}`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
              });
            } else if (status === 'canceled') {
              Swal.fire('Canceled', 'The checkout was canceled by the customer.', 'info');
            } else {
              // Still pending — resume waiting UI and re-establish listener + polling
              setPendingSaleId(pendingDoc.id);
              setStripeCheckoutSessionId(pendingData.stripeCheckoutSessionId || null);
              setStripeCheckoutUrl(pendingData.stripeCheckoutUrl || null);
              setShowStripeWaitingModal(true);

              const unsubscribe = listenToPendingSale(pendingDoc.id);
              setPendingSaleListener(() => unsubscribe);

              if (pendingData.stripeCheckoutSessionId) {
                pollStripeCheckoutStatus(pendingData.stripeCheckoutSessionId, pendingDoc.id);
              }
            }
          }
        }
      } catch (pendingErr) {
        console.error('Error checking pending sales on load:', pendingErr);
      }
      // Load all active employees (all roles can clock in/out; role check is done per-action)
      try {
        const empSnapshot = await getDocs(
          query(collection(db, 'users', uid, 'employees'), where('status', '==', 'active'), orderBy('name'))
        );
        const allActiveEmps = empSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setEmployees(allActiveEmps);
      } catch (empErr) {
        console.error('Error loading employees:', empErr);
        setEmployees([]);
      }
    } catch (e) {
      console.error('Error loading POS data:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadOpenTabs = async () => {
    if (!user || !pos) return;
    try {
      const functions = getFunctions();
      const getOpenTabsFn = httpsCallable(functions, 'getOpenTabs');
      // Query by locationId so all POSs at this location see the same tabs
      const result = await getOpenTabsFn({ locationId: pos.locationId });
      const tabs = result.data.tabs || [];
      console.log('[loadOpenTabs] loaded', tabs.length, 'tabs. Sample table fields:', tabs.slice(0, 3).map(t => ({ id: t.id, tableId: t.tableId, tableName: t.tableName })));
      setOpenTabs(tabs);
    } catch (e) {
      console.error('Error loading open tabs:', e);
    }
  };

  const loadTables = async () => {
    if (!user || !pos) return;
    try {
      const db = getFirestore();
      const [sectionsSnap, tablesSnap] = await Promise.all([
        getDocs(query(collection(db, 'users', user.uid, 'locations', pos.locationId, 'sections'), orderBy('order'))),
        getDocs(collection(db, 'users', user.uid, 'locations', pos.locationId, 'tables')),
      ]);
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTables(tablesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading tables:', e);
    }
  };

  const filteredTabs = openTabs.filter(tab => {
    const q = tabSearchQuery.toLowerCase();
    return !q ||
      tab.tabName?.toLowerCase().includes(q) ||
      tab.customerName?.toLowerCase().includes(q) ||
      tab.posName?.toLowerCase().includes(q);
  });

  // Mode-aware stock check — same resolution as the backend depletion
  // trigger and the native POS/kiosk: explicit inventoryMode wins; legacy =
  // non-empty recipe → 'recipe' (availability comes from `available`, which
  // the backend auto-86s when ingredients run out); else trackStock → 'stock'.
  const isInStock = (p) => {
    const recipe = Array.isArray(p.recipe) ? p.recipe : [];
    const mode = (p.inventoryMode === 'stock' || p.inventoryMode === 'recipe')
      ? p.inventoryMode
      : (recipe.length > 0 ? 'recipe' : (p.trackStock === true ? 'stock' : null));
    if (mode !== 'stock') return true;
    if (p.stockScope === 'byLocation') {
      const m = p.stockByLocation || {};
      return (Number(m[pos?.locationId]) || 0) > 0;
    }
    return (Number(p.stockQty) || 0) > 0;
  };

  const filteredProducts = products.filter(p => {
    // Merchant-disabled or auto-86'd products never sell (native parity).
    if (p.available === false) return false;
    const matchesCategory =
      selectedCategory === 'all' ? true :
      selectedCategory === '__deals__' ? p.onSale === true :
      p.category === selectedCategory;
    const matchesSearch = !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Out-of-stock products stay VISIBLE but disabled (native shows an
  // "OUT OF STOCK" badge) — staff can see what would sell.
  const isProductSellable = (p) => p.available !== false && isInStock(p);

  const addToCart = (product) => {
    if (!isProductSellable(product)) return; // out of stock / 86'd (native parity)
    // Show product detail modal for ALL products so user can set qty + instructions
    setOptionsProduct(product);
    setSelectedOptions({});
    setOptionsExtraFees({});
    setOptionsQty(1);
    setOptionsInstructions('');
    setShowOptionsModal(true);
  };

  const finalizeAddToCart = (product, options, extraFeeTotal, qty = 1, instructions = '') => {
    const basePrice = product.onSale && product.salePrice !== undefined ? product.salePrice : (product.price || 0);
    const finalPrice = basePrice + extraFeeTotal;
    const lineTotal = finalPrice * qty;
    const trimmedInstructions = instructions.trim() || null;

    // Check if same product + options + instructions already in cart
    setCart(prev => {
      const existing = prev.find(item =>
        item.productId === product.id &&
        JSON.stringify(item.options || []) === JSON.stringify(options || []) &&
        item.instructions === trimmedInstructions
      );
      if (existing) {
        return prev.map(item =>
          item.productId === product.id &&
          JSON.stringify(item.options || []) === JSON.stringify(options || []) &&
          item.instructions === trimmedInstructions
            ? { ...item, qty: (item.qty || 0) + qty, total: item.price * ((item.qty || 0) + qty) }
            : item
        );
      }
      return [...prev, {
        productId: product.id,
        productName: product.name,
        productDescription: product.description || '',
        productImage: product.imageUrl || null,
        categoryId: product.categoryId || null,
        categoryName: product.category || '',
        workzoneId: getWorkzoneId(product.categoryId) || null,
        price: finalPrice,
        qty: qty,
        total: lineTotal,
        options: options,
        instructions: trimmedInstructions,
        date: new Date(),
      }];
    });
  };

  // A cart line = product + options + instructions (two lines with the same
  // product but different modifiers are DIFFERENT lines — native parity;
  // productId-only matching edited/removed them together).
  const cartLineKey = (item) => `${item.productId}::${JSON.stringify(item.options || [])}::${item.instructions || ''}`;

  const updateQuantity = (lineKey, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (cartLineKey(item) === lineKey) {
          const newQty = Math.max(0, (item.qty || 0) + delta);
          return { ...item, qty: newQty, total: item.price * newQty };
        }
        return item;
      }).filter(item => item.qty > 0);
    });
  };

  const removeFromCart = (lineKey) => {
    setCart(prev => prev.filter(item => cartLineKey(item) !== lineKey));
  };

  // Calculator functions
  const pressCalc = (key) => {
    if (key === 'C') { setCalcValue(''); return; }
    if (key === '←') { setCalcValue(prev => prev.slice(0, -1)); return; }
    if (key === '+/-') { setCalcValue(prev => prev.startsWith('-') ? prev.slice(1) : '-' + prev); return; }
    if (key === '.') { if (calcValue.includes('.')) return; setCalcValue(prev => prev + '.'); return; }
    setCalcValue(prev => prev + key);
  };

  const applyCalc = () => {
    if (!selectedCartItem || !calcMode || calcValue === '' || calcValue === '-') return;
    const val = parseFloat(calcValue);
    if (isNaN(val)) return;
    setCart(prev => prev.map(item => {
      if (cartLineKey(item) !== cartLineKey(selectedCartItem)) return item;
      if (calcMode === 'qty') {
        const newQty = Math.max(1, val);
        return { ...item, qty: newQty, total: item.price * newQty };
      }
      if (calcMode === 'price') {
        return { ...item, price: val, total: val * item.qty };
      }
      if (calcMode === 'discount') {
        const original = item.originalPrice || item.price;
        const discountAmt = Math.max(0, val);
        const newPrice = Math.max(0, original - discountAmt);
        return {
          ...item,
          originalPrice: original,
          price: newPrice,
          total: newPrice * item.qty,
          discountAmount: discountAmt,
          discountNote: discountAmt > 0 ? `-${formatCurrency(discountAmt)}` : null,
        };
      }
      return item;
    }));
    setCalcValue('');
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + (item.total || (item.price || 0) * (item.qty || 0)), 0);
  const cartCount = cart.reduce((sum, item) => sum + (item.qty || 0), 0);
  const taxRate = location?.taxRate || 0;
  const cartTax = cartSubtotal * (taxRate / 100);
  const cartDiscount = parseFloat(discountInput) || 0;
  const calculatedTip = tipIsPercent
    ? cartSubtotal * ((parseFloat(tipInput) || 0) / 100)
    : (parseFloat(tipInput) || 0);
  const cartTip = calculatedTip;
  const promoDiscount = appliedPromo?.discountAmount || 0;
  // Pre-fee total. This is what actually gets SENT to the card-charging
  // Cloud Functions (processTerminalPayment, createCardPaymentIntent, etc)
  // — they compute and add their OWN Convenience Fee server-side from the
  // location config, so sending a fee-inclusive amount here would double
  // it. See cartConvenienceFee/cartTotal below, which are for *preview*
  // and for cash sales (which never carry a fee) only.
  const cartBaseTotal = cartSubtotal + cartTax + cartTip - cartDiscount - promoDiscount;

  // Convenience Fee (a.k.a. Processing Fee): extra % applied ONLY when
  // paying by card, configured per location. Preview only — the Cloud
  // Functions that actually charge the card re-validate and apply this fee
  // server-side from `cartBaseTotal`, and the real charged amount they
  // return is what gets reconciled into the persisted sale afterward.
  const cartConvenienceFee = paymentMethod === 'card'
    ? (() => {
        const fee = location?.convenienceFee;
        if (!fee || fee.enabled !== true) return 0;
        const percent = parseFloat(fee.percent) || 0;
        if (percent <= 0) return 0;
        return cartBaseTotal * (percent / 100);
      })()
    : 0;

  const cartTotal = cartBaseTotal + cartConvenienceFee;

  // Line items for promo scoping (productId/categoryId/lineTotal)
  const buildPromoLineItems = () => cart.map((item) => ({
    productId: item.productId,
    categoryId: item.categoryId || null,
    lineTotal: item.total || (item.price || 0) * (item.qty || 0),
  }));

  const handleApplyPromo = async () => {
    const code = promoCodeInput.trim();
    if (!code || !location || !pos) return;
    setApplyingPromo(true);
    setPromoError('');
    try {
      const functions = getFunctions();
      const validatePromo = httpsCallable(functions, 'validatePromoCode');
      const { data } = await validatePromo({
        businessId: user.uid,
        locationId: pos.locationId,
        code,
        salesChannel: 'POS',
        items: buildPromoLineItems(),
        cartSubtotal,
        commit: false,
      });
      if (data.success) {
        setAppliedPromo({ ...data, code: code.toUpperCase() });
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || 'This promo code is not valid.');
      }
    } catch (e) {
      console.error('Error validating promo code:', e);
      setPromoError('Could not validate promo code. Please try again.');
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
  };

  // Re-validates + atomically commits the applied promo code (increments its
  // usage counter, records a redemption) right before a sale/tab is actually
  // created — mirrors the same pattern used in public-web's CheckoutPage.js.
  // Returns { discountAmount, promoId, promoCode } (discountAmount 0 if no
  // promo applied), or null if the commit failed (caller should abort).
  const commitPromoCode = async (silent = false) => {
    if (!appliedPromo) return { discountAmount: 0, promoId: null, promoCode: null };
    try {
      const functions = getFunctions();
      const validatePromo = httpsCallable(functions, 'validatePromoCode');
      const { data } = await validatePromo({
        businessId: user.uid,
        locationId: pos.locationId,
        code: appliedPromo.code,
        salesChannel: 'POS',
        items: buildPromoLineItems(),
        cartSubtotal,
        commit: true,
      });
      if (!data.success) {
        console.warn('Promo commit failed:', data.message);
        if (!silent) {
          setAppliedPromo(null);
          Swal.fire('Promo Code Issue', data.message || 'This promo code could not be applied. Please remove it and try again.', 'warning');
          return null;
        }
        return { discountAmount: 0, promoId: null, promoCode: null };
      }
      return { discountAmount: data.discountAmount, promoId: data.promoId, promoCode: appliedPromo.code };
    } catch (e) {
      console.error('Error committing promo code:', e);
      if (!silent) {
        Swal.fire('Error', 'Could not apply promo code. Please try again.', 'error');
        return null;
      }
      return { discountAmount: 0, promoId: null, promoCode: null };
    }
  };

  // Check terminal reader status when card payment is selected
  useEffect(() => {
    if (paymentMethod === 'card' && pos?.stripeTerminalId) {
      checkTerminalStatus();
    } else {
      setTerminalReaderOnline(false);
      setCheckingTerminal(false);
    }
  }, [paymentMethod]);

  const checkTerminalStatus = async () => {
    if (!pos?.stripeTerminalId) return;
    setCheckingTerminal(true);
    try {
      const functions = getFunctions();
      const getTerminalReaderStatusFn = httpsCallable(functions, 'getTerminalReaderStatus');
      const result = await getTerminalReaderStatusFn({ stripeTerminalId: pos.stripeTerminalId });
      setTerminalReaderOnline(result.data.status === 'online');
    } catch (error) {
      console.error('Error checking terminal status:', error);
      setTerminalReaderOnline(false);
    } finally {
      setCheckingTerminal(false);
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setPaymentMethod('cash');
    setCashReceived('');
    setTerminalPaymentIntentId(null);
    setTerminalReaderOnline(false);
    setCheckingTerminal(false);
    setCardEntryMode('terminal');
    setStripeCheckoutUrl(null);
    setStripeCheckoutSessionId(null);
    setShowStripeWaitingModal(false);
    setShowPaymentModal(true);
  };

  // ========== OPEN TABS FUNCTIONS ==========

  const handleOpenTab = () => {
    if (cart.length === 0) return;
    setTabNameInput('');
    setTabPaymentMethod('cash');
    setSelectedTableId(null);
    setShowTablePickerModal(false);
    setShowCreateTabModal(true);
  };

  const closeCreateTabModal = () => {
    setShowCreateTabModal(false);
    setTabNameInput('');
    setTabSelectedCustomer(null);
    setTabPaymentMethod('cash');
    setSelectedTableId(null);
    setShowTablePickerModal(false);
  };

  const createOpenTabFromCart = async () => {
    if (!user || !pos || !session) return;
    if (!tabNameInput.trim()) {
      Swal.fire('Error', 'Please enter a tab name', 'error');
      return;
    }
    setTabCreating(true);
    try {
      // Commit the promo code now — safe to abort here if it fails, since
      // creating a tab never finalizes a card charge (card tabs only place
      // a pre-authorization, which is simply never captured if we bail out).
      let committedPromoDiscount = 0;
      let committedPromoId = null;
      let committedPromoCode = null;
      if (appliedPromo) {
        const promoResult = await commitPromoCode();
        if (!promoResult) {
          setTabCreating(false);
          return;
        }
        committedPromoDiscount = promoResult.discountAmount;
        committedPromoId = promoResult.promoId;
        committedPromoCode = promoResult.promoCode;
      }
      const combinedDiscount = cartDiscount + committedPromoDiscount;

      const db = getFirestore();
      // Generate guest number
      let guestNumber = null;
      if (pos?.locationId && user?.uid) {
        const locRef = doc(db, 'users', user.uid, 'locations', pos.locationId);
        try {
          guestNumber = await runTransaction(db, async (transaction) => {
            const locDoc = await transaction.get(locRef);
            let ordersPosition = locDoc.data()?.ordersPosition || 0;
            const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
            ordersPosition++;
            // Wrap past the raw restart point (matches every other surface)
            if (ordersPosition > ordersCorrelative) ordersPosition = 1;
            transaction.update(locRef, { ordersPosition });
            return ordersPosition.toString().padStart(4, '0');
          });
        } catch (e) {
          console.error('Error generating guest number for open tab:', e);
        }
      }

      const functions = getFunctions();
      const createOpenTabFn = httpsCallable(functions, 'createOpenTab');

      const result = await createOpenTabFn({
        items: cart.map(item => ({
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription || '',
          productImage: item.productImage || null,
          qty: item.qty || 0,
          price: item.price || 0,
          total: item.total || 0,
          options: item.options || [],
          instructions: item.instructions || null,
          categoryId: item.categoryId || null,
          categoryName: item.categoryName || '',
          workzoneId: item.workzoneId || null,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        })),
        totals: {
          subtotal: cartSubtotal,
          tax: cartTax,
          tip: cartTip,
          tipPercent: parseFloat(tipInput) || 0,
          tipIsPercent: tipIsPercent,
          discount: combinedDiscount,
          total: cartTotal,
          itemsQty: cartCount,
        },
        promoId: committedPromoId,
        promoCode: committedPromoCode,
        customerInfo: {
          customerId: tabSelectedCustomer?.id || selectedCustomer?.id || null,
          fodyesUid: tabSelectedCustomer?.fodyesUid || selectedCustomer?.fodyesUid || null,
          customerName: tabSelectedCustomer?.name || selectedCustomer?.name || 'Walk-in Customer',
          customerEmail: tabSelectedCustomer?.email || selectedCustomer?.email || '',
          customerPhone: tabSelectedCustomer?.phone || selectedCustomer?.phone || '',
        },
        posId: pos.id,
        posName: pos?.posName || '',
        sessionId: sessionId,
        locationId: pos.locationId,
        locationName: location?.companyName || location?.locationName || '',
        tabName: tabNameInput.trim(),
        paymentMethod: tabPaymentMethod,
        guestNumber: guestNumber || null,
        tableId: selectedTableId || null,
        tableName: tables.find(t => t.id === selectedTableId)?.name || null,
      });

      console.log('[createOpenTab] payload table:', { selectedTableId, tableName: tables.find(t => t.id === selectedTableId)?.name });
      console.log('[createOpenTab] payload fulfillment:', { defaultFs: location?.defaultFulfillmentStatus, firstItemFs: cart[0]?.fulfillmentStatus });

      const openTabId = result.data.openTabId;

      // Create a parallel location sales doc for the open tab
      try {
        const db = getFirestore();
        const timestamp = new Date();
        const confirmationNumber = `TAB-${timestamp.getTime().toString().slice(-6)}`;
        const saleId = openTabId; // Use same ID as open tab for easy linking

        const saleItems = cart.map(item => ({
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription || '',
          productImage: item.productImage || null,
          qty: item.qty || 0,
          price: item.price || 0,
          total: item.total || 0,
          options: item.options || [],
          instructions: item.instructions || null,
          categoryId: item.categoryId || null,
          categoryName: item.categoryName || '',
          workzoneId: item.workzoneId || null,
          date: timestamp,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        }));

        const saleData = {
          id: saleId,
          userId: user.uid,
          locationId: pos.locationId,
          locationName: location?.companyName || location?.locationName || '',
          posId: pos.id,
          posSessionId: sessionId,
          salesChannel: 'POS',
          customerId: tabSelectedCustomer?.id || selectedCustomer?.id || null,
          fodyesUid: tabSelectedCustomer?.fodyesUid || selectedCustomer?.fodyesUid || null,
          customerName: tabSelectedCustomer?.name || selectedCustomer?.name || 'Walk-in Customer',
          customerEmail: tabSelectedCustomer?.email || selectedCustomer?.email || '',
          customerPhone: tabSelectedCustomer?.phone || selectedCustomer?.phone || '',
          orderType: selectedOrderType || 'pos',
          isGuestCustomer: !(tabSelectedCustomer || selectedCustomer),
          confirmationNumber,
          guestNumber: guestNumber || null,
          modifiedAt: timestamp,
          closeAt: null,
          paymentType: tabPaymentMethod,
          paymentMethod: tabPaymentMethod,
          status: 'open',
          paymentStatus: 'pending',
          orderStatus: 'Open',
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
          itemsQty: cartCount,
          subtotal: cartSubtotal,
          tax: cartTax,
          tip: cartTip,
          tipPercent: parseFloat(tipInput) || 0,
          tipIsPercent: tipIsPercent,
          discount: combinedDiscount,
          promoId: committedPromoId,
          promoCode: committedPromoCode,
          total: cartTotal,
          deliveryAddress: null,
          deliveryCity: null,
          deliveryState: null,
          deliveryZipCode: null,
          deliveryCountry: null,
          deliveryLatitude: null,
          deliveryLongitude: null,
          notes: '',
          tableId: selectedTableId || null,
          tableName: tables.find(t => t.id === selectedTableId)?.name || null,
          openTabId: saleId,
          orderDate: timestamp,
          createdAt: timestamp,
          updatedAt: timestamp,
        };

        await setDoc(doc(db, 'users', user.uid, 'locations', pos.locationId, 'sales', saleId), saleData);
        await Promise.all(saleItems.map(item => setDoc(doc(collection(db, 'users', user.uid, 'locations', pos.locationId, 'sales', saleId, 'items')), item)));
      } catch (saleErr) {
        console.error('Error creating parallel sale doc for open tab:', saleErr);
        // Non-blocking: the open tab itself was created successfully
      }

      // If card payment method, present card to terminal for authorization only
      if (tabPaymentMethod === 'card' && pos?.stripeTerminalId) {
        closeCreateTabModal();
        setPendingTabIdForCard(openTabId);
        await presentCardForOpenTab(openTabId);
      } else {
        closeCreateTabModal();
        setCart([]);
        setSelectedCustomer(null);
        setDiscountInput('');
        setAppliedPromo(null);
        setPromoCodeInput('');
        await loadOpenTabs();
        // Auto-open the newly created tab detail
        const db = getFirestore();
        const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', openTabId));
        if (tabDoc.exists()) {
          viewOpenTab({ id: tabDoc.id, ...tabDoc.data() });
        }
        Swal.fire({
          title: 'Tab Opened!',
          text: `Tab "${tabNameInput.trim()}" has been opened.`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error('Error creating open tab:', error);
      Swal.fire('Error', error.message || 'Failed to create open tab', 'error');
    } finally {
      setTabCreating(false);
    }
  };

  const presentCardForOpenTab = async (openTabId) => {
    if (!user || !pos || !session) return;
    try {
      setTerminalStatusMessage('Please tap or insert card on the terminal...');
      setShowTerminalWaitingModal(true);

      const functions = getFunctions();
      const presentCardFn = httpsCallable(functions, 'presentCardForOpenTab');
      const result = await presentCardFn({
        openTabId,
        stripeTerminalId: pos.stripeTerminalId,
        description: `Open Tab - Save Card`,
      });

      const { paymentIntentId } = result.data;
      setTerminalPaymentIntentId(paymentIntentId);
      setTerminalStatusMessage('Card presented. Authorizing...');

      // Poll for PaymentIntent status
      pollOpenTabCardStatus(openTabId, paymentIntentId);
    } catch (error) {
      console.error('Error presenting card for open tab:', error);
      setShowTerminalWaitingModal(false);
      Swal.fire('Error', error.message || 'Failed to present card', 'error');
    }
  };

  const pollOpenTabCardStatus = (openTabId, paymentIntentId) => {
    if (terminalPollIntervalRef.current) {
      clearInterval(terminalPollIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 120;

    const poll = async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(terminalPollIntervalRef.current);
        setShowTerminalWaitingModal(false);
        setTerminalPaymentIntentId(null);
        setPendingTabIdForCard(null);
        // Cancel the pending terminal action so the reader stops waiting
        if (pos?.stripeTerminalId) {
          try {
            const functions = getFunctions();
            const cancelFn = httpsCallable(functions, 'cancelTerminalAction');
            await cancelFn({ stripeTerminalId: pos.stripeTerminalId });
          } catch (cancelErr) {
            console.warn('Error canceling terminal action on timeout:', cancelErr.message);
          }
        }
        // Clean up the open tab so the user can re-present the card
        try {
          const db = getFirestore();
          await updateDoc(doc(db, 'users', user.uid, 'openTabs', openTabId), {
            stripePaymentIntentId: deleteField(),
            status: 'open',
            updatedAt: new Date(),
          });
        } catch (e) {
          console.error('Error cleaning up open tab on timeout:', e);
        }
        Swal.fire('Timeout', 'Card presentation timed out. The tab was created but card was not authorized.', 'warning');
        await loadOpenTabs();
        return;
      }

      try {
        const functions = getFunctions();
        const checkStatusFn = httpsCallable(functions, 'checkOpenTabCardStatus');
        const result = await checkStatusFn({ openTabId, paymentIntentId });
        const { status, cardPresented, cardBrand, cardLast4, lastPaymentError } = result.data;
        const declineCode = lastPaymentError?.declineCode || lastPaymentError?.code || '';

        if (status === 'requires_capture' || cardPresented) {
          clearInterval(terminalPollIntervalRef.current);
          setShowTerminalWaitingModal(false);
          setTerminalPaymentIntentId(null);
          setPendingTabIdForCard(null);
          setCart([]);
          setSelectedCustomer(null);
          // Mark tab as card presented
          try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid, 'openTabs', openTabId), {
              status: 'card_presented',
              updatedAt: new Date(),
            });
          } catch (e) {
            console.error('Error marking tab as card presented:', e);
          }
          await loadOpenTabs();
          // Auto-open the newly created tab detail
          const db = getFirestore();
          const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', openTabId));
          if (tabDoc.exists()) {
            viewOpenTab({ id: tabDoc.id, ...tabDoc.data() });
          }
          Swal.fire({
            title: 'Card Authorized!',
            text: `Tab opened and card (${cardBrand || 'Card'} •••• ${cardLast4 || ''}) has been authorized.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        } else if (status === 'canceled') {
          clearInterval(terminalPollIntervalRef.current);
          setShowTerminalWaitingModal(false);
          setTerminalPaymentIntentId(null);
          setPendingTabIdForCard(null);
          // Clean up the open tab so the user can re-present the card
          try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid, 'openTabs', openTabId), {
              stripePaymentIntentId: deleteField(),
              status: 'open',
              updatedAt: new Date(),
            });
          } catch (e) {
            console.error('Error cleaning up open tab on cancel:', e);
          }
          Swal.fire('Canceled', 'The card presentation was canceled.', 'info');
          await loadOpenTabs();
        } else if (status === 'requires_payment_method') {
          if (lastPaymentError) {
            // PIN-required declines are intermediate states (terminal prompts for PIN, then succeeds)
            if (PIN_REQUIRED_DECLINE_CODES.has(declineCode)) {
              setTerminalStatusMessage('PIN entry required on terminal. Please enter PIN...');
            } else {
              // Real terminal decline — show specific message
              setTerminalStatusMessage(`Card declined: ${lastPaymentError.message || 'Please try a different card.'}`);
            }
          } else {
            setTerminalStatusMessage('Please tap or insert card on the terminal...');
          }
        } else if (status === 'processing') {
          setTerminalStatusMessage('Processing card on terminal...');
        }
      } catch (error) {
        console.error('Error polling open tab card status:', error);
      }
    };

    poll();
    terminalPollIntervalRef.current = setInterval(poll, 3000);
  };

  const viewOpenTab = async (tab) => {
    setSelectedOpenTab(tab);
    setShowTabDetailModal(true);
    setTabCart([]);
    setTabTipInput(tab.tip?.toString() || '');
    setTabTipIsPercent(tab.tipIsPercent !== false);
    setTabDiscountInput(tab.discount?.toString() || '');
    // Load split checks
    loadSplitChecks(tab.id);
  };

  const loadSplitChecks = async (tabId) => {
    if (!user || !tabId) return;
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', user.uid, 'openTabs', tabId, 'splitChecks'));
      const splits = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSplitChecks(splits);
    } catch (e) {
      console.error('Error loading split checks:', e);
      setSplitChecks([]);
    }
  };

  const createSplitChecksFromTab = async (splitsData) => {
    if (!selectedOpenTab || !user) return;
    Swal.fire({
      title: 'Creating Splits...',
      text: 'Please wait while we generate the split checks.',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'createSplitChecks');
      await fn({ openTabId: selectedOpenTab.id, checks: splitsData });
      await loadSplitChecks(selectedOpenTab.id);
      Swal.close();
    } catch (error) {
      Swal.close();
      console.error('Error creating split checks:', error);
      Swal.fire('Error', error.message || 'Failed to create splits', 'error');
    }
  };

  const openSplitTipModal = (split) => {
    setSelectedSplitForTip(split);
    setSplitTipInput('');
    setSplitTipIsPercent(true);
    setShowSplitTipModal(true);
  };

  const applySplitTipAndPresentCard = async () => {
    if (!selectedSplitForTip || !selectedOpenTab) return;
    const split = selectedSplitForTip;
    const tipAmount = splitTipIsPercent
      ? (split.subtotal || 0) * ((parseFloat(splitTipInput) || 0) / 100)
      : (parseFloat(splitTipInput) || 0);
    const roundedTip = Math.round(tipAmount * 100) / 100;
    const newTotal = (split.total || 0) + roundedTip;

    try {
      const db = getFirestore();
      const splitRef = doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', split.id);
      await updateDoc(splitRef, {
        tip: roundedTip,
        updatedAt: new Date(),
      });

      // Sync parent tab tip from all splits
      await syncTabTipFromSplits(selectedOpenTab.id);

      setShowSplitTipModal(false);
      setSelectedSplitForTip(null);
      await loadSplitChecks(selectedOpenTab.id);

      // Now present the card
      if (!pos?.stripeTerminalId) {
        Swal.fire('Error', 'No Stripe terminal configured.', 'error');
        return;
      }

      // Show "present card" modal with Cancel option
      splitPresentationCancelRequested.current = false;

      // Start the presentation request FIRST so it's already in flight
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'presentCardForSplit');
      const presentPromise = fn({
        openTabId: selectedOpenTab.id,
        splitId: split.id,
        amount: newTotal,
        stripeTerminalId: pos.stripeTerminalId,
        description: `Split - ${selectedOpenTab.tabName}`,
      });

      // THEN show the Swal modal so it stays open during the entire flow
      // (presentation request returns quickly; actual waiting happens in polling)
      const presentSwal = Swal.fire({
        title: 'Please Present Card',
        text: 'Ask the customer to tap or insert their card on the terminal.',
        icon: 'info',
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        cancelButtonColor: '#dc3545',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Wait for presentation request to complete (fast) while Swal is open
      let result;
      try {
        result = await presentPromise;
      } catch (error) {
        // If user already cancelled, just swallow the error
        if (splitPresentationCancelRequested.current) return;
        Swal.close();
        console.error('Error presenting card for split:', error);
        Swal.fire('Error', error.message || 'Failed to present card', 'error');
        return;
      }

      // If user cancelled while presentation request was in flight
      if (splitPresentationCancelRequested.current) {
        setPendingSplitIdForCard(null);
        return;
      }

      // Start polling — Swal stays open; polling will close it on success/timeout
      setPendingSplitIdForCard(split.id);
      pollSplitCardStatus(split.id, result.data.paymentIntentId);

      // Handle user clicking Cancel on the Swal
      presentSwal.then(async (swalResult) => {
        if (swalResult.dismiss === Swal.DismissReason.cancel) {
          splitPresentationCancelRequested.current = true;
          setPendingSplitIdForCard(null);
          try {
            const cancelFn = httpsCallable(functions, 'cancelTerminalAction');
            await cancelFn({ stripeTerminalId: pos.stripeTerminalId });
          } catch (cancelErr) {
            console.warn('Error canceling terminal action:', cancelErr.message);
          }
          Swal.fire('Canceled', 'Card presentation was canceled.', 'warning');
        }
      });
    } catch (error) {
      console.error('Error applying tip and presenting card:', error);
      Swal.fire('Error', error.message || 'Failed to process', 'error');
    }
  };

  const presentCardForSplitCheck = async (splitId, amount) => {
    if (!selectedOpenTab || !pos?.stripeTerminalId) return;
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'presentCardForSplit');
      const result = await fn({
        openTabId: selectedOpenTab.id,
        splitId,
        amount,
        stripeTerminalId: pos.stripeTerminalId,
        description: `Split - ${selectedOpenTab.tabName}`,
      });
      setPendingSplitIdForCard(splitId);
      pollSplitCardStatus(splitId, result.data.paymentIntentId);
    } catch (error) {
      console.error('Error presenting card for split:', error);
      Swal.fire('Error', error.message || 'Failed to present card', 'error');
    }
  };

  const pollSplitCardStatus = (splitId, paymentIntentId) => {
    let pollCount = 0;
    const maxPolls = 120;

    const poll = async () => {
      if (splitPresentationCancelRequested.current) {
        setPendingSplitIdForCard(null);
        return;
      }
      pollCount++;
      if (pollCount > maxPolls) {
        setPendingSplitIdForCard(null);
        // Cancel the pending terminal action so the reader stops waiting
        if (pos?.stripeTerminalId) {
          try {
            const functions = getFunctions();
            const cancelFn = httpsCallable(functions, 'cancelTerminalAction');
            await cancelFn({ stripeTerminalId: pos.stripeTerminalId });
          } catch (cancelErr) {
            console.warn('Error canceling terminal action on timeout:', cancelErr.message);
          }
        }
        // Clean up the split check so the user can re-present the card
        try {
          const db = getFirestore();
          await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', splitId), {
            stripePaymentIntentId: deleteField(),
            status: 'pending',
            updatedAt: new Date(),
          });
          await loadSplitChecks(selectedOpenTab.id);
        } catch (e) {
          console.error('Error cleaning up split check on timeout:', e);
        }
        Swal.close();
        Swal.fire('Timeout', 'Card presentation timed out.', 'warning');
        return;
      }
      try {
        const functions = getFunctions();
        const checkFn = httpsCallable(functions, 'checkOpenTabCardStatus');
        const result = await checkFn({ openTabId: selectedOpenTab.id, paymentIntentId });
        const { status, cardPresented } = result.data;

        if (splitPresentationCancelRequested.current) {
          setPendingSplitIdForCard(null);
          return;
        }

        if (cardPresented || status === 'requires_capture') {
          setPendingSplitIdForCard(null);
          // Mark split as card presented
          try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', splitId), {
              status: 'card_presented',
              updatedAt: new Date(),
            });
          } catch (e) {
            console.error('Error marking split as card presented:', e);
          }
          await loadSplitChecks(selectedOpenTab.id);
          Swal.close();
          Swal.fire('Success', 'Card presented successfully. Add tip and capture when ready.', 'success');
        } else if (status === 'canceled') {
          setPendingSplitIdForCard(null);
          // Clean up the split check so the user can re-present the card
          try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', splitId), {
              stripePaymentIntentId: deleteField(),
              status: 'pending',
              updatedAt: new Date(),
            });
            await loadSplitChecks(selectedOpenTab.id);
          } catch (e) {
            console.error('Error cleaning up split check on cancel:', e);
          }
          Swal.close();
          Swal.fire('Canceled', 'Card presentation was canceled.', 'warning');
        } else {
          setTimeout(poll, 3000);
        }
      } catch (error) {
        console.error('Error polling split card status:', error);
        if (!splitPresentationCancelRequested.current) {
          setTimeout(poll, 3000);
        }
      }
    };
    poll();
  };

  const captureSplitPaymentCheck = async (splitId, finalAmount) => {
    if (!selectedOpenTab) return;
    Swal.fire({
      title: 'Capturing Payment...',
      text: 'Please wait while we process the payment.',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'captureSplitPayment');
      await fn({ openTabId: selectedOpenTab.id, splitId, finalAmount });
      await loadSplitChecks(selectedOpenTab.id);
      await syncTabTipFromSplits(selectedOpenTab.id);
      Swal.close();
      Swal.fire('Success', 'Payment captured successfully.', 'success');
    } catch (error) {
      Swal.close();
      console.error('Error capturing split payment:', error);
      Swal.fire('Error', error.message || 'Failed to capture payment', 'error');
    }
  };

  const clearSplitChecks = async () => {
    if (!selectedOpenTab || !user) return;
    const result = await Swal.fire({
      title: 'Clear Splits?',
      text: 'This will remove all split checks. Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Clear',
    });
    if (!result.isConfirmed) return;
    try {
      const db = getFirestore();
      const splitRef = collection(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks');
      const snapshot = await getDocs(splitRef);
      const batch = writeBatch(db);
      snapshot.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
      setSplitChecks([]);
    } catch (error) {
      console.error('Error clearing splits:', error);
      Swal.fire('Error', error.message || 'Failed to clear splits', 'error');
    }
  };

  const syncTabTipFromSplits = async (tabId) => {
    if (!user) return;
    try {
      const db = getFirestore();
      const splitsSnap = await getDocs(collection(db, 'users', user.uid, 'openTabs', tabId, 'splitChecks'));
      const totalTip = splitsSnap.docs.reduce((sum, d) => sum + (d.data().tip || 0), 0);

      const tabRef = doc(db, 'users', user.uid, 'openTabs', tabId);
      const tabDoc = await getDoc(tabRef);
      if (tabDoc.exists()) {
        const tabData = tabDoc.data();
        const newTotal = (tabData.subtotal || 0) + (tabData.tax || 0) + totalTip - (tabData.discount || 0);
        await updateDoc(tabRef, {
          tip: totalTip,
          total: newTotal,
          updatedAt: new Date(),
        });
        // Refresh local selectedOpenTab state
        setSelectedOpenTab(prev => prev ? { ...prev, tip: totalTip, total: newTotal } : prev);
      }
    } catch (error) {
      console.error('Error syncing tab tip from splits:', error);
    }
  };

  const addItemsToOpenTab = async () => {
    if (!selectedOpenTab || tabCart.length === 0) return;

    // Show loading overlay immediately so there's no gap
    Swal.fire({
      title: 'Saving...',
      text: 'Updating tab and authorizing card',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const functions = getFunctions();
      const addItemsFn = httpsCallable(functions, 'addItemsToOpenTab');

      // Calculate new totals
      const currentItemsTotal = selectedOpenTab.items.reduce((sum, item) => sum + (item.total || 0), 0);
      const newItemsTotal = tabCart.reduce((sum, item) => sum + (item.total || 0), 0);
      const newSubtotal = currentItemsTotal + newItemsTotal;
      const newTax = newSubtotal * (taxRate / 100);
      const newTip = tabTipIsPercent
        ? newSubtotal * ((parseFloat(tabTipInput) || 0) / 100)
        : (parseFloat(tabTipInput) || 0);
      const newDiscount = parseFloat(tabDiscountInput) || 0;
      const newTotal = newSubtotal + newTax + newTip - newDiscount;
      const newItemsQty = (selectedOpenTab.itemsQty || 0) + tabCart.reduce((sum, item) => sum + (item.qty || 0), 0);

      await addItemsFn({
        openTabId: selectedOpenTab.id,
        newItems: tabCart.map(item => ({
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription || '',
          productImage: item.productImage || null,
          qty: item.qty || 0,
          price: item.price || 0,
          total: item.total || 0,
          options: item.options || [],
          instructions: item.instructions || null,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        })),
        newTotals: {
          subtotal: newSubtotal,
          tax: newTax,
          tip: newTip,
          discount: newDiscount,
          total: newTotal,
          itemsQty: newItemsQty,
        },
      });

      // Sync new items to the parallel location sales doc
      try {
        const db = getFirestore();
        const saleId = selectedOpenTab.id;
        const locId = selectedOpenTab.locationId;
        const timestamp = new Date();
        for (const item of tabCart) {
          await setDoc(doc(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items')), {
            productId: item.productId,
            productName: item.productName,
            productDescription: item.productDescription || '',
            productImage: item.productImage || null,
            qty: item.qty || 0,
            price: item.price || 0,
            total: item.total || 0,
            options: item.options || [],
            instructions: item.instructions || null,
            categoryId: item.categoryId || null,
            categoryName: item.categoryName || '',
            workzoneId: item.workzoneId || null,
            date: timestamp,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
          });
        }
        // Update totals on location sale doc and reset fulfillmentStatus since new items were added
        await updateDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId), {
          subtotal: newSubtotal,
          tax: newTax,
          tip: newTip,
          discount: newDiscount,
          total: newTotal,
          itemsQty: newItemsQty,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
          openTabId: saleId,
          updatedAt: timestamp,
        });
      } catch (syncErr) {
        console.error('Error syncing added items to location sale doc:', syncErr);
      }

      setTabCart([]);
      setShowAddToTabModal(false);
      setTabAddProductSearch('');

      // If this is a card tab with a PaymentIntent, auto-increment authorization
      // if the new total exceeds the currently authorized amount.
      if (selectedOpenTab.paymentMethod === 'card' && selectedOpenTab.stripePaymentIntentId && newTotal > (selectedOpenTab.total || 0)) {
        try {
          const incrementFn = httpsCallable(functions, 'incrementOpenTabAuth');
          const incrementResult = await incrementFn({ openTabId: selectedOpenTab.id, newTotal });
          if (incrementResult.data.incremented) {
            console.log('Auto-incremented auth to:', incrementResult.data.newAuthorizedAmount);
          }
        } catch (incrementErr) {
          console.warn('Could not auto-increment authorization:', incrementErr.message);
          Swal.close();
          Swal.fire({
            icon: 'warning',
            title: 'Authorization Limit',
            text: 'Items were added, but the card authorization may not cover the new total. You may need to re-present the card before checkout.',
            timer: 4000,
            showConfirmButton: false,
          });
          // Don't return here; still refresh and show success below
        }
      }

      await loadOpenTabs();

      // Refresh selected tab directly from Firestore to avoid stale state
      try {
        const db = getFirestore();
        const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id));
        if (tabDoc.exists()) {
          setSelectedOpenTab({ id: tabDoc.id, ...tabDoc.data() });
        }
      } catch (refreshErr) {
        console.error('Error refreshing selected tab:', refreshErr);
      }

      Swal.close();
      Swal.fire({
        title: 'Items Added!',
        text: `Added ${tabCart.length} item(s) to the tab.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error adding items to open tab:', error);
      Swal.close();
      Swal.fire('Error', error.message || 'Failed to add items', 'error');
    }
  };

  // Tab add-item helpers (options + qty + instructions)
  const startTabAddProduct = (product) => {
    setTabAddProduct(product);
    setTabAddSelectedOptions({});
    setTabAddExtraFees({});
    setTabAddQty(1);
    setTabAddInstructions('');
    if (product.optionIds?.length > 0) {
      setShowTabAddOptions(true);
    } else {
      setShowTabAddQty(true);
    }
  };

  const finalizeTabAddProduct = () => {
    if (!tabAddProduct) return;
    const extraFeeTotal = Object.values(tabAddExtraFees).reduce((sum, fee) => sum + fee, 0);
    const flatOptions = [];
    for (const [optId, namesSet] of Object.entries(tabAddSelectedOptions)) {
      const optionDef = productOptions.find(o => o.id === optId);
      namesSet.forEach(name => {
        const optItem = optionDef?.options?.find(oi => oi.name === name);
        if (optItem) {
          flatOptions.push({
            optionId: optId,
            optionName: optionDef?.name,
            itemName: name,
            extraFee: optItem.extraFee || 0,
          });
        }
      });
    }
    const basePrice = tabAddProduct.onSale && tabAddProduct.salePrice !== undefined ? tabAddProduct.salePrice : (tabAddProduct.price || 0);
    const finalPrice = basePrice + extraFeeTotal;
    setTabCart(prev => [...prev, {
      productId: tabAddProduct.id,
      productName: tabAddProduct.name,
      productDescription: tabAddProduct.description || '',
      productImage: tabAddProduct.imageUrl || null,
      categoryId: tabAddProduct.categoryId || null,
      categoryName: tabAddProduct.category || '',
      workzoneId: getWorkzoneId(tabAddProduct.categoryId) || null,
      price: finalPrice,
      qty: tabAddQty,
      total: finalPrice * tabAddQty,
      options: flatOptions,
      instructions: tabAddInstructions || null,
      date: new Date(),
    }]);
    setShowTabAddOptions(false);
    setShowTabAddQty(false);
    setTabAddProduct(null);
  };

  const completeOpenTab = async () => {
    if (!selectedOpenTab) return;
    setTabCompleting(true);
    Swal.fire({
      title: 'Completing Tab...',
      text: 'Please wait while we finalize the order.',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      // Get freshest tab data from Firestore before completing
      const db = getFirestore();
      const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id));
      if (!tabDoc.exists()) {
        Swal.close();
        Swal.fire('Error', 'Tab no longer exists', 'error');
        return;
      }
      const freshTab = { id: tabDoc.id, ...tabDoc.data() };

      const functions = getFunctions();
      const completeFn = httpsCallable(functions, 'completeOpenTab');

      // Calculate final totals from freshest items
      const tabSubtotal = freshTab.items.reduce((sum, item) => sum + (item.total || 0), 0);
      const tabTax = tabSubtotal * (taxRate / 100);
      // Use customer-facing tip modal selection if active, otherwise fall back to manual input
      const tabTip = showCustomerTipModal ? customerTipAmount : (
        tabTipIsPercent
          ? tabSubtotal * ((parseFloat(tabTipInput) || 0) / 100)
          : (parseFloat(tabTipInput) || 0)
      );
      const tabDiscount = parseFloat(tabDiscountInput) || 0;
      const tabTotal = tabSubtotal + tabTax + tabTip - tabDiscount;

      const result = await completeFn({
        openTabId: selectedOpenTab.id,
        finalTotals: {
          subtotal: tabSubtotal,
          tax: tabTax,
          tip: tabTip,
          discount: tabDiscount,
          total: tabTotal,
          itemsQty: freshTab.items.reduce((sum, item) => sum + (item.qty || 0), 0),
        },
      });

      Swal.close();

      // If the final total exceeded the pre-authorized amount, the backend created
      // a new PaymentIntent and sent it to the terminal for a fallback charge.
      if (result.data?.requiresTerminalPayment) {
        // Close any other modals first so the terminal modal isn't hidden behind them
        setShowCustomerTipModal(false);
        setShowTabDetailModal(false);
        setTerminalStatusMessage(result.data.message || 'Please tap or insert card on the terminal...');
        setShowTerminalWaitingModal(true);

        // Poll the new PaymentIntent on the terminal
        const fallbackPaymentIntentId = result.data.paymentIntentId;
        let pollCount = 0;
        const maxPolls = 120;

        const pollFallback = async () => {
          pollCount++;
          if (pollCount > maxPolls) {
            setShowTerminalWaitingModal(false);
            // Cancel the pending terminal action so the reader stops waiting
            if (pos?.stripeTerminalId) {
              try {
                const cancelFn = httpsCallable(functions, 'cancelTerminalAction');
                await cancelFn({ stripeTerminalId: pos.stripeTerminalId });
              } catch (cancelErr) {
                console.warn('Error canceling terminal action on timeout:', cancelErr.message);
              }
            }
            Swal.fire('Timeout', 'Terminal payment timed out. Please try again.', 'warning');
            return;
          }
          try {
            const checkStatusFn = httpsCallable(functions, 'checkTerminalPaymentStatus');
            const statusResult = await checkStatusFn({ paymentIntentId: fallbackPaymentIntentId });
            const { status } = statusResult.data;

            if (status === 'succeeded') {
              setShowTerminalWaitingModal(false);
              setSelectedOpenTab(null);
              await loadOpenTabs();
              await reloadSessionSales();
              Swal.fire({
                title: 'Tab Completed!',
                text: `Total: $${tabTotal.toFixed(2)}`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
              });
            } else if (status === 'canceled') {
              setShowTerminalWaitingModal(false);
              Swal.fire('Payment Failed', 'The card payment was not completed on the terminal.', 'error');
            } else if (status === 'requires_payment_method') {
              setTerminalStatusMessage('Please tap or insert card on the terminal...');
              setTimeout(pollFallback, 3000);
            } else if (status === 'processing') {
              setTerminalStatusMessage('Processing payment on terminal...');
              setTimeout(pollFallback, 3000);
            } else {
              setTimeout(pollFallback, 3000);
            }
          } catch (err) {
            console.error('Error polling fallback terminal payment:', err);
            setTimeout(pollFallback, 3000);
          }
        };

        pollFallback();
        return;
      }

      setShowCustomerTipModal(false);
      setShowTabDetailModal(false);
      setSelectedOpenTab(null);
      await loadOpenTabs();
      await reloadSessionSales();

      Swal.fire({
        title: 'Tab Completed!',
        text: `Total: $${tabTotal.toFixed(2)}`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.close();
      console.error('Error completing open tab:', error);
      setShowCustomerTipModal(false);
      const msg = error.message || 'Failed to complete tab';
      Swal.fire('Error', msg, 'error');
    } finally {
      setTabCompleting(false);
    }
  };

  const removeItemsFromTab = async (itemIndices) => {
    if (!selectedOpenTab || itemIndices.length === 0) return;

    const wouldEmptyTab = selectedOpenTab.items?.length === itemIndices.length;

    // Check if any item being removed has a non-pending fulfillment status
    const nonPendingItems = itemIndices
      .map(idx => selectedOpenTab.items?.[idx])
      .filter(item => item && item.fulfillmentStatus && item.fulfillmentStatus !== 'pending');

    const hasNonPendingItems = nonPendingItems.length > 0;
    const isManagerOrAdmin = !screenSaverVerifiedEmp || ['manager', 'admin', 'owner'].includes(screenSaverVerifiedEmp.role);

    if (hasNonPendingItems && !isManagerOrAdmin) {
      const nonPendingNames = nonPendingItems.map(i => i.productName).join(', ');
      await Swal.fire({
        title: 'Manager Approval Required',
        html: `The following item(s) have already been sent to the kitchen and cannot be removed without manager approval:<br><strong>${nonPendingNames}</strong>`,
        icon: 'warning',
        confirmButtonColor: '#6c757d',
        confirmButtonText: 'OK',
      });
      return;
    }

    const result = await Swal.fire({
      title: wouldEmptyTab ? 'Cancel Tab?' : 'Remove Item?',
      text: wouldEmptyTab
        ? 'This will remove the last item and cancel the tab. The card authorization will be released.'
        : hasNonPendingItems
        ? `Warning: ${nonPendingItems.map(i => i.productName).join(', ')} already sent to kitchen. Are you sure you want to remove?`
        : `Are you sure you want to remove ${itemIndices.length} item(s) from this tab?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: wouldEmptyTab ? 'Cancel Tab' : 'Remove',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      // Show loading overlay immediately
      Swal.fire({
        title: 'Updating...',
        text: 'Removing items from tab',
        imageUrl: '/fodyfee_loading.png',
        imageWidth: 80,
        imageHeight: 80,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const functions = getFunctions();
        const removeItemsFn = httpsCallable(functions, 'removeItemsFromOpenTab');
        const res = await removeItemsFn({
          openTabId: selectedOpenTab.id,
          itemIndices,
        });

        // Sync removal to location sales doc
        try {
          const db = getFirestore();
          const locId = selectedOpenTab.locationId;
          const saleId = selectedOpenTab.id;
          const locSaleRef = doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId);

          // Delete all existing items in location sale subcollection
          const locItemsSnap = await getDocs(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items'));
          await Promise.all(locItemsSnap.docs.map(d => deleteDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items', d.id))));

          // Fetch updated tab to get remaining items
          const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', saleId));
          const remainingItems = tabDoc.exists() ? (tabDoc.data().items || []) : [];
          const timestamp = new Date();

          // Re-write remaining items to location sale subcollection
          await Promise.all(remainingItems.map(item => setDoc(doc(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items')), {
            productId: item.productId || '',
            productName: item.productName || '',
            productDescription: item.productDescription || '',
            productImage: item.productImage || null,
            qty: item.qty || 0,
            price: item.price || 0,
            total: item.total || 0,
            options: item.options || [],
            instructions: item.instructions || null,
            categoryId: item.categoryId || null,
            categoryName: item.categoryName || '',
            workzoneId: item.workzoneId || null,
            date: item.addedAt || timestamp,
            fulfillmentStatus: item.fulfillmentStatus || 'pending',
          })));

          // Update totals on location sale doc
          await updateDoc(locSaleRef, {
            subtotal: res.data.totals?.subtotal || 0,
            tax: res.data.totals?.tax || 0,
            tip: res.data.totals?.tip || 0,
            discount: res.data.totals?.discount || 0,
            total: res.data.totals?.total || 0,
            itemsQty: res.data.totals?.itemsQty || 0,
            openTabId: saleId,
            updatedAt: timestamp,
          });
        } catch (syncErr) {
          console.error('Error syncing item removal to location sale doc:', syncErr);
        }

        await loadOpenTabs();

        if (res.data.tabCanceled) {
          setShowTabDetailModal(false);
          setSelectedOpenTab(null);
          Swal.close();
          Swal.fire({
            title: 'Tab Canceled',
            text: 'The last item was removed and the tab has been canceled.',
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
          });
          return;
        }

        // Refresh selected tab directly from Firestore
        const db = getFirestore();
        const tabDoc = await getDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id));
        if (tabDoc.exists()) {
          setSelectedOpenTab({ id: tabDoc.id, ...tabDoc.data() });
        }
        Swal.close();
        Swal.fire({
          title: 'Removed',
          text: 'Item(s) removed from tab.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error removing items from tab:', error);
        Swal.close();
        Swal.fire('Error', error.message || 'Failed to remove items', 'error');
      }
    }
  };

  const cancelOpenTab = async (tab) => {
    // Check if any item has non-pending fulfillment status
    const hasNonPendingItems = tab.items?.some(item => item.fulfillmentStatus && item.fulfillmentStatus !== 'pending');
    const isManagerOrAdmin = !screenSaverVerifiedEmp || ['manager', 'admin', 'owner'].includes(screenSaverVerifiedEmp.role);

    if (hasNonPendingItems && !isManagerOrAdmin) {
      const nonPendingNames = tab.items
        .filter(item => item.fulfillmentStatus && item.fulfillmentStatus !== 'pending')
        .map(i => i.productName)
        .join(', ');
      await Swal.fire({
        title: 'Manager Approval Required',
        html: `This tab contains item(s) that have already been sent to the kitchen and cannot be canceled without manager approval:<br><strong>${nonPendingNames}</strong>`,
        icon: 'warning',
        confirmButtonColor: '#6c757d',
        confirmButtonText: 'OK',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Cancel Tab?',
      text: hasNonPendingItems
        ? `Warning: This tab contains items already sent to the kitchen. Are you sure you want to cancel "${tab.tabName}"?`
        : `Are you sure you want to cancel "${tab.tabName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Cancel Tab',
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Canceling...',
        text: 'Please wait while we cancel the tab',
        imageUrl: '/fodyfee_loading.png',
        imageWidth: 80,
        imageHeight: 80,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const functions = getFunctions();
        const cancelFn = httpsCallable(functions, 'cancelOpenTab');
        await cancelFn({ openTabId: tab.id });

        // Sync cancellation to location sales doc
        try {
          const db = getFirestore();
          const locId = tab.locationId;
          const saleId = tab.id;
          await updateDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId), {
            status: 'canceled',
            paymentStatus: 'canceled',
            orderStatus: 'Canceled',
            fulfillmentStatus: 'canceled',
            openTabId: saleId,
            modifiedAt: new Date(),
            updatedAt: new Date(),
          });
        } catch (syncErr) {
          console.error('Error syncing tab cancellation to location sale doc:', syncErr);
        }

        await loadOpenTabs();
        if (selectedOpenTab?.id === tab.id) {
          setShowTabDetailModal(false);
          setSelectedOpenTab(null);
        }
        Swal.fire('Canceled', 'The tab has been canceled.', 'info');
      } catch (error) {
        console.error('Error canceling open tab:', error);
        Swal.fire('Error', error.message || 'Failed to cancel tab', 'error');
      }
    }
  };

  const _showFulfillmentStatusPicker = async (tab) => {
    if (!tab) return;
    const currentStatus = tab.fulfillmentStatus || 'pending';
    const statusOptions = FULFILLMENT_STATUSES.map(s => `
      <div class="form-check text-left mb-2">
        <input class="form-check-input" type="radio" name="fulfillmentStatus" id="fs_${s.value}" value="${s.value}" ${s.value === currentStatus ? 'checked' : ''}>
        <label class="form-check-label ml-2" for="fs_${s.value}">
          <span class="badge" style="background-color: ${s.bg}; color: ${s.color}; border: 1px solid ${s.border}; font-size: 0.85rem;">${s.label}</span>
        </label>
      </div>
    `).join('');

    const result = await Swal.fire({
      title: 'Update Fulfillment Status',
      html: `<div class="text-left mt-3">${statusOptions}</div>`,
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const selected = document.querySelector('input[name="fulfillmentStatus"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select a status');
          return null;
        }
        return selected.value;
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const newStatus = result.value;
    if (newStatus === currentStatus) return;

    Swal.fire({
      title: 'Updating...',
      text: 'Updating fulfillment status',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const db = getFirestore();
      const timestamp = new Date();
      const locId = tab.locationId;
      const saleId = tab.id;

      // 1. Update open tab doc and its inline items array — only items at or before new status
      const tabRef = doc(db, 'users', user.uid, 'openTabs', saleId);
      const tabDoc = await getDoc(tabRef);
      if (tabDoc.exists()) {
        const tabData = tabDoc.data();
        const updatedItems = (tabData.items || []).map(item => {
          const itemStatus = item.fulfillmentStatus || 'pending';
          const shouldUpdate = statusOrder(itemStatus) <= statusOrder(newStatus);
          return shouldUpdate ? { ...item, fulfillmentStatus: newStatus } : item;
        });
        // Recalculate min status from updated inline items
        const minStatus = getMinFulfillmentStatus(updatedItems);
        await updateDoc(tabRef, {
          items: updatedItems,
          fulfillmentStatus: minStatus,
          updatedAt: timestamp,
        });
      }

      // 2. Update items in location sale subcollection (setDoc with merge to avoid missing doc errors)
      const locItemsSnap = await getDocs(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items'));
      const batch = [];
      locItemsSnap.forEach(d => {
        const data = d.data();
        const itemStatus = data.fulfillmentStatus || 'pending';
        if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
          batch.push(setDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items', d.id), { fulfillmentStatus: newStatus, updatedAt: timestamp }, { merge: true }));
        }
      });
      await Promise.all(batch);

      // 3. Recalculate min status from location subcollection items
      const refreshedLocItems = await getDocs(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items'));
      const allItems = refreshedLocItems.docs.map(d => d.data());
      const minStatus = getMinFulfillmentStatus(allItems);

      // 4. Update location sale doc with recalculated min status
      await updateDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId), {
        fulfillmentStatus: minStatus,
        updatedAt: timestamp,
      });

      // 5. Bidirectional sync: if a user sale doc exists, update it too
      try {
        const userSaleRef = doc(db, 'users', user.uid, 'sales', saleId);
        const userSaleDoc = await getDoc(userSaleRef);
        if (userSaleDoc.exists()) {
          const userItemsSnap = await getDocs(collection(db, 'users', user.uid, 'sales', saleId, 'items'));
          const userBatch = [];
          userItemsSnap.forEach(d => {
            const data = d.data();
            const itemStatus = data.fulfillmentStatus || 'pending';
            if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
              userBatch.push(setDoc(doc(db, 'users', user.uid, 'sales', saleId, 'items', d.id), { fulfillmentStatus: newStatus, updatedAt: timestamp }, { merge: true }));
            }
          });
          await Promise.all(userBatch);
          await setDoc(userSaleRef, { fulfillmentStatus: minStatus, updatedAt: timestamp }, { merge: true });
        }
      } catch (userSaleErr) {
        console.warn('Could not sync to user sale doc:', userSaleErr.message);
      }

      // Refresh tab data
      const freshTabDoc = await getDoc(tabRef);
      if (freshTabDoc.exists()) {
        const updatedTab = { id: freshTabDoc.id, ...freshTabDoc.data() };
        setSelectedOpenTab(updatedTab);
      }
      await loadOpenTabs();

      Swal.close();
      Swal.fire({
        title: 'Status Updated',
        text: `Fulfillment status updated to ${getFulfillmentStatusMeta(minStatus).label}.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error updating fulfillment status:', error);
      Swal.close();
      Swal.fire('Error', error.message || 'Failed to update fulfillment status', 'error');
    }
  };

  const updateTabItemFulfillment = async (tab, itemIndex) => {
    if (!tab || !user) return;
    const item = tab.items?.[itemIndex];
    if (!item) return;
    const currentStatus = item.fulfillmentStatus || 'pending';
    const statusOptions = FULFILLMENT_STATUSES.map(s => `
      <div class="form-check text-left mb-2">
        <input class="form-check-input" type="radio" name="tabItemFulfillmentStatus" id="tifs_${s.value}" value="${s.value}" ${s.value === currentStatus ? 'checked' : ''}>
        <label class="form-check-label ml-2" for="tifs_${s.value}">
          <span class="badge" style="background-color: ${s.bg}; color: ${s.color}; border: 1px solid ${s.border}; font-size: 0.85rem;">${s.label}</span>
        </label>
      </div>
    `).join('');

    const result = await Swal.fire({
      title: `Update Item: ${item.productName}`,
      html: `<div class="text-left mt-3">${statusOptions}</div>`,
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const selected = document.querySelector('input[name="tabItemFulfillmentStatus"]:checked');
        if (!selected) {
          Swal.showValidationMessage('Please select a status');
          return null;
        }
        return selected.value;
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const newStatus = result.value;
    if (newStatus === currentStatus) return;

    Swal.fire({
      title: 'Updating...',
      text: 'Updating item fulfillment status',
      imageUrl: '/fodyfee_loading.png',
      imageWidth: 80,
      imageHeight: 80,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); },
    });

    try {
      const db = getFirestore();
      const timestamp = new Date();
      const locId = tab.locationId;
      const saleId = tab.id;

      // 1. Update this specific item in the inline items array
      const tabRef = doc(db, 'users', user.uid, 'openTabs', saleId);
      const tabDoc = await getDoc(tabRef);
      if (tabDoc.exists()) {
        const tabData = tabDoc.data();
        const updatedItems = (tabData.items || []).map((it, idx) =>
          idx === itemIndex ? { ...it, fulfillmentStatus: newStatus } : it
        );
        const minStatus = getMinFulfillmentStatus(updatedItems);
        await updateDoc(tabRef, {
          items: updatedItems,
          fulfillmentStatus: minStatus,
          updatedAt: timestamp,
        });
      }

      // 2. Find and update the matching item in the location sale subcollection (setDoc with merge)
      const locItemsSnap = await getDocs(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items'));
      let matchedDocId = null;
      locItemsSnap.forEach(d => {
        const data = d.data();
        if (data.productId === item.productId && (data.fulfillmentStatus || 'pending') === currentStatus) {
          matchedDocId = d.id;
        }
      });
      if (matchedDocId) {
        await setDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items', matchedDocId), {
          fulfillmentStatus: newStatus,
          updatedAt: timestamp,
        }, { merge: true });
      }

      // 3. Recalculate min status from refreshed location subcollection items
      const refreshedLocItems = await getDocs(collection(db, 'users', user.uid, 'locations', locId, 'sales', saleId, 'items'));
      const allItems = refreshedLocItems.docs.map(d => d.data());
      const minStatus = getMinFulfillmentStatus(allItems);

      // 4. Update location sale doc with recalculated min
      await updateDoc(doc(db, 'users', user.uid, 'locations', locId, 'sales', saleId), {
        fulfillmentStatus: minStatus,
        updatedAt: timestamp,
      });

      // 5. Bidirectional sync: update user sale doc if it exists
      try {
        const userSaleRef = doc(db, 'users', user.uid, 'sales', saleId);
        const userSaleDoc = await getDoc(userSaleRef);
        if (userSaleDoc.exists()) {
          const userItemsSnap = await getDocs(collection(db, 'users', user.uid, 'sales', saleId, 'items'));
          let userMatchedDocId = null;
          userItemsSnap.forEach(d => {
            const data = d.data();
            if (data.productId === item.productId && (data.fulfillmentStatus || 'pending') === currentStatus) {
              userMatchedDocId = d.id;
            }
          });
          if (userMatchedDocId) {
            await setDoc(doc(db, 'users', user.uid, 'sales', saleId, 'items', userMatchedDocId), {
              fulfillmentStatus: newStatus,
              updatedAt: timestamp,
            }, { merge: true });
          }
          await setDoc(userSaleRef, { fulfillmentStatus: minStatus, updatedAt: timestamp }, { merge: true });
        }
      } catch (userSaleErr) {
        console.warn('Could not sync to user sale doc:', userSaleErr.message);
      }

      // Refresh tab data
      const freshTabDoc = await getDoc(tabRef);
      if (freshTabDoc.exists()) {
        const updatedTab = { id: freshTabDoc.id, ...freshTabDoc.data() };
        setSelectedOpenTab(updatedTab);
      }
      await loadOpenTabs();

      Swal.close();
      Swal.fire({
        title: 'Updated',
        text: `Item status updated to ${getFulfillmentStatusMeta(newStatus).label}. Tab status is now ${getFulfillmentStatusMeta(minStatus).label}.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error updating item fulfillment status:', error);
      Swal.close();
      Swal.fire('Error', error.message || 'Failed to update item status', 'error');
    }
  };

  const tabCartSubtotal = tabCart.reduce((sum, item) => sum + (item.total || (item.price || 0) * (item.qty || 0)), 0);
  const tabCartCount = tabCart.reduce((sum, item) => sum + (item.qty || 0), 0);
  const tabCartTax = tabCartSubtotal * (taxRate / 100);
  const tabCartDiscount = parseFloat(tabDiscountInput) || 0;
  const tabCartTip = tabTipIsPercent
    ? tabCartSubtotal * ((parseFloat(tabTipInput) || 0) / 100)
    : (parseFloat(tabTipInput) || 0);
  const tabCartTotal = tabCartSubtotal + tabCartTax + tabCartTip - tabCartDiscount;

  // Handler to add a custom item to the main cart or tab cart
  const addCustomItemToCart = (targetCart = 'main') => {
    const desc = customItemDescription.trim();
    const price = parseFloat(customItemPrice) || 0;
    if (!desc || price <= 0) return;
    const qty = Math.max(1, customItemQty);
    const customItem = {
      productId: `custom-${Date.now()}`,
      productName: desc,
      productDescription: desc,
      price: price,
      qty: qty,
      total: price * qty,
      options: [],
      instructions: customItemInstructions.trim() || null,
      categoryId: null,
      categoryName: 'Custom',
      workzoneId: customItemWorkzoneId || null,
      fulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
    };
    if (targetCart === 'tab') {
      setTabCart(prev => [...prev, customItem]);
    } else {
      setCart(prev => [...prev, customItem]);
    }
    setShowCustomItemModal(false);
    setCustomItemDescription('');
    setCustomItemPrice('');
    setCustomItemQty(1);
    setCustomItemInstructions('');
    setCustomItemWorkzoneId('');
  };

  const changeDue = parseFloat(cashReceived || 0) - cartTotal;

  const handlePayment = async () => {
    if (!user || !pos || !session) return;
    if (paymentMethod === 'cash' && parseFloat(cashReceived || 0) < cartTotal) {
      Swal.fire('Error', 'Insufficient cash received', 'error');
      return;
    }

    // Terminal card payment flow
    if (paymentMethod === 'card' && cardEntryMode === 'terminal' && pos?.stripeTerminalId) {
      await startTerminalPayment();
      return;
    }

    // Stripe checkout payment flow
    if (paymentMethod === 'stripe') {
      await startStripeCheckoutPayment();
      return;
    }

    setSaving(true);
    try {
      await finalizeSale();
    } catch (e) {
      console.error('Error saving sale:', e);
      Swal.fire('Error', 'Failed to save sale', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startTerminalPayment = async () => {
    if (!user || !pos || !session) return;
    setSaving(true);
    try {
      const functions = getFunctions();
      const processTerminalPaymentFn = httpsCallable(functions, 'processTerminalPayment');
      const timestamp = new Date();
      const confirmationNumber = `POS-${timestamp.getTime().toString().slice(-6)}`;

      const result = await processTerminalPaymentFn({
        // Pre-fee amount — processTerminalPayment computes and adds its OWN
        // Convenience Fee server-side from the location config, so sending
        // a fee-inclusive amount here would double-charge it.
        amount: cartBaseTotal,
        currency: 'usd',
        stripeTerminalId: pos.stripeTerminalId,
        stripeLocationId: location?.stripeLocationId,
        locationId: pos.locationId,
        amountEligible: cartSubtotal,
        skipTipping: cartTip > 0, // tip was already added in the app, skip terminal tip prompt
        description: `POS Sale #${confirmationNumber}`,
        metadata: {
          posId: pos.id,
          posSessionId: sessionId,
          userId: user.uid,
          locationId: pos.locationId,
        },
        cartItems: cart.map(item => ({
          productName: item.productName,
          qty: item.qty,
          total: item.total,
          price: item.price,
        })),
        cartTax: cartTax,
        // Fee-inclusive total, only used for the reader's on-screen cart
        // preview before charging (not the actual charge amount above).
        cartTotal: cartTotal,
      });

      const { paymentIntentId } = result.data;
      setTerminalPaymentIntentId(paymentIntentId);
      setTerminalStatusMessage('Please tap or insert card on the terminal...');
      setShowTerminalWaitingModal(true);

      // Start polling for payment status
      pollTerminalPaymentStatus(paymentIntentId);
    } catch (error) {
      console.error('Error starting terminal payment:', error);
      Swal.fire('Error', error.message || 'Failed to start terminal payment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startStripeCheckoutPayment = async () => {
    if (!user || !pos || !session) return;
    setSaving(true);
    try {
      const db = getFirestore();
      const timestamp = new Date();
      const confirmationNumber = `POS-${timestamp.getTime().toString().slice(-6)}`;

      // Generate guest number (same correlative pattern used by finalizeSale/open tabs)
      let guestNumber = null;
      if (pos?.locationId && user?.uid) {
        const locRef = doc(db, 'users', user.uid, 'locations', pos.locationId);
        try {
          guestNumber = await runTransaction(db, async (transaction) => {
            const locDoc = await transaction.get(locRef);
            let ordersPosition = locDoc.data()?.ordersPosition || 0;
            const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
            ordersPosition++;
            // Wrap past the raw restart point (matches every other surface)
            if (ordersPosition > ordersCorrelative) ordersPosition = 1;
            transaction.update(locRef, { ordersPosition });
            return ordersPosition.toString().padStart(4, '0');
          });
        } catch (e) {
          console.error('Error generating guest number for Stripe checkout:', e);
        }
      }

      // Step 1: Create a pending sale in Firestore FIRST (durable record)
      const functions = getFunctions();
      const createPendingSaleFn = httpsCallable(functions, 'createPendingSale');
      const pendingResult = await createPendingSaleFn({
        items: cart.map(item => ({
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription || '',
          productImage: item.productImage || null,
          qty: item.qty || 0,
          price: item.price || 0,
          total: item.total || 0,
          options: item.options || [],
          instructions: item.instructions || null,
        })),
        totals: {
          subtotal: cartSubtotal,
          tax: cartTax,
          tip: cartTip,
          tipPercent: parseFloat(tipInput) || 0,
          tipIsPercent: tipIsPercent,
          discount: cartDiscount,
          total: cartTotal,
          itemsQty: cartCount,
        },
        customerInfo: {
          customerId: selectedCustomer?.id || null,
          fodyesUid: selectedCustomer?.fodyesUid || null,
          customerName: selectedCustomer?.name || 'Walk-in Customer',
          customerEmail: selectedCustomer?.email || '',
          customerPhone: selectedCustomer?.phone || '',
        },
        posId: pos.id,
        sessionId: sessionId,
        locationId: pos.locationId,
        locationName: location?.companyName || location?.locationName || '',
        confirmationNumber,
        guestNumber,
      });

      const currentPendingSaleId = pendingResult.data.pendingSaleId;
      setPendingSaleId(currentPendingSaleId);

      // Step 2: Create Stripe checkout session with pendingSaleId in metadata
      const createStripeCheckoutSessionFn = httpsCallable(functions, 'createStripeCheckoutSession');
      const baseUrl = window.location.origin + window.location.pathname;
      const result = await createStripeCheckoutSessionFn({
        amount: cartTotal,
        currency: 'usd',
        description: `POS Sale #${confirmationNumber}`,
        metadata: {
          posId: pos.id,
          posSessionId: sessionId,
          userId: user.uid,
          locationId: pos.locationId,
          pendingSaleId: currentPendingSaleId,
        },
        successUrl: `${baseUrl}?session=${sessionId}&checkout_success=true`,
        cancelUrl: `${baseUrl}?session=${sessionId}&checkout_canceled=true`,
      });

      const { sessionUrl, sessionId: checkoutSessionId } = result.data;

      // Save checkout session details to pending sale for recovery after page reload
      await updateDoc(doc(db, 'users', user.uid, 'pendingSales', currentPendingSaleId), {
        stripeCheckoutSessionId: checkoutSessionId,
        stripeCheckoutUrl: sessionUrl,
        updatedAt: new Date(),
      });

      setStripeCheckoutUrl(sessionUrl);
      setStripeCheckoutSessionId(checkoutSessionId);
      setShowStripeWaitingModal(true);

      // Step 3: Listen to the pending sale document for completion
      const unsubscribe = listenToPendingSale(currentPendingSaleId);
      setPendingSaleListener(() => unsubscribe);

      // Step 4: Backup polling in case webhook is delayed
      pollStripeCheckoutStatus(checkoutSessionId, currentPendingSaleId);
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      Swal.fire('Error', error.message || 'Failed to create Stripe checkout', 'error');
    } finally {
      setSaving(false);
    }
  };

  const listenToPendingSale = (saleId) => {
    const db = getFirestore();
    const pendingRef = doc(db, 'users', user.uid, 'pendingSales', saleId);

    return onSnapshot(pendingRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.status === 'completed') {
          // Sale was completed by webhook
          setShowStripeWaitingModal(false);
          setStripeCheckoutSessionId(null);
          setStripeCheckoutUrl(null);
          setPendingSaleId(null);
          setPendingSaleListener(null);
          if (stripePollIntervalRef.current) {
            clearInterval(stripePollIntervalRef.current);
          }
          // Show success and clear cart (use stored total in case of page reload)
          const completedTotal = data.total || 0;
          Swal.fire({
            title: 'Sale Completed!',
            text: `Total: $${completedTotal.toFixed(2)}`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
          });
          setCart([]);
          setSelectedCustomer(null);
          setShowPaymentModal(false);
          // Reload session sales
          reloadSessionSales();
        } else if (data.status === 'canceled') {
          setShowStripeWaitingModal(false);
          setStripeCheckoutSessionId(null);
          setStripeCheckoutUrl(null);
          setPendingSaleId(null);
          setPendingSaleListener(null);
          if (stripePollIntervalRef.current) {
            clearInterval(stripePollIntervalRef.current);
          }
        }
      }
    });
  };

  const pollStripeCheckoutStatus = (checkoutSessionId, currentPendingSaleId) => {
    if (stripePollIntervalRef.current) {
      clearInterval(stripePollIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 120; // ~6 minutes at 3 second intervals

    const poll = async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(stripePollIntervalRef.current);
        // Don't timeout immediately — let the user manually recover
        setTerminalStatusMessage('Still waiting for payment... Check recovery button.');
        return;
      }

      try {
        const functions = getFunctions();
        const getStripeCheckoutSessionFn = httpsCallable(functions, 'getStripeCheckoutSession');
        const result = await getStripeCheckoutSessionFn({ sessionId: checkoutSessionId });
        const { status, paymentStatus, paymentIntentId, charges } = result.data;

        if (paymentStatus === 'paid' || status === 'complete') {
          clearInterval(stripePollIntervalRef.current);
          // The webhook should have completed the sale, but if not, finalize now
          await finalizeSale({
            paymentIntentId,
            cardBrand: charges?.[0]?.cardBrand,
            cardLast4: charges?.[0]?.cardLast4,
          });
        } else if (paymentStatus === 'canceled') {
          clearInterval(stripePollIntervalRef.current);
          setShowStripeWaitingModal(false);
          setStripeCheckoutSessionId(null);
          setStripeCheckoutUrl(null);
          setPendingSaleId(null);
          Swal.fire('Canceled', 'The checkout payment was canceled.', 'info');
        }
      } catch (error) {
        console.error('Error polling Stripe checkout status:', error);
      }
    };

    poll(); // immediate first poll
    stripePollIntervalRef.current = setInterval(poll, 3000);
  };

  const cancelStripeCheckout = async () => {
    if (stripePollIntervalRef.current) {
      clearInterval(stripePollIntervalRef.current);
    }
    if (pendingSaleListener) {
      pendingSaleListener();
      setPendingSaleListener(null);
    }
    setShowStripeWaitingModal(false);
    setStripeCheckoutSessionId(null);
    setStripeCheckoutUrl(null);
    setPendingSaleId(null);
  };

  const reloadSessionSales = async () => {
    if (!user || !sessionId) return;
    try {
      const db = getFirestore();
      const salesSnapshot = await getDocs(
        query(collection(db, 'users', user.uid, 'posSessions', sessionId, 'sales'), orderBy('createdAt', 'desc'))
      );
      setSessionSales(salesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error reloading session sales:', e);
    }
  };

  // PIN-required decline codes from Stripe Terminal testing docs.
  // The terminal prompts for PIN entry after these; payment succeeds after PIN.
  const PIN_REQUIRED_DECLINE_CODES = new Set([
    'offline_pin_required',
    'online_or_offline_pin_required',
  ]);

  const pollTerminalPaymentStatus = (paymentIntentId) => {
    if (terminalPollIntervalRef.current) {
      clearInterval(terminalPollIntervalRef.current);
    }

    let pollCount = 0;
    const maxPolls = 120; // ~6 minutes at 3 second intervals

    const poll = async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(terminalPollIntervalRef.current);
        setShowTerminalWaitingModal(false);
        // Cancel the pending terminal action so the reader stops waiting
        if (pos?.stripeTerminalId) {
          try {
            const functions = getFunctions();
            const cancelFn = httpsCallable(functions, 'cancelTerminalAction');
            await cancelFn({ stripeTerminalId: pos.stripeTerminalId });
          } catch (cancelErr) {
            console.warn('Error canceling terminal action on timeout:', cancelErr.message);
          }
        }
        Swal.fire('Timeout', 'Terminal payment timed out. Please try again.', 'warning');
        return;
      }

      try {
        const functions = getFunctions();
        const checkTerminalPaymentStatusFn = httpsCallable(functions, 'checkTerminalPaymentStatus');
        const result = await checkTerminalPaymentStatusFn({ paymentIntentId });
        const { status, chargeAmount, tipAmount, convenienceFeeAmount, charges, lastPaymentError } = result.data;
        const declineCode = lastPaymentError?.declineCode || lastPaymentError?.code || '';

        if (status === 'succeeded') {
          clearInterval(terminalPollIntervalRef.current);
          if (isFinalizingSaleRef.current) {
            console.log('[POS] finalizeSale already in progress, skipping duplicate');
            return;
          }
          isFinalizingSaleRef.current = true;
          setShowTerminalWaitingModal(false);
          setTerminalPaymentIntentId(null);
          setShowPaymentModal(false); // close the Payment modal immediately so it doesn't peek through

          // Use the actual amounts captured by the terminal
          const finalTotal = (chargeAmount || 0) / 100;
          const finalTip = (tipAmount || 0) / 100;
          // If terminal reported no tip but tip was already added in the app,
          // use the app's tip so the receipt shows the correct amount.
          const effectiveTip = finalTip || cartTip;

          // Show loading overlay while finalizing the sale so the screen doesn't look idle
          Swal.fire({
            title: 'Finalizing Sale...',
            text: 'Please wait a moment',
            imageUrl: '/fodyfee_loading.png',
            imageWidth: 80,
            imageHeight: 80,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          try {
            // Complete the sale with actual amounts from terminal
            await finalizeSale({
              tipOverride: effectiveTip,
              totalOverride: finalTotal,
              convenienceFeeOverride: (convenienceFeeAmount || 0) / 100,
              paymentIntentId,
              cardBrand: charges?.[0]?.cardBrand,
              cardLast4: charges?.[0]?.cardLast4,
            });
          } finally {
            isFinalizingSaleRef.current = false;
          }

          Swal.close();
        } else if (status === 'canceled') {
          clearInterval(terminalPollIntervalRef.current);
          setShowTerminalWaitingModal(false);
          setTerminalPaymentIntentId(null);
          Swal.fire('Payment Failed', 'The card payment was not completed on the terminal.', 'error');
        } else if (status === 'requires_payment_method') {
          // Check if a card was declined (lastPaymentError means an attempt failed)
          if (lastPaymentError) {
            // PIN-required declines are intermediate states (terminal prompts for PIN, then succeeds)
            if (PIN_REQUIRED_DECLINE_CODES.has(declineCode)) {
              setTerminalStatusMessage('PIN entry required on terminal. Please enter PIN...');
            } else {
              // Real terminal decline — show specific message
              setTerminalStatusMessage(`Card declined: ${lastPaymentError.message || 'Please try a different card.'}`);
            }
          } else {
            setTerminalStatusMessage('Please tap or insert card on the terminal...');
          }
        } else if (status === 'processing') {
          setTerminalStatusMessage('Processing payment on terminal...');
        } else if (status === 'requires_capture') {
          setTerminalStatusMessage('Payment authorized. Finalizing...');
        } else if (status === 'requires_action') {
          setTerminalStatusMessage('Additional authentication required on terminal...');
        }
      } catch (error) {
        console.error('Error polling terminal payment:', error);
      }
    };

    poll(); // immediate first poll
    terminalPollIntervalRef.current = setInterval(poll, 3000);
  };

  const cancelTerminalPayment = async () => {
    if (terminalPollIntervalRef.current) {
      clearInterval(terminalPollIntervalRef.current);
    }
    if (pos?.stripeTerminalId) {
      try {
        const functions = getFunctions();
        const cancelTerminalActionFn = httpsCallable(functions, 'cancelTerminalAction');
        await cancelTerminalActionFn({ stripeTerminalId: pos.stripeTerminalId });
      } catch (error) {
        console.error('Error canceling terminal action:', error);
      }
    }
    // If we were in the middle of creating a tab, cancel that tab too
    if (pendingTabIdForCard) {
      try {
        const functions = getFunctions();
        const cancelFn = httpsCallable(functions, 'cancelOpenTab');
        await cancelFn({ openTabId: pendingTabIdForCard });
        await loadOpenTabs();
      } catch (error) {
        console.error('Error canceling pending tab:', error);
      }
      setPendingTabIdForCard(null);
    }
    setShowTerminalWaitingModal(false);
    setTerminalPaymentIntentId(null);
    setTerminalStatusMessage('');
  };

  const finalizeSale = async (overrides = {}) => {
    const db = getFirestore();
    const timestamp = new Date();

    // Commit the promo code now (increments usage, records redemption).
    // `silent: true` because a card payment may have already been charged
    // (using cartTotal, already net of the promo preview) by the time this
    // runs — never block/alarm on a sale that's already effectively done;
    // worst case here is a promo commit failing on a rare usage-cap race,
    // in which case this sale simply won't carry a promoId/promoCode.
    let committedPromoDiscount = 0;
    let committedPromoId = null;
    let committedPromoCode = null;
    if (appliedPromo) {
      const promoResult = await commitPromoCode(true);
      if (promoResult) {
        committedPromoDiscount = promoResult.discountAmount;
        committedPromoId = promoResult.promoId;
        committedPromoCode = promoResult.promoCode;
      }
    }

    // Build items matching SalesPage.js structure
    const saleItems = cart.map(item => ({
      productId: item.productId,
      productName: item.productName,
      productDescription: item.productDescription || '',
      productImage: item.productImage || null,
      qty: item.qty || 0,
      price: item.price || 0,
      total: item.total || 0,
      options: item.options || [],
      instructions: item.instructions || null,
      categoryId: item.categoryId || null,
      categoryName: item.categoryName || '',
      workzoneId: item.workzoneId || null,
      date: item.date || timestamp,
      // Per-item discount audit trail (native parity — the calculator sets
      // these on the cart line; they were being dropped from the sale).
      ...(item.originalPrice != null ? { originalPrice: item.originalPrice } : {}),
      ...(item.discountAmount != null ? { discountAmount: item.discountAmount } : {}),
      ...(item.discountNote != null ? { discountNote: item.discountNote } : {}),
      // Cash orders may start at the location's cash override (native parity:
      // food trucks hand cash orders straight to the kitchen window).
      fulfillmentStatus: (paymentMethod === 'cash' && (location?.cashFulfillmentStatus || '').toString().trim() !== ''
        ? location.cashFulfillmentStatus
        : (location?.defaultFulfillmentStatus || 'pending')),
    }));

    const confirmationNumber = `POS-${timestamp.getTime().toString().slice(-6)}`;

    // Calculate guest number
    let guestNumber = null;
    if (pos?.locationId && user?.uid) {
      const locRef = doc(db, 'users', user.uid, 'locations', pos.locationId);
      try {
        guestNumber = await runTransaction(db, async (transaction) => {
          const locDoc = await transaction.get(locRef);
          let ordersPosition = locDoc.data()?.ordersPosition || 0;
          const ordersCorrelative = locDoc.data()?.ordersCorrelative || 1;
          ordersPosition++;
          // Wrap past the raw restart point (matches every other surface)
            if (ordersPosition > ordersCorrelative) ordersPosition = 1;
          transaction.update(locRef, { ordersPosition });
          return ordersPosition.toString().padStart(4, '0');
        });
      } catch (e) {
        console.error('Error generating guest number:', e);
      }
    }

    const finalTip = overrides.tipOverride !== undefined ? overrides.tipOverride : cartTip;
    const finalTotal = overrides.totalOverride !== undefined ? overrides.totalOverride : cartTotal;
    const finalConvenienceFee = overrides.convenienceFeeOverride !== undefined ? overrides.convenienceFeeOverride : cartConvenienceFee;

    const pointsEarned = (selectedCustomer?.id && location?.pointsPerDollar > 0)
      ? Math.floor(cartSubtotal * location.pointsPerDollar)
      : 0;

    const saleData = {
      id: null, // set after creating doc
      userId: user.uid,
      locationId: pos.locationId,
      locationName: location?.companyName || location?.locationName || '',
      posId: pos.id,
      posSessionId: sessionId,
      salesChannel: 'POS',
      customerId: selectedCustomer?.id || null,
      fodyesUid: selectedCustomer?.fodyesUid || null,
      customerName: selectedCustomer?.name || 'Walk-in Customer',
      customerEmail: selectedCustomer?.email || '',
      customerPhone: selectedCustomer?.phone || '',
      orderType: selectedOrderType || 'pos',
      isGuestCustomer: !selectedCustomer,
      confirmationNumber,
      guestNumber,
      modifiedAt: timestamp,
      closeAt: timestamp,
      paymentType: paymentMethod,
      paymentMethod: paymentMethod,
      status: 'completed',
      paymentStatus: 'completed',
      orderStatus: 'completed',
      // Cash orders may start at the location's cash override (native parity:
      // food trucks hand cash orders straight to the kitchen window).
      fulfillmentStatus: (paymentMethod === 'cash' && (location?.cashFulfillmentStatus || '').toString().trim() !== ''
        ? location.cashFulfillmentStatus
        : (location?.defaultFulfillmentStatus || 'pending')),
      cardType: paymentMethod === 'card' ? (overrides.cardBrand || 'debit') : null,
      cardLast4: paymentMethod === 'card' ? (overrides.cardLast4 || null) : null,
      itemsQty: cartCount,
      subtotal: cartSubtotal,
      tax: cartTax,
      tip: finalTip,
      tipPercent: 0,
      tipIsPercent: false,
      convenienceFee: finalConvenienceFee,
      discount: cartDiscount + committedPromoDiscount,
      promoId: committedPromoId,
      promoCode: committedPromoCode,
      total: finalTotal,
      pointsEarned,
      stripePaymentIntentId: overrides.paymentIntentId || null,
      deliveryAddress: null,
      deliveryCity: null,
      deliveryState: null,
      deliveryZipCode: null,
      deliveryCountry: null,
      deliveryLatitude: null,
      deliveryLongitude: null,
      notes: '',
      orderDate: timestamp,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const saleId = doc(collection(db, 'users', user.uid, 'sales')).id;
    saleData.id = saleId;

    // Save to user sales
    await setDoc(doc(db, 'users', user.uid, 'sales', saleId), saleData);
    // Save items subcollection for user sales
    await Promise.all(saleItems.map(item => setDoc(doc(collection(db, 'users', user.uid, 'sales', saleId, 'items')), item)));
    
    // Save to location sales
    await setDoc(doc(db, 'users', user.uid, 'locations', pos.locationId, 'sales', saleId), saleData);
    // Save items subcollection for location sales (so SalesPage.js can load them)
    await Promise.all(saleItems.map(item => setDoc(doc(collection(db, 'users', user.uid, 'locations', pos.locationId, 'sales', saleId, 'items')), item)));
    
    // Save to session sales
    await setDoc(doc(db, 'users', user.uid, 'posSessions', sessionId, 'sales', saleId), saleData);
    // Save items subcollection for session sales (kept consistent with user/location sales)
    await Promise.all(saleItems.map(item => setDoc(doc(collection(db, 'users', user.uid, 'posSessions', sessionId, 'sales', saleId, 'items')), item)));

    // Update customer reward points
    if (pointsEarned > 0) {
      await updateDoc(doc(db, 'users', user.uid, 'customers', selectedCustomer.id), {
        rewardPoints: increment(pointsEarned),
      });
    }

    // Update session totals with payment breakdown
    const sessionRef = doc(db, 'users', user.uid, 'posSessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    if (sessionDoc.exists()) {
      const currentData = sessionDoc.data();
      const newTotalSales = (currentData.totalSales || 0) + finalTotal;
      const newTotalTransactions = (currentData.totalTransactions || 0) + 1;
      const newCash = paymentMethod === 'cash' ? (currentData.cash || 0) + finalTotal : (currentData.cash || 0);
      const newCardSales = paymentMethod === 'card' ? (currentData.cardSales || 0) + finalTotal : (currentData.cardSales || 0);
      const newStripeSales = paymentMethod === 'stripe' ? (currentData.stripeSales || 0) + finalTotal : (currentData.stripeSales || 0);
      await updateDoc(sessionRef, {
        totalSales: newTotalSales,
        totalTransactions: newTotalTransactions,
        cash: newCash,
        cardSales: newCardSales,
        stripeSales: newStripeSales,
        updatedAt: timestamp,
      });
      // Update local session state so Close POS modal shows live data
      setSession(prev => prev ? {
        ...prev,
        totalSales: newTotalSales,
        totalTransactions: newTotalTransactions,
        cash: newCash,
        cardSales: newCardSales,
        stripeSales: newStripeSales,
      } : prev);
    }

    // Update POS cash
    const posRef = doc(db, 'users', user.uid, 'pos', posId);
    const posDoc = await getDoc(posRef);
    if (posDoc.exists()) {
      const currentCash = posDoc.data().cash || 0;
      await updateDoc(posRef, {
        cash: paymentMethod === 'cash' ? currentCash + finalTotal : currentCash,
        updatedAt: timestamp,
      });
    }

    setCart([]);
    setSelectedCustomer(null);
    setTipInput('');
    setTipIsPercent(true);
    setDiscountInput('');
    setAppliedPromo(null);
    setPromoCodeInput('');
    setShowPaymentModal(false);
    
    // Reload session sales
    const salesSnapshot = await getDocs(
      query(collection(db, 'users', user.uid, 'posSessions', sessionId, 'sales'), orderBy('createdAt', 'desc'))
    );
    setSessionSales(salesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    
    // Auto-print receipt
    autoPrintReceipt(saleData, saleItems, confirmationNumber);
    
    Swal.fire({
      title: 'Sale Completed!',
      text: guestNumber ? `Guest #${guestNumber} — Total: $${finalTotal.toFixed(2)}` : `Total: $${finalTotal.toFixed(2)}`,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const calculateClosingTotal = () => {
    return Object.entries(closingCash).reduce((sum, [denom, count]) => sum + (parseInt(denom) * parseInt(count || 0)), 0);
  };

  const handleClosePos = async () => {
    if (!user || !session || !pos) return;

    // Require employee selection and PIN verification to close session
    if (!selectedCloseEmployeeId) {
      Swal.fire('Error', 'Please select the employee closing this session', 'error');
      return;
    }
    const emp = employees.find(e => e.id === selectedCloseEmployeeId);
    if (!emp) {
      Swal.fire('Error', 'Selected employee not found', 'error');
      return;
    }
    if (emp.pinHash) {
      if (!closeEmployeePin || closeEmployeePin.length < 4) {
        Swal.fire('Error', 'Please enter the employee passkey (4-6 digits)', 'error');
        return;
      }
      const inputHash = await hashPin(closeEmployeePin, emp.pinSalt);
      if (inputHash !== emp.pinHash) {
        Swal.fire('Error', 'Incorrect passkey', 'error');
        return;
      }
    }

    try {
      const db = getFirestore();
      const closingTotal = calculateClosingTotal();
      const expectedCash = (session.startingCash || 0) + (session.cash || 0);
      const difference = closingTotal - expectedCash;
      const timestamp = new Date();
      
      // Update session
      await updateDoc(doc(db, 'users', user.uid, 'posSessions', sessionId), {
        status: 'closed',
        endTime: timestamp,
        closedAt: timestamp,
        closedBy: user.uid,
        closedByEmployeeId: emp.id,
        closedByEmployeeName: emp.name,
        closedByEmployeeRole: emp.role,
        endingCash: closingTotal,
        endingCashBreakdown: { ...closingCash },
        expectedCash: expectedCash,
        cashDifference: difference,
        updatedAt: timestamp,
      });

      // Reset POS cash
      await updateDoc(doc(db, 'users', user.uid, 'pos', posId), {
        cash: 0,
        updatedAt: timestamp,
      });

      setShowCloseModal(false);
      setSelectedCloseEmployeeId('');
      setCloseEmployeePin('');

      // Clear persisted session state
      try {
        sessionStorage.removeItem('activePosSession');
        sessionStorage.removeItem('screenSaverActive');
      } catch (e) {}

      Swal.fire({
        title: 'Session Closed',
        html: `
          <div style="text-align: left;">
            <p><strong>Starting Cash:</strong> $${(session.startingCash || 0).toFixed(2)}</p>
            <p><strong>Cash Sales:</strong> $${(session.cash || 0).toFixed(2)}</p>
            <p><strong>Expected Cash:</strong> $${expectedCash.toFixed(2)}</p>
            <p><strong>Actual Cash:</strong> $${closingTotal.toFixed(2)}</p>
            <p><strong>Difference:</strong> <span style="color: ${difference >= 0 ? '#28a745' : '#dc3545'}">$${difference.toFixed(2)}</span></p>
            <p><strong>Total Sales:</strong> $${(session.totalSales || 0).toFixed(2)}</p>
            <p><strong>Transactions:</strong> ${session.totalTransactions || 0}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Done',
      }).then(() => {
        navigate('/admin/pos');
      });
    } catch (e) {
      console.error('Error closing session:', e);
      Swal.fire('Error', 'Failed to close session', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const autoPrintReceipt = (saleData, saleItems, confirmationNumber) => {
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) {
      console.warn('Popup blocked — receipt could not auto-print');
      return;
    }

    const timestamp = saleData.orderDate || new Date();
    const dateStr = timestamp.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const itemsHtml = saleItems.map(item => {
      const optionsStr = item.options?.length > 0
        ? `<div style="font-size:10px;color:#555;padding-left:8px;">+ ${item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}</div>`
        : '';
      const instructionsStr = item.instructions ? `<div style="font-size:10px;color:#777;padding-left:8px;"><i>Note: ${item.instructions}</i></div>` : '';
      return `
        <div style="margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;">
            <div style="flex:1;">
              <span style="font-weight:bold;">${item.qty}x</span> ${item.productName}
            </div>
            <div style="text-align:right;min-width:60px;">${formatCurrency(item.total)}</div>
          </div>
          ${optionsStr}
          ${instructionsStr}
        </div>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Receipt #${confirmationNumber}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 80mm;
              max-width: 80mm;
              margin: 0 auto;
              padding: 8px;
              box-sizing: border-box;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }
            .line {
              display: flex;
              justify-content: space-between;
            }
            .total {
              font-size: 14px;
              font-weight: bold;
            }
            .logo {
              max-width: 60mm;
              max-height: 50px;
              object-fit: contain;
              margin-bottom: 4px;
            }
          </style>
        </head>
        <body>
          ${location?.logoUrl ? `<div class="center"><img src="${location.logoUrl}" class="logo" onerror="this.style.display='none'" /></div>` : ''}
          <div class="center bold" style="font-size:14px;">${location?.companyName || location?.locationName || 'Restaurant'}</div>
          ${location?.address ? `<div class="center">${location.address}</div>` : ''}
          ${location?.city || location?.state || location?.zip ? `<div class="center">${[location?.city, location?.state, location?.zip].filter(Boolean).join(', ')}</div>` : ''}
          ${location?.phone ? `<div class="center">Tel: ${location.phone}</div>` : ''}
          <div class="divider"></div>

          <div class="center bold">RECEIPT</div>
          <div class="line"><span>Order #:</span><span>${confirmationNumber}</span></div>
          ${saleData.guestNumber ? `<div class="line bold" style="font-size:16px;"><span>Guest #:</span><span>${saleData.guestNumber}</span></div>` : ''}
          <div class="line"><span>Date:</span><span>${dateStr}</span></div>
          <div class="line"><span>Type:</span><span>POS</span></div>
          <div class="line"><span>Payment:</span><span>${(saleData.paymentType || 'cash').toUpperCase()}</span></div>
          <div class="divider"></div>

          <div class="bold">Customer</div>
          <div>${saleData.customerName || 'Walk-in'}</div>
          ${saleData.customerPhone ? `<div>Tel: ${saleData.customerPhone}</div>` : ''}
          <div class="divider"></div>

          <div class="bold">Items</div>
          ${itemsHtml}
          <div class="divider"></div>

          <div class="line"><span>Subtotal</span><span>${formatCurrency(saleData.subtotal)}</span></div>
          <div class="line"><span>Tax (${location?.taxRate || 0}%)</span><span>${formatCurrency(saleData.tax)}</span></div>
          <div class="line"><span>Tip</span><span>${formatCurrency(saleData.tip)}</span></div>
          ${saleData.discount > 0 ? `<div class="line"><span>Discount</span><span>-${formatCurrency(saleData.discount)}</span></div>` : ''}
          <div class="divider"></div>
          <div class="line total"><span>TOTAL</span><span>${formatCurrency(saleData.total)}</span></div>

          <div class="divider"></div>
          <div class="center" style="font-size:11px;margin-top:4px;">
            <div>Thank you for your visit!</div>
            <div>Please come again</div>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              }, 300);
  };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const printTabReceipt = async (tab) => {
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) {
      Swal.fire('Error', 'Please allow popups to print the receipt', 'error');
      return;
    }

    const timestamp = tab.createdAt ? (tab.createdAt.toDate ? tab.createdAt.toDate() : new Date(tab.createdAt)) : new Date();
    const dateStr = timestamp.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const itemsHtml = (tab.items || []).map(item => {
      const optionsStr = item.options?.length > 0
        ? `<div style="font-size:10px;color:#555;padding-left:8px;">+ ${item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}</div>`
        : '';
      const instructionsStr = item.instructions ? `<div style="font-size:10px;color:#777;padding-left:8px;"><i>Note: ${item.instructions}</i></div>` : '';
      return `
        <div style="margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;">
            <div style="flex:1;">
              <span style="font-weight:bold;">${item.qty}x</span> ${item.productName}
            </div>
            <div style="text-align:right;min-width:60px;">${formatCurrency(item.total)}</div>
          </div>
          ${optionsStr}
          ${instructionsStr}
        </div>
      `;
    }).join('');

    const subtotal = tab.subtotal || 0;
    const tax = tab.tax || 0;
    const currentTotal = tab.total || 0;

    // If split checks exist, print one receipt per split
    if (splitChecks.length > 0) {
      const splitReceipts = splitChecks.map((split, idx) => {
        const splitSubtotal = split.subtotal || 0;
        const splitTax = split.tax || 0;
        const splitTip = split.tip || 0;
        const splitDiscount = split.discount || 0;
        const splitTotal = (split.total || 0) + splitTip;
        const splitItems = (split.items || tab.items || []);
        const splitItemsHtml = splitItems.map(item => {
          return `
            <div style="margin-bottom:6px;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;">
                <div style="flex:1;">
                  <span style="font-weight:bold;">${item.qty || 1}x</span> ${item.productName || 'Item'}
                </div>
                <div style="text-align:right;min-width:60px;">${formatCurrency(item.total || item.price * (item.qty || 1))}</div>
              </div>
            </div>
          `;
        }).join('');

        return `
          <div style="page-break-after: always; padding: 8px;">
            ${location?.logoUrl ? `<div class="center"><img src="${location.logoUrl}" class="logo" onerror="this.style.display='none'" /></div>` : ''}
            <div class="center bold" style="font-size:14px;">${location?.companyName || location?.locationName || 'Restaurant'}</div>
            ${location?.address ? `<div class="center">${location.address}</div>` : ''}
            ${location?.city || location?.state || location?.zip ? `<div class="center">${[location?.city, location?.state, location?.zip].filter(Boolean).join(', ')}</div>` : ''}
            ${location?.phone ? `<div class="center">Tel: ${location.phone}</div>` : ''}
            <div class="divider"></div>

            <div class="center bold">${split.label || `CHECK ${idx + 1}`}</div>
            <div class="line"><span>Tab:</span><span>${tab.tabName || 'Tab'}</span></div>
            <div class="line"><span>Date:</span><span>${dateStr}</span></div>
            <div class="line"><span>Payment:</span><span>${(split.paymentMethod || 'card').toUpperCase()}</span></div>
            <div class="divider"></div>

            <div class="bold">Items</div>
            ${splitItemsHtml}
            <div class="divider"></div>

            <div class="line"><span>Subtotal</span><span>${formatCurrency(splitSubtotal)}</span></div>
            <div class="line"><span>Tax</span><span>${formatCurrency(splitTax)}</span></div>
            ${splitTip > 0 ? `<div class="line"><span>Tip</span><span>${formatCurrency(splitTip)}</span></div>` : ''}
            ${splitDiscount > 0 ? `<div class="line"><span>Discount</span><span>-${formatCurrency(splitDiscount)}</span></div>` : ''}
            <div class="divider"></div>
            <div class="line total"><span>TOTAL FOR ${split.label || `CHECK ${idx + 1}`}</span><span>${formatCurrency(splitTotal)}</span></div>

            ${split.tip === 0 ? `
              <div class="line" style="font-size:13px;margin-top:4px;">
                <span>Tip</span>
                <span>$__________</span>
              </div>
              <div class="line total" style="margin-top:4px;">
                <span>TOTAL</span>
                <span>$__________</span>
              </div>
            ` : ''}

            <div class="divider"></div>
            <div class="center" style="font-size:11px;margin-top:4px;">
              <div>Thank you for your visit!</div>
              <div>Please come again</div>
            </div>
          </div>
        `;
      }).join('');

      const fullHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Split Receipts - ${tab.tabName || 'Tab'}</title>
            <style>
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
              body {
                font-family: 'Courier New', Courier, monospace;
                font-size: 12px;
                line-height: 1.4;
                color: #000;
                width: 80mm;
                max-width: 80mm;
                margin: 0 auto;
                padding: 8px;
                box-sizing: border-box;
              }
              .center { text-align: center; }
              .bold { font-weight: bold; }
              .divider {
                border-top: 1px dashed #000;
                margin: 8px 0;
              }
              .line {
                display: flex;
                justify-content: space-between;
              }
              .total {
                font-size: 14px;
                font-weight: bold;
              }
              .logo {
                max-width: 60mm;
                max-height: 50px;
                object-fit: contain;
                margin-bottom: 4px;
              }
            </style>
          </head>
          <body>
            ${splitReceipts}
            <script>
              setTimeout(function() { window.print(); }, 300);
            </script>
            <div class="no-print" style="margin-top:16px;text-align:center;">
              <button onclick="window.print();" style="padding:8px 16px;font-size:14px;cursor:pointer;">Print Receipts</button>
            </div>
          </body>
        </html>
      `;
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      printWindow.focus();
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Tab Receipt - ${tab.tabName || 'Tab'}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 80mm;
              max-width: 80mm;
              margin: 0 auto;
              padding: 8px;
              box-sizing: border-box;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }
            .line {
              display: flex;
              justify-content: space-between;
            }
            .total {
              font-size: 14px;
              font-weight: bold;
            }
            .logo {
              max-width: 60mm;
              max-height: 50px;
              object-fit: contain;
              margin-bottom: 4px;
            }
            .tip-line {
              border-bottom: 1px solid #000;
              display: inline-block;
              width: 80px;
              margin-left: 8px;
            }
            .notice {
              border: 1px dashed #000;
              padding: 6px;
              margin: 8px 0;
              font-size: 11px;
              text-align: center;
            }
          </style>
        </head>
        <body>
    `;

    const receiptBody = `
      ${location?.logoUrl ? `<div class="center"><img src="${location.logoUrl}" class="logo" onerror="this.style.display='none'" /></div>` : ''}
      <div class="center bold" style="font-size:14px;">${location?.companyName || location?.locationName || 'Restaurant'}</div>
      ${location?.address ? `<div class="center">${location.address}</div>` : ''}
      ${location?.city || location?.state || location?.zip ? `<div class="center">${[location?.city, location?.state, location?.zip].filter(Boolean).join(', ')}</div>` : ''}
      ${location?.phone ? `<div class="center">Tel: ${location.phone}</div>` : ''}
      <div class="divider"></div>

      <div class="center bold">OPEN TAB RECEIPT</div>
      <div class="line"><span>Tab:</span><span>${tab.tabName || 'Tab'}</span></div>
      <div class="line"><span>Date:</span><span>${dateStr}</span></div>
      <div class="line"><span>Type:</span><span>POS</span></div>
      <div class="line"><span>Payment:</span><span>${(tab.paymentMethod === 'define_later' ? 'DEFINE LATER' : (tab.paymentMethod || 'cash').toUpperCase())}</span></div>
      <div class="divider"></div>

      <div class="bold">Customer</div>
      <div>${tab.customerName || 'Walk-in'}</div>
      ${tab.customerPhone ? `<div>Tel: ${tab.customerPhone}</div>` : ''}
      <div class="divider"></div>

      <div class="bold">Items</div>
      ${itemsHtml}
      <div class="divider"></div>

      <div class="line"><span>Subtotal</span><span>${formatCurrency(subtotal)}</span></div>
      <div class="line"><span>Tax (${location?.taxRate || 0}%)</span><span>${formatCurrency(tax)}</span></div>
      <div class="divider"></div>

      <!-- Suggested tip amounts based on location config -->
      ${(location?.terminalTipping?.percentages || [15, 18, 20]).map(pct => {
        const tipAmt = subtotal * (pct / 100);
        const totalWithTip = subtotal + tax + tipAmt;
        return `<div class="line" style="font-size:11px;"><span>${pct}% Tip</span><span>${formatCurrency(tipAmt)} = ${formatCurrency(totalWithTip)}</span></div>`;
      }).join('')}
      <div class="divider"></div>

      <div class="line" style="font-size:13px;margin-top:4px;">
        <span>Tip</span>
        <span>$__________</span>
      </div>
      <div class="line total" style="margin-top:4px;">
        <span>TOTAL</span>
        <span>$__________</span>
      </div>

      <div class="notice">
        Card pre-authorized for ${formatCurrency(currentTotal)}.<br>
        Total will be adjusted with tip at checkout.
      </div>

      <!-- Signature Section -->
      <div style="margin-top:12px;">
        <div style="margin-bottom:4px;">Signature</div>
        <div style="border-bottom:1px solid #000; height:30px; margin-bottom:8px;"></div>
        <div style="font-size:10px; color:#555;">I agree to pay the total amount including any tip added.</div>
      </div>

      <div class="divider"></div>
      <div class="center" style="font-size:11px;margin-top:4px;">
        <div>Thank you for your visit!</div>
        <div>Please come again</div>
      </div>
    `;

    const merchantCopy = `
      <div style="page-break-after: always;">
        <div class="center bold" style="font-size:16px; margin-bottom:8px;">MERCHANT COPY</div>
        ${receiptBody}
      </div>
    `;

    const clientCopy = `
      <div>
        <div class="center bold" style="font-size:16px; margin-bottom:8px;">CLIENT COPY</div>
        ${receiptBody}
      </div>
    `;

    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Tab Receipt - ${tab.tabName || 'Tab'}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 80mm;
              max-width: 80mm;
              margin: 0 auto;
              padding: 8px;
              box-sizing: border-box;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }
            .line {
              display: flex;
              justify-content: space-between;
            }
            .total {
              font-size: 14px;
              font-weight: bold;
            }
            .logo {
              max-width: 60mm;
              max-height: 50px;
              object-fit: contain;
              margin-bottom: 4px;
            }
            .tip-line {
              border-bottom: 1px solid #000;
              display: inline-block;
              width: 80px;
              margin-left: 8px;
            }
            .notice {
              border: 1px dashed #000;
              padding: 6px;
              margin: 8px 0;
              font-size: 11px;
              text-align: center;
            }
          </style>
        </head>
        <body>
          ${merchantCopy}
          ${clientCopy}
          <script>
            setTimeout(function() { window.print(); }, 300);
          </script>
          <div class="no-print" style="margin-top:16px;text-align:center;">
            <button onclick="window.print();" style="padding:8px 16px;font-size:14px;cursor:pointer;">Print Receipt</button>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(fullHtml);
    printWindow.document.close();
    printWindow.focus();
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100" style={{ background: '#f8f9fa' }}>
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 120, height: 120, objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <div className="pos-page d-flex flex-column vh-100" style={{ background: '#f8f9fa' }}>
      {/* Screensaver / Idle Lock Overlay */}
      {screenSaverActive && (
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#1a1a2e',
            zIndex: 9999,
            color: '#fff',
            padding: '2rem',
          }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          onScroll={e => e.stopPropagation()}
        >
          {/* Location Logo & Name */}
          <div className="text-center mb-5">
            {location?.logoUrl ? (
              <div
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '12px 24px',
                  marginBottom: 16,
                }}
              >
                <img
                  src={location.logoUrl}
                  alt="Logo"
                  style={{ maxWidth: 200, maxHeight: 100, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <i className="fas fa-store fa-4x mb-3" style={{ color: '#ff6b35' }}></i>
            )}
            <h2 className="font-weight-bold" style={{ color: '#fff', letterSpacing: 1 }}>
              {location?.companyName || location?.locationName || 'POS Locked'}
            </h2>
            <p className="text-muted" style={{ color: '#aaa' }}>
              Session paused due to inactivity
            </p>
          </div>

          {/* Unlock Panel */}
          <div
            className="card border-0"
            style={{ background: '#16213e', borderRadius: 20, width: '100%', maxWidth: 420, padding: '1.5rem' }}
          >
            {!showTimeclockChoice ? (
              <>
                <h5 className="font-weight-bold text-center mb-3" style={{ color: '#fff' }}>
                  <i className="fas fa-lock mr-2 text-warning"></i>Unlock to Continue
                </h5>

                {/* Employee Select */}
                <div className="form-group mb-3">
                  <label className="small text-muted mb-1" style={{ color: '#ccc' }}>Employee</label>
                  <select
                    className="form-control"
                    style={{ background: '#0f3460', color: '#fff', border: '1px solid #2a2a4a' }}
                    value={screenSaverSelectedEmployeeId}
                    onChange={e => { setScreenSaverSelectedEmployeeId(e.target.value); setScreenSaverPin(''); setScreenSaverError(''); }}
                  >
                    <option value="" style={{ color: '#333' }}>— Select employee —</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id} style={{ color: '#333' }}>
                        {emp.name} ({emp.role})
                      </option>
                    ))}
                  </select>
                </div>

                {/* PIN Display */}
                <div className="text-center mb-3">
                  <div
                    className="d-inline-block px-4 py-2"
                    style={{
                      background: '#0f3460',
                      borderRadius: 12,
                      fontSize: '1.5rem',
                      letterSpacing: 8,
                      minWidth: 160,
                      color: screenSaverPin ? '#fff' : '#555',
                    }}
                  >
                    {screenSaverPin.replace(/./g, '●') || '—'}
                  </div>
                </div>

                {/* Numeric Keypad */}
                <div className="row no-gutters mb-3">
                  {['1','2','3','4','5','6','7','8','9'].map(num => (
                    <div key={num} className="col-4 p-1">
                      <button
                        className="btn btn-block font-weight-bold"
                        style={{
                          background: '#0f3460',
                          color: '#fff',
                          border: '1px solid #2a2a4a',
                          borderRadius: 12,
                          fontSize: '1.25rem',
                          height: 56,
                        }}
                        onClick={() => {
                          if (screenSaverPin.length < 6) {
                            setScreenSaverPin(prev => prev + num);
                            setScreenSaverError('');
                          }
                        }}
                      >
                        {num}
                      </button>
                    </div>
                  ))}
                  <div className="col-4 p-1">
                    <button
                      className="btn btn-block font-weight-bold"
                      style={{
                        background: '#0f3460',
                        color: '#fff',
                        border: '1px solid #2a2a4a',
                        borderRadius: 12,
                        fontSize: '1.25rem',
                        height: 56,
                      }}
                      onClick={() => { setScreenSaverPin(''); setScreenSaverError(''); }}
                    >
                      C
                    </button>
                  </div>
                  <div className="col-4 p-1">
                    <button
                      className="btn btn-block font-weight-bold"
                      style={{
                        background: '#0f3460',
                        color: '#fff',
                        border: '1px solid #2a2a4a',
                        borderRadius: 12,
                        fontSize: '1.25rem',
                        height: 56,
                      }}
                      onClick={() => {
                        if (screenSaverPin.length < 6) {
                          setScreenSaverPin(prev => prev + '0');
                          setScreenSaverError('');
                        }
                      }}
                    >
                      0
                    </button>
                  </div>
                  <div className="col-4 p-1">
                    <button
                      className="btn btn-block font-weight-bold"
                      style={{
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: '1.25rem',
                        height: 56,
                      }}
                      onClick={handleScreenSaverGo}
                      disabled={timeclockLoading}
                    >
                      {timeclockLoading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        'Go'
                      )}
                    </button>
                  </div>
                </div>

                {/* Backspace (below keypad, centered) */}
                <div className="text-center mb-2">
                  <button
                    className="btn btn-sm text-muted"
                    style={{ color: '#aaa' }}
                    onClick={() => setScreenSaverPin(prev => prev.slice(0, -1))}
                  >
                    <i className="fas fa-backspace mr-1"></i>Backspace
                  </button>
                </div>

                {screenSaverError && (
                  <div className="alert alert-danger py-2 text-center" style={{ borderRadius: 10, fontSize: '0.85rem' }}>
                    <i className="fas fa-exclamation-circle mr-1"></i>{screenSaverError}
                  </div>
                )}

                <p className="text-center small mb-0" style={{ color: '#888' }}>
                  Only session opener or authorized staff can unlock
                </p>
              </>
            ) : (
              <>
                {/* Timeclock / Unlock Choice */}
                <h5 className="font-weight-bold text-center mb-3" style={{ color: '#fff' }}>
                  <i className="fas fa-user-check mr-2 text-success"></i>Welcome, {screenSaverVerifiedEmp?.name}
                </h5>

                <div className="mb-3">
                  {/* Unlock POS only for non-employee roles */}
                  {screenSaverVerifiedEmp?.role !== 'employee' && (
                    <button
                      className="btn btn-block btn-lg font-weight-bold mb-2"
                      style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 12 }}
                      onClick={unlockPos}
                    >
                      <i className="fas fa-unlock mr-2"></i>Unlock POS
                    </button>
                  )}

                  {!screenSaverVerifiedEmp?.clockedIn ? (
                    <button
                      className="btn btn-block btn-lg font-weight-bold mb-2"
                      style={{ background: '#0f3460', color: '#fff', border: '1px solid #2a2a4a', borderRadius: 12 }}
                      onClick={handleClockIn}
                      disabled={timeclockLoading}
                    >
                      {timeclockLoading ? (
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                      ) : (
                        <i className="fas fa-clock mr-2"></i>
                      )}
                      Clock In
                    </button>
                  ) : (
                    <button
                      className="btn btn-block btn-lg font-weight-bold mb-2"
                      style={{ background: '#0f3460', color: '#fff', border: '1px solid #2a2a4a', borderRadius: 12 }}
                      onClick={handleClockOut}
                      disabled={timeclockLoading}
                    >
                      {timeclockLoading ? (
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                      ) : (
                        <i className="fas fa-clock mr-2"></i>
                      )}
                      Clock Out
                    </button>
                  )}
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-sm text-muted"
                    style={{ color: '#aaa' }}
                    onClick={() => {
                      setShowTimeclockChoice(false);
                      setScreenSaverVerifiedEmp(null);
                      setScreenSaverPin('');
                      setScreenSaverError('');
                    }}
                  >
                    <i className="fas fa-arrow-left mr-1"></i>Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="pos-header d-flex align-items-center justify-content-between px-4 py-3" style={{ background: '#fff', borderBottom: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <div className="d-flex align-items-center">
          <button className="btn btn-link text-muted mr-3" onClick={handleBackButton}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h5 className="m-0 font-weight-bold">{pos?.posName}</h5>
            <small className="text-muted">
              <i className="fas fa-store mr-1"></i>
              {location?.companyName || location?.locationName}
            </small>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className="mr-4 text-right">
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Session Started</div>
            <div className="font-weight-bold">{formatTime(session?.startTime)}</div>
          </div>
          <button
            className="btn btn-outline-primary mr-2"
            style={{ borderRadius: '12px' }}
            onClick={async () => { await loadOpenTabs(); setShowOpenTabsModal(true); }}
          >
            <i className="fas fa-clipboard-list mr-2"></i>
            Open Tabs {openTabs.length > 0 && <span className="badge badge-light ml-1">{openTabs.length}</span>}
          </button>
          <button
            className="btn btn-outline-danger"
            style={{ borderRadius: '12px' }}
            onClick={() => setShowCloseModal(true)}
          >
            <i className="fas fa-power-off mr-2"></i>Close POS
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 d-flex overflow-hidden">
        {/* Products Section */}
        <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
          {/* Categories & Search */}
          <div className="p-3" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
            {/* Horizontal Scrollable Categories */}
            <div className="d-flex overflow-auto" style={{ gap: 12, paddingBottom: 4, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* All Categories Pill */}
              <div
                className="text-center cursor-pointer"
                style={{
                  minWidth: 72,
                  cursor: 'pointer',
                  opacity: selectedCategory === 'all' ? 1 : 0.6,
                  transform: selectedCategory === 'all' ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.2s',
                }}
                onClick={() => setSelectedCategory('all')}
              >
                <div
                  className="mx-auto mb-1 d-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: selectedCategory === 'all' ? '#ff6b35' : '#f8f9fa',
                    border: selectedCategory === 'all' ? '2px solid #ff6b35' : '2px solid transparent',
                  }}
                >
                  <i className="fas fa-th-large" style={{ color: selectedCategory === 'all' ? '#fff' : '#6c757d', fontSize: 20 }}></i>
                </div>
                <div className="font-weight-bold" style={{ fontSize: '0.75rem', color: selectedCategory === 'all' ? '#ff6b35' : '#6c757d' }}>
                  All
                </div>
              </div>

              {/* Deals Category */}
              <div
                className="text-center cursor-pointer"
                style={{
                  minWidth: 72,
                  cursor: 'pointer',
                  opacity: selectedCategory === '__deals__' ? 1 : 0.6,
                  transform: selectedCategory === '__deals__' ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.2s',
                }}
                onClick={() => setSelectedCategory('__deals__')}
              >
                <div
                  className="mx-auto mb-1 d-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: selectedCategory === '__deals__' ? '#dc3545' : '#f8f9fa',
                    border: selectedCategory === '__deals__' ? '2px solid #dc3545' : '2px solid transparent',
                  }}
                >
                  <i className="fas fa-tags" style={{ color: selectedCategory === '__deals__' ? '#fff' : '#dc3545', fontSize: 20 }}></i>
                </div>
                <div className="font-weight-bold" style={{ fontSize: '0.75rem', color: selectedCategory === '__deals__' ? '#dc3545' : '#6c757d' }}>
                  Deals
                </div>
              </div>

              {/* Category Pills */}
              {categories.map(cat => {
                const catName = cat.name || cat.id;
                const isSelected = selectedCategory === catName;
                return (
                  <div
                    key={cat.id}
                    className="text-center cursor-pointer"
                    style={{
                      minWidth: 72,
                      cursor: 'pointer',
                      opacity: isSelected ? 1 : 0.6,
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => setSelectedCategory(catName)}
                  >
                    <div
                      className="mx-auto mb-1 d-flex align-items-center justify-content-center overflow-hidden"
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 16,
                        background: isSelected ? '#ff6b35' : '#f8f9fa',
                        border: isSelected ? '2px solid #ff6b35' : '2px solid transparent',
                      }}
                    >
                      {cat.imageUrl ? (
                        <img
                          src={cat.imageUrl}
                          alt={catName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <i className="fas fa-utensils" style={{ color: isSelected ? '#fff' : '#6c757d', fontSize: 20 }}></i>
                      )}
                    </div>
                    <div className="font-weight-bold text-truncate" style={{ fontSize: '0.75rem', color: isSelected ? '#ff6b35' : '#6c757d', maxWidth: 72 }}>
                      {catName}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Search Bar */}
            <div className="input-group mt-3">
              <div className="input-group-prepend">
                <span className="input-group-text bg-white border-right-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control border-left-0"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <div className="input-group-append">
                  <button className="btn btn-outline-secondary" type="button" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-grow-1 overflow-auto p-3">
            <div className="row">
              {filteredProducts.map(product => (
                <div key={product.id} className="col-lg-3 col-md-4 col-sm-6 mb-3">
                  <div 
                    className="card h-100 cursor-pointer"
                    style={{ 
                      borderRadius: '16px', 
                      border: 'none', 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      cursor: isProductSellable(product) ? 'pointer' : 'not-allowed',
                      transition: 'transform 0.15s',
                      opacity: isProductSellable(product) ? 1 : 0.45,
                      position: 'relative',
                    }}
                    onClick={() => addToCart(product)}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    {!isProductSellable(product) && (
                      <span style={{ position: 'absolute', top: 8, right: 8, background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 6, letterSpacing: 0.5 }}>
                        OUT OF STOCK
                      </span>
                    )}
                    <div className="card-body p-3 text-center">
                      {product.imageUrl ? (
                        <img 
                          src={product.imageUrl} 
                          alt={product.name}
                          className="mb-2"
                          style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
                        />
                      ) : (
                        <div 
                          className="d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{ width: 60, height: 60, background: '#f8f9fa', borderRadius: 8 }}
                        >
                          <i className="fas fa-utensils text-muted"></i>
                        </div>
                      )}
                      <h6 className="font-weight-bold mb-1" style={{ fontSize: '0.9rem' }}>{product.name}</h6>
                      <div className="font-weight-bold" style={{ color: '#ff6b35' }}>
                        {product.onSale && product.salePrice !== undefined ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.75rem', marginRight: 4 }}>
                              {formatCurrency(product.price)}
                            </span>
                            {formatCurrency(product.salePrice)}
                          </>
                        ) : (
                          formatCurrency(product.price)
                        )}
                      </div>
                      {product.onSale && (
                        <span className="badge badge-danger" style={{ fontSize: '0.6rem', marginTop: 2 }}>SALE</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {filteredProducts.length === 0 && (
              <div className="text-center py-5">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <p className="text-muted">No products found</p>
              </div>
            )}
          </div>
        </div>

        {/* Cart Section */}
        <div className="cart-section d-flex flex-column" style={{ width: 380, background: '#fff', borderLeft: '1px solid #e9ecef' }}>
          {/* Cart Header */}
          <div className="p-3" style={{ borderBottom: '1px solid #e9ecef' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="m-0 font-weight-bold">
                <i className="fas fa-shopping-cart mr-2"></i>Cart ({cartCount})
              </h6>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-sm btn-link text-primary p-0 mr-2"
                  onClick={() => {
                    setCustomItemDescription('');
                    setCustomItemPrice('');
                    setCustomItemQty(1);
                    setCustomItemInstructions('');
                    setCustomItemWorkzoneId('');
                    setShowCustomItemModal(true);
                  }}
                >
                  <i className="fas fa-plus-square mr-1"></i>Custom Item
                </button>
                {cart.length > 0 && (
                  <button className="btn btn-sm btn-link text-danger p-0" onClick={() => setCart([])}>
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Customer Selector */}
            <div className="position-relative" ref={customerDropdownRef}>
              <div
                className="d-flex align-items-center px-2 py-1"
                style={{
                  border: '1px solid #ced4da',
                  borderRadius: 4,
                  background: '#fff',
                  cursor: 'pointer',
                  minHeight: 31,
                }}
                onClick={() => {
                  setShowCustomerDropdown(!showCustomerDropdown);
                  if (!showCustomerDropdown) setCustomerSearch('');
                }}
              >
                <i className="fas fa-user text-muted mr-2" style={{ fontSize: 12 }}></i>
                <span className="text-truncate flex-grow-1" style={{ fontSize: '0.875rem' }}>
                  {selectedCustomer ? selectedCustomer.name : 'Walk-in Customer'}
                </span>
                <i className={`fas fa-chevron-${showCustomerDropdown ? 'up' : 'down'} text-muted ml-2`} style={{ fontSize: 10 }}></i>
              </div>

              {showCustomerDropdown && (
                <div
                  className="position-absolute w-100 mt-1"
                  style={{
                    background: '#fff',
                    border: '1px solid #ced4da',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: 280,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Search input inside dropdown */}
                  <div className="p-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-white border-right-0">
                          <i className="fas fa-search text-muted" style={{ fontSize: 12 }}></i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control border-left-0"
                        placeholder="Search customers..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Customer list */}
                  <div className="overflow-auto" style={{ maxHeight: 220 }}>
                    <div
                      className="px-3 py-2 cursor-pointer"
                      style={{ cursor: 'pointer', fontSize: '0.875rem' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      onClick={() => {
                        setSelectedCustomer(null);
                        setShowCustomerDropdown(false);
                      }}
                    >
                      <i className="fas fa-walking mr-2 text-muted"></i>
                      Walk-in Customer
                    </div>
                    {customers
                      .filter(c => !customerSearch || c.name?.toLowerCase().includes(customerSearch.toLowerCase()) || c.email?.toLowerCase().includes(customerSearch.toLowerCase()) || c.phone?.includes(customerSearch))
                      .map(cust => (
                        <div
                          key={cust.id}
                          className="px-3 py-2 cursor-pointer"
                          style={{ cursor: 'pointer', fontSize: '0.875rem', borderTop: '1px solid #f8f9fa' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          onClick={() => {
                            setSelectedCustomer(cust);
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <div className="font-weight-bold">{cust.name}</div>
                          {(cust.email || cust.phone) && (
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {cust.email}{cust.email && cust.phone ? ' • ' : ''}{cust.phone}
                            </div>
                          )}
                        </div>
                      ))}
                    {customers.filter(c => !customerSearch || c.name?.toLowerCase().includes(customerSearch.toLowerCase()) || c.email?.toLowerCase().includes(customerSearch.toLowerCase()) || c.phone?.includes(customerSearch)).length === 0 && (
                      <div className="px-3 py-2 text-muted text-center" style={{ fontSize: '0.8rem' }}>
                        No customers found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order type (native POS parity — drives reporting + KDS tickets) */}
          {orderTypes.length > 0 && (
            <div className="px-3 pt-2 pb-1 border-bottom">
              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                {orderTypes.map(ot => {
                  const isSel = selectedOrderType === ot.name;
                  return (
                    <button key={ot.id} type="button"
                      className={`btn btn-sm ${isSel ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ borderRadius: 16, backgroundColor: isSel ? '#ff6b35' : undefined, borderColor: isSel ? '#ff6b35' : undefined }}
                      onClick={() => setSelectedOrderType(ot.name)}>
                      {ot.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cart Items */}
          <div className="flex-grow-1 overflow-auto p-3">
            {cart.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-shopping-basket fa-3x text-muted mb-3"></i>
                <p className="text-muted">Cart is empty</p>
                <p className="text-muted" style={{ fontSize: '0.8rem' }}>Click a product to add it</p>
              </div>
            ) : (
              cart.map(item => {
                const isSelected = selectedCartItem != null && cartLineKey(selectedCartItem) === cartLineKey(item);
                return (
                  <div
                    key={cartLineKey(item)}
                    className="d-flex align-items-center mb-3 p-2"
                    style={{
                      background: isSelected ? '#fff3e0' : '#f8f9fa',
                      borderRadius: 12,
                      cursor: 'pointer',
                      border: isSelected ? '2px solid #ff6b35' : '2px solid transparent',
                    }}
                    onClick={() => {
                      setSelectedCartItem(isSelected ? null : item);
                      setCalcMode(null);
                      setCalcValue('');
                    }}
                  >
                    <div className="flex-grow-1">
                      <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{item.productName}</div>
                      {item.options?.length > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          + {item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}
                        </div>
                      )}
                      {item.instructions && (
                        <div style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>
                          “{item.instructions}”
                        </div>
                      )}
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                        {item.discountNote ? (
                          <span>
                            <span style={{ textDecoration: 'line-through' }}>{formatCurrency(item.originalPrice || item.price)}</span>
                            {' '}{formatCurrency(item.price)} × {item.qty} = {formatCurrency(item.total)}
                            <span className="text-danger ml-1">{item.discountNote}</span>
                          </span>
                        ) : (
                          <span>{formatCurrency(item.price)} × {item.qty} = {formatCurrency(item.total)}</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-sm btn-light rounded-circle"
                        style={{ width: 32, height: 32, padding: 0 }}
                        onClick={() => updateQuantity(cartLineKey(item), -1)}
                      >
                        <i className="fas fa-minus text-muted" style={{ fontSize: 10 }}></i>
                      </button>
                      <span className="mx-2 font-weight-bold" style={{ minWidth: 24, textAlign: 'center' }}>{item.qty}</span>
                      <button
                        className="btn btn-sm btn-light rounded-circle"
                        style={{ width: 32, height: 32, padding: 0 }}
                        onClick={() => updateQuantity(cartLineKey(item), 1)}
                      >
                        <i className="fas fa-plus text-muted" style={{ fontSize: 10 }}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-link text-danger ml-2 p-0"
                        onClick={() => {
                          removeFromCart(cartLineKey(item));
                          if (selectedCartItem != null && cartLineKey(selectedCartItem) === cartLineKey(item)) {
                            setSelectedCartItem(null);
                            setCalcMode(null);
                            setCalcValue('');
                          }
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Cart Footer */}
          <div className="p-3" style={{ borderTop: '1px solid #e9ecef', background: '#fff' }}>
            {/* Tip & Discount — collapsible */}
            <div className="mb-2">
              <button
                type="button"
                className={`btn btn-sm btn-block ${showTipDiscount ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{
                  borderRadius: 10,
                  fontSize: '0.75rem',
                  backgroundColor: showTipDiscount ? '#ff6b35' : undefined,
                  borderColor: showTipDiscount ? '#ff6b35' : undefined,
                }}
                onClick={() => setShowTipDiscount(v => !v)}
              >
                <i className={`fas ${showTipDiscount ? 'fa-chevron-up' : 'fa-chevron-down'} mr-1`}></i>
                {showTipDiscount ? 'Hide Tip / Discount' : 'Add Tip / Discount'}
                {!showTipDiscount && (cartTip > 0 || cartDiscount > 0) && (
                  <span className="ml-1">
                    ({cartTip > 0 ? `Tip ${formatCurrency(cartTip)}` : ''}
                    {cartTip > 0 && cartDiscount > 0 ? ' / ' : ''}
                    {cartDiscount > 0 ? `Discount -${formatCurrency(cartDiscount)}` : ''})
                  </span>
                )}
              </button>

              {showTipDiscount && (
                <>
                  {/* Tip */}
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <span className="text-muted flex-grow-1">Tip</span>
                      <span className="font-weight-bold">{formatCurrency(cartTip)}</span>
                    </div>
                    <div className="d-flex flex-wrap mb-1" style={{ gap: 6 }}>
                      {(location?.terminalTipping?.percentages || [15, 18, 20]).map(pct => (
                        <button key={pct} type="button"
                          className={`btn btn-sm ${tipIsPercent && parseFloat(tipInput) === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ borderRadius: 16, flex: 1, fontSize: '0.75rem', backgroundColor: tipIsPercent && parseFloat(tipInput) === pct ? '#ff6b35' : undefined, borderColor: tipIsPercent && parseFloat(tipInput) === pct ? '#ff6b35' : undefined }}
                          onClick={() => { setTipIsPercent(true); setTipInput(pct.toString()); }}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                    <div className="d-flex align-items-center">
                      <input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 90 }}
                        value={tipInput} placeholder={tipIsPercent ? 'Custom %' : 'Custom $'}
                        onChange={e => setTipInput(e.target.value)} />
                      <span className="mx-1" style={{ color: tipIsPercent ? '#ff6b35' : '#666', fontSize: '0.8rem' }}>%</span>
                      <div className="custom-control custom-switch mx-1">
                        <input type="checkbox" className="custom-control-input" id="tipToggle"
                          checked={!tipIsPercent}
                          onChange={e => { setTipIsPercent(!e.target.checked); setTipInput(''); }} />
                        <label className="custom-control-label" htmlFor="tipToggle"></label>
                      </div>
                      <span style={{ color: !tipIsPercent ? '#ff6b35' : '#666', fontSize: '0.8rem' }}>$</span>
                    </div>
                  </div>

                  {/* Discount */}
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <span className="text-muted flex-grow-1">Discount</span>
                      <span className="font-weight-bold text-danger">-{formatCurrency(cartDiscount)}</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 120 }}
                        value={discountInput} placeholder="0.00" onChange={e => setDiscountInput(e.target.value)} />
                    </div>
                  </div>

                  {/* Promo Code */}
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <span className="text-muted flex-grow-1">Promo Code</span>
                      {promoDiscount > 0 && (
                        <span className="font-weight-bold" style={{ color: '#2e7d32' }}>-{formatCurrency(promoDiscount)}</span>
                      )}
                    </div>
                    {appliedPromo ? (
                      <div className="d-flex align-items-center justify-content-between p-1" style={{ background: '#e8f5e9', borderRadius: 6 }}>
                        <span className="font-weight-bold" style={{ color: '#2e7d32', fontSize: '0.85rem' }}>{appliedPromo.code}</span>
                        <button className="btn btn-sm btn-link text-danger p-0" style={{ fontSize: '0.75rem' }} onClick={handleRemovePromo}>Remove</button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <input type="text" className="form-control form-control-sm" style={{ width: 120 }}
                          value={promoCodeInput} placeholder="Enter code"
                          onChange={e => { setPromoCodeInput(e.target.value); setPromoError(''); }}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }} />
                        <button className="btn btn-sm btn-outline-primary" disabled={applyingPromo || !promoCodeInput.trim()} onClick={handleApplyPromo}>
                          {applyingPromo ? '...' : 'Apply'}
                        </button>
                      </div>
                    )}
                    {promoError && <small className="text-danger d-block mt-1">{promoError}</small>}
                  </div>
                </>
              )}
            </div>

            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Subtotal ({cartCount} items)</span>
              <span className="font-weight-bold">{formatCurrency(cartSubtotal)}</span>
            </div>
            {taxRate > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Tax ({taxRate}%)</span>
                <span className="font-weight-bold">{formatCurrency(cartTax)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Promo ({appliedPromo?.code})</span>
                <span className="font-weight-bold" style={{ color: '#2e7d32' }}>-{formatCurrency(promoDiscount)}</span>
              </div>
            )}
            {cartConvenienceFee > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Convenience Fee</span>
                <span className="font-weight-bold">{formatCurrency(cartConvenienceFee)}</span>
              </div>
            )}

            <div className="d-flex justify-content-between mb-2">
              <span className="font-weight-bold" style={{ fontSize: '1.1rem' }}>Total</span>
              <span className="font-weight-bold" style={{ fontSize: '1.25rem', color: '#ff6b35' }}>
                {formatCurrency(cartTotal)}
              </span>
            </div>

            {/* Calculator Mode Buttons */}
            <div className="d-flex mb-2" style={{ gap: 6 }}>
              {[
                { key: 'qty', label: 'Item Qty', icon: 'fa-hashtag' },
                { key: 'price', label: 'Price Override', icon: 'fa-tag' },
                { key: 'discount', label: 'Line Discount', icon: 'fa-percent' },
              ].map(m => (
                <button
                  key={m.key}
                  className={`btn btn-sm flex-fill ${calcMode === m.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{
                    borderRadius: 10,
                    fontSize: '0.7rem',
                    backgroundColor: calcMode === m.key ? '#ff6b35' : undefined,
                    borderColor: calcMode === m.key ? '#ff6b35' : undefined,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => {
                    if (calcMode === m.key) {
                      setCalcMode(null);
                      setCalcValue('');
                    } else {
                      setCalcMode(m.key);
                      setCalcValue('');
                    }
                  }}
                >
                  <i className={`fas ${m.icon} mr-1`}></i>{m.label}
                </button>
              ))}
            </div>

            {/* Calculator Display */}
            {calcMode && (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center px-2 py-1 mb-1" style={{ background: '#f8f9fa', borderRadius: 8, minHeight: 32 }}>
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {calcMode === 'qty' ? 'Qty' : calcMode === 'price' ? 'Price' : 'Discount'} for {selectedCartItem ? selectedCartItem.productName : '— select item'}
                  </span>
                  <span className="font-weight-bold" style={{ fontSize: '1rem', color: '#ff6b35' }}>
                    {calcValue || '0'}
                  </span>
                </div>

                {/* Numpad */}
                <div className="row no-gutters" style={{ gap: 4 }}>
                  {['C', '←', '+/-'].map(key => (
                    <div key={key} className="col" style={{ padding: 2 }}>
                      <button
                        className="btn btn-light btn-block font-weight-bold"
                        style={{ borderRadius: 10, fontSize: '0.85rem', height: 40, padding: 0 }}
                        onClick={() => pressCalc(key)}
                      >
                        {key === '←' ? <i className="fas fa-backspace"></i> : key}
                      </button>
                    </div>
                  ))}
                </div>
                {[
                  ['7', '8', '9'],
                  ['4', '5', '6'],
                  ['1', '2', '3'],
                  ['.', '0', ''],
                ].map((row, rIdx) => (
                  <div key={rIdx} className="row no-gutters" style={{ gap: 4 }}>
                    {row.map(key => (
                      <div key={key || 'apply'} className="col" style={{ padding: 2 }}>
                        {key ? (
                          <button
                            className="btn btn-light btn-block font-weight-bold"
                            style={{ borderRadius: 10, fontSize: '0.9rem', height: 44, padding: 0 }}
                            onClick={() => pressCalc(key)}
                          >
                            {key}
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary btn-block font-weight-bold"
                            style={{ borderRadius: 10, fontSize: '0.85rem', height: 44, padding: 0, backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                            disabled={!selectedCartItem || calcValue === ''}
                            onClick={applyCalc}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <button
              className="btn btn-block btn-lg font-weight-bold"
              style={{
                backgroundColor: cart.length > 0 ? '#ff6b35' : '#e9ecef',
                borderColor: cart.length > 0 ? '#ff6b35' : '#e9ecef',
                color: cart.length > 0 ? '#fff' : '#6c757d',
                borderRadius: '16px',
              }}
              disabled={cart.length === 0}
              onClick={handleCheckout}
            >
              <i className="fas fa-credit-card mr-2"></i>
              Charge {formatCurrency(cartTotal)}
            </button>

            <button
              className="btn btn-block btn-lg font-weight-bold mt-2"
              style={{
                backgroundColor: cart.length > 0 ? '#28a745' : '#e9ecef',
                borderColor: cart.length > 0 ? '#28a745' : '#e9ecef',
                color: cart.length > 0 ? '#fff' : '#6c757d',
                borderRadius: '16px',
              }}
              disabled={cart.length === 0}
              onClick={handleOpenTab}
            >
              <i className="fas fa-clipboard-list mr-2"></i>
              Open Tab
            </button>
          </div>
        </div>
      </div>

      {/* Product Options Modal */}
      {showOptionsModal && optionsProduct && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxHeight: '85vh' }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">Select Options — {optionsProduct.name}</h5>
                <button type="button" className="close" onClick={() => { setShowOptionsModal(false); setOptionsProduct(null); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {optionsProduct.description && <p className="text-muted">{optionsProduct.description}</p>}

                {/* Quantity */}
                <div className="mb-3">
                  <div className="font-weight-bold mb-2">Quantity</div>
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <button type="button" className="btn btn-light"
                      style={{ borderRadius: '50%', width: 36, height: 36, padding: 0 }}
                      onClick={() => setOptionsQty(q => Math.max(1, q - 1))}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="font-weight-bold" style={{ fontSize: '1.2rem', minWidth: 32, textAlign: 'center' }}>
                      {optionsQty}
                    </span>
                    <button type="button" className="btn btn-light"
                      style={{ borderRadius: '50%', width: 36, height: 36, padding: 0 }}
                      onClick={() => setOptionsQty(q => q + 1)}>
                      <i className="fas fa-plus"></i>
                    </button>
                    <span className="ml-auto font-weight-bold text-muted">
                      {formatCurrency(
                        ((optionsProduct.onSale && optionsProduct.salePrice !== undefined ? optionsProduct.salePrice : (optionsProduct.price || 0))
                        + Object.values(optionsExtraFees).reduce((sum, fee) => sum + fee, 0)) * optionsQty
                      )}
                    </span>
                  </div>
                </div>

                {/* Options */}
                {productOptions
                  .filter(opt => optionsProduct.optionIds?.includes(opt.id))
                  .map(option => (
                    <div key={option.id} className="mb-3">
                      <div className="font-weight-bold mb-2">
                        {option.name}
                        {option.optionRequired && <span className="text-danger"> *</span>}
                      </div>
                      <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                        {option.options?.map(optItem => {
                          const isSelected = selectedOptions[option.id]?.has(optItem.name);
                          return (
                            <button key={optItem.name} type="button"
                              className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                              style={{
                                borderRadius: 16,
                                backgroundColor: isSelected ? '#ff6b35' : undefined,
                                borderColor: isSelected ? '#ff6b35' : undefined,
                              }}
                              onClick={() => {
                                const newSelected = { ...selectedOptions };
                                const newFees = { ...optionsExtraFees };

                                if (option.allowMultipleSelection) {
                                  newSelected[option.id] = new Set(newSelected[option.id] || []);
                                  if (newSelected[option.id].has(optItem.name)) {
                                    newSelected[option.id].delete(optItem.name);
                                    delete newFees[optItem.name];
                                  } else {
                                    newSelected[option.id].add(optItem.name);
                                    newFees[optItem.name] = optItem.extraFee || 0;
                                  }
                                } else {
                                  newSelected[option.id] = new Set([optItem.name]);
                                  // clear fees for this option group
                                  option.options?.forEach(oi => delete newFees[oi.name]);
                                  newFees[optItem.name] = optItem.extraFee || 0;
                                }

                                setSelectedOptions(newSelected);
                                setOptionsExtraFees(newFees);
                              }}
                            >
                              {optItem.name}{optItem.extraFee > 0 ? ` +${formatCurrency(optItem.extraFee)}` : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                {/* Special Instructions */}
                {optionsProduct?.acceptSpecialRequirement && (
                  <div className="mb-3">
                    <div className="font-weight-bold mb-2">Special Instructions</div>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="e.g. No onions, extra sauce..."
                      value={optionsInstructions}
                      onChange={(e) => setOptionsInstructions(e.target.value)}
                      style={{ borderRadius: 12, resize: 'none' }}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: 12 }}
                  onClick={() => { setShowOptionsModal(false); setOptionsProduct(null); }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-primary font-weight-bold"
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12 }}
                  onClick={() => {
                    const applicableOptions = productOptions.filter(opt => optionsProduct.optionIds?.includes(opt.id));
                    for (const option of applicableOptions) {
                      if (option.optionRequired && (!selectedOptions[option.id] || selectedOptions[option.id].size === 0)) {
                        Swal.fire('Error', `Please select required option: ${option.name}`, 'error');
                        return;
                      }
                    }
                    const extraFeeTotal = Object.values(optionsExtraFees).reduce((sum, fee) => sum + fee, 0);
                    const flatOptions = [];
                    for (const [optId, namesSet] of Object.entries(selectedOptions)) {
                      const optionDef = productOptions.find(o => o.id === optId);
                      namesSet.forEach(name => {
                        const optItem = optionDef?.options?.find(oi => oi.name === name);
                        if (optItem) {
                          flatOptions.push({
                            optionId: optId,
                            optionName: optionDef?.name,
                            itemName: name,
                            extraFee: optItem.extraFee || 0,
                          });
                        }
                      });
                    }
                    finalizeAddToCart(optionsProduct, flatOptions, extraFeeTotal, optionsQty, optionsInstructions);
                    setShowOptionsModal(false);
                    setOptionsProduct(null);
                    setSelectedOptions({});
                    setOptionsExtraFees({});
                    setOptionsQty(1);
                    setOptionsInstructions('');
                  }}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add to Cart
                  <span className="ml-2">
                    {formatCurrency(
                      ((optionsProduct.onSale && optionsProduct.salePrice !== undefined ? optionsProduct.salePrice : (optionsProduct.price || 0))
                      + Object.values(optionsExtraFees).reduce((sum, fee) => sum + fee, 0)) * optionsQty
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Payment</h5>
                <button type="button" className="close" onClick={() => setShowPaymentModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>Amount Due</div>
                  <div className="font-weight-bold" style={{ fontSize: '2rem', color: '#ff6b35' }}>
                    {formatCurrency(cartTotal)}
                  </div>
                </div>

                {/* Payment Method Tabs */}
                <div className="d-flex mb-4">
                  {/* Cash hides when the location turned it off for the POS
                      channel (acceptCashByChannel.pos; legacy acceptCash is
                      the fallback) — native payment_sheet parity */}
                  {['cash', 'card', 'stripe'].filter(m => {
                    if (m !== 'cash') return true;
                    const byChannel = location?.acceptCashByChannel;
                    if (byChannel && typeof byChannel === 'object') return byChannel.pos !== false;
                    return location?.acceptCash !== false;
                  }).map(method => (
                    <button
                      key={method}
                      className={`btn flex-fill mr-2 ${paymentMethod === method ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        borderRadius: '12px',
                        backgroundColor: paymentMethod === method ? '#ff6b35' : 'transparent',
                        borderColor: paymentMethod === method ? '#ff6b35' : '#dee2e6',
                        color: paymentMethod === method ? '#fff' : '#6c757d',
                      }}
                      onClick={() => setPaymentMethod(method)}
                    >
                      <i className={`fas mr-2 ${
                        method === 'cash' ? 'fa-money-bill-wave' : 
                        method === 'card' ? 'fa-credit-card' : 'fa-mobile-alt'
                      }`}></i>
                      {method === 'cash' ? 'Cash' : method === 'card' ? 'Card' : 'Stripe'}
                    </button>
                  ))}
                </div>

                {/* Cash Payment Details */}
                {paymentMethod === 'cash' && (
                  <div>
                    <label className="font-weight-bold">Cash Received</label>
                    <div className="input-group input-group-lg mb-3">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        placeholder="0.00"
                        autoFocus
                      />
                    </div>
                    
                    {/* Quick Amount Buttons */}
                    <div className="row mb-3">
                      {[5, 10, 20, 50, 100].map(amount => (
                        <div key={amount} className="col">
                          <button
                            className="btn btn-outline-secondary btn-block"
                            style={{ borderRadius: '10px' }}
                            onClick={() => setCashReceived(amount.toString())}
                          >
                            ${amount}
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="row mb-3">
                      {['Exact', '+1', '+5', '+10'].map(label => (
                        <div key={label} className="col">
                          <button
                            className="btn btn-outline-secondary btn-block"
                            style={{ borderRadius: '10px' }}
                            onClick={() => {
                              if (label === 'Exact') setCashReceived(cartTotal.toFixed(2));
                              else {
                                const current = parseFloat(cashReceived || 0);
                                const add = parseInt(label.replace('+', ''));
                                setCashReceived((current + add).toFixed(2));
                              }
                            }}
                          >
                            {label}
                          </button>
                        </div>
                      ))}
                    </div>

                    {parseFloat(cashReceived || 0) >= cartTotal && (
                      <div className="alert alert-light border text-center" style={{ borderRadius: '12px' }}>
                        <div className="text-muted">Change Due</div>
                        <div className="font-weight-bold" style={{ fontSize: '1.5rem', color: '#28a745' }}>
                          {formatCurrency(changeDue)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Card Payment */}
                {paymentMethod === 'card' && (
                  <div className="py-2">
                    {/* Card entry mode selector */}
                    <div className="d-flex mb-3" style={{ gap: 8 }}>
                      <button
                        className={`btn flex-fill ${cardEntryMode === 'terminal' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{
                          borderRadius: '10px',
                          backgroundColor: cardEntryMode === 'terminal' ? '#ff6b35' : 'transparent',
                          borderColor: cardEntryMode === 'terminal' ? '#ff6b35' : '#dee2e6',
                          color: cardEntryMode === 'terminal' ? '#fff' : '#6c757d',
                        }}
                        onClick={() => setCardEntryMode('terminal')}
                      >
                        <i className="fas fa-credit-card mr-2"></i>Terminal
                      </button>
                      <button
                        className={`btn flex-fill ${cardEntryMode === 'manual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{
                          borderRadius: '10px',
                          backgroundColor: cardEntryMode === 'manual' ? '#ff6b35' : 'transparent',
                          borderColor: cardEntryMode === 'manual' ? '#ff6b35' : '#dee2e6',
                          color: cardEntryMode === 'manual' ? '#fff' : '#6c757d',
                        }}
                        onClick={() => setCardEntryMode('manual')}
                      >
                        <i className="fas fa-keyboard mr-2"></i>Manual Entry
                      </button>
                    </div>

                    {cardEntryMode === 'terminal' && (
                      <div className="text-center py-2">
                        <i className="fas fa-credit-card fa-3x text-muted mb-3"></i>
                        {!pos?.stripeTerminalId ? (
                          <div className="alert alert-warning" style={{ borderRadius: 12 }}>
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            No Stripe reader paired. Please pair a reader from the POS list first.
                          </div>
                        ) : checkingTerminal ? (
                          <p className="text-muted">
                            <span className="spinner-border spinner-border-sm mr-2"></span>
                            Checking terminal status...
                          </p>
                        ) : terminalReaderOnline ? (
                          <div className="alert alert-success" style={{ borderRadius: 12 }}>
                            <i className="fas fa-check-circle mr-2"></i>
                            Terminal reader is online and ready.
                          </div>
                        ) : (
                          <div className="alert alert-danger" style={{ borderRadius: 12 }}>
                            <i className="fas fa-times-circle mr-2"></i>
                            Terminal reader is offline. Please check the device.
                            <button className="btn btn-sm btn-link" onClick={checkTerminalStatus}>
                              Retry
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {cardEntryMode === 'manual' && (
                      <ManualCardForm
                        amount={cartBaseTotal}
                        pos={pos}
                        sessionId={sessionId}
                        user={user}
                        feeEnabled={location?.convenienceFee?.enabled === true}
                        onSuccess={async (result) => {
                          setShowPaymentModal(false);
                          await finalizeSale({
                            paymentIntentId: result.paymentIntentId,
                            cardBrand: result.cardBrand,
                            cardLast4: result.cardLast4,
                            totalOverride: result.amount,
                            convenienceFeeOverride: result.convenienceFeeAmount,
                          });
                        }}
                        onError={(msg) => Swal.fire('Error', msg, 'error')}
                      />
                    )}
                  </div>
                )}

                {/* Stripe Payment */}
                {paymentMethod === 'stripe' && (
                  <div className="text-center py-3">
                    <i className="fab fa-stripe fa-3x text-muted mb-3"></i>
                    {stripeCheckoutUrl ? (
                      <div>
                        <p className="text-muted">Scan or open this link to pay:</p>
                        <div className="input-group mb-3">
                          <input
                            type="text"
                            className="form-control"
                            value={stripeCheckoutUrl}
                            readOnly
                          />
                          <div className="input-group-append">
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                navigator.clipboard.writeText(stripeCheckoutUrl);
                                Swal.fire({ icon: 'success', title: 'Copied!', timer: 1000, showConfirmButton: false });
                              }}
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <a
                          href={stripeCheckoutUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-block"
                          style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12 }}
                        >
                          <i className="fas fa-external-link-alt mr-2"></i>Open Checkout
                        </a>
                      </div>
                    ) : (
                      <p className="text-muted">Click Complete Sale to generate a Stripe checkout link.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-lg btn-primary font-weight-bold"
                  style={{ 
                    backgroundColor: '#ff6b35', 
                    borderColor: '#ff6b35',
                    borderRadius: '12px',
                    minWidth: 150,
                  }}
                  disabled={
                    saving ||
                    (paymentMethod === 'cash' && parseFloat(cashReceived || 0) < cartTotal) ||
                    (paymentMethod === 'card' && cardEntryMode === 'terminal' && (!pos?.stripeTerminalId || !terminalReaderOnline)) ||
                    (paymentMethod === 'card' && cardEntryMode === 'manual') ||
                    (paymentMethod === 'stripe' && stripeCheckoutUrl)
                  }
                  onClick={handlePayment}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                  ) : (
                    <i className="fas fa-check mr-2"></i>
                  )}
                  Complete Sale
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Waiting Modal */}
      {showTerminalWaitingModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', textAlign: 'center' }}>
              <div className="modal-body py-5">
                <div className="mb-4">
                  <i className="fas fa-credit-card fa-4x text-primary" style={{ animation: 'pulse 1.5s infinite' }}></i>
                </div>
                <h4 className="font-weight-bold mb-3">Waiting for Card</h4>
                <p className="text-muted mb-2" style={{ fontSize: '1.1rem' }}>
                  {terminalStatusMessage || 'Please tap or insert card on the terminal...'}
                </p>
                <div className="font-weight-bold mb-4" style={{ fontSize: '1.5rem', color: '#ff6b35' }}>
                  {formatCurrency(cartTotal)}
                </div>
                <div className="d-flex justify-content-center">
                  <button
                    className="btn btn-outline-danger"
                    style={{ borderRadius: '12px' }}
                    onClick={cancelTerminalPayment}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancel Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Checkout Waiting Modal */}
      {showStripeWaitingModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', textAlign: 'center' }}>
              <div className="modal-body py-5">
                <div className="mb-4">
                  <i className="fab fa-stripe fa-4x text-primary" style={{ animation: 'pulse 1.5s infinite' }}></i>
                </div>
                <h4 className="font-weight-bold mb-3">Waiting for Checkout</h4>
                <p className="text-muted mb-2" style={{ fontSize: '1.1rem' }}>
                  Customer is completing payment on Stripe checkout...
                </p>
                <div className="font-weight-bold mb-4" style={{ fontSize: '1.5rem', color: '#ff6b35' }}>
                  {formatCurrency(cartTotal)}
                </div>
                {stripeCheckoutUrl && (
                  <div className="mb-3">
                    <a
                      href={stripeCheckoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary"
                      style={{ borderRadius: '12px' }}
                    >
                      <i className="fas fa-external-link-alt mr-2"></i>Open Checkout Again
                    </a>
                  </div>
                )}
                <div className="d-flex justify-content-center">
                  <button
                    className="btn btn-outline-danger"
                    style={{ borderRadius: '12px' }}
                    onClick={cancelStripeCheckout}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancel Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back Button Auth Modal */}
      {showBackModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">Leave POS Session</h5>
                <button type="button" className="close" onClick={() => setShowBackModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">Verify your identity to leave this POS session.</p>
                <div className="form-group">
                  <label className="font-weight-bold">Employee <span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    value={backEmployeeId}
                    onChange={e => { setBackEmployeeId(e.target.value); setBackEmployeePin(''); }}
                  >
                    <option value="">— Select employee —</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>
                    ))}
                  </select>
                </div>
                {backEmployeeId && employees.find(e => e.id === backEmployeeId)?.pinHash && (
                  <div className="form-group">
                    <label className="font-weight-bold">Passkey <span className="text-danger">*</span></label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="form-control"
                      value={backEmployeePin}
                      onChange={e => setBackEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 4-6 digit PIN"
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-outline-secondary" style={{ borderRadius: 10 }} onClick={() => setShowBackModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary font-weight-bold"
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 10 }}
                  onClick={verifyBackEmployee}
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>Leave Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close POS Modal */}
      {showCloseModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">Close Session — {pos?.posName}</h5>
                <button type="button" className="close" onClick={() => setShowCloseModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Employee Authentication */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="card border-0" style={{ background: '#fff8f5', borderRadius: '12px' }}>
                      <div className="card-body py-3">
                        <h6 className="font-weight-bold mb-3"><i className="fas fa-user-shield mr-2 text-primary"></i>Employee Authentication Required</h6>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label className="font-weight-bold">Closing Employee <span className="text-danger">*</span></label>
                              <select
                                className="form-control"
                                value={selectedCloseEmployeeId}
                                onChange={e => { setSelectedCloseEmployeeId(e.target.value); setCloseEmployeePin(''); }}
                              >
                                <option value="">— Select employee —</option>
                                {employees.map(emp => (
                                  <option key={emp.id} value={emp.id}>
                                    {emp.name} ({emp.role})
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="col-md-6">
                            {selectedCloseEmployeeId && employees.find(e => e.id === selectedCloseEmployeeId)?.pinHash && (
                              <div className="form-group mb-2">
                                <label className="font-weight-bold">Passkey <span className="text-danger">*</span></label>
                                <input
                                  type="password"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  maxLength={6}
                                  className="form-control"
                                  value={closeEmployeePin}
                                  onChange={e => setCloseEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  placeholder="Enter 4-6 digit PIN"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  {/* Left: Cash Count */}
                  <div className="col-md-6">
                    <h6 className="font-weight-bold mb-3">Cash Count</h6>
                    <p className="text-muted small">Count the cash in your drawer by denomination:</p>
                    <div className="row">
                      {[1, 5, 10, 20, 50, 100].map(denom => (
                        <div key={denom} className="col-6 mb-3">
                          <label className="font-weight-bold">${denom} bills</label>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text">x</span>
                            </div>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              value={closingCash[denom]}
                              onChange={(e) => setClosingCash({ ...closingCash, [denom]: parseInt(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="alert alert-light border" style={{ borderRadius: '12px' }}>
                      <div className="d-flex justify-content-between">
                        <span className="font-weight-bold">Total in Drawer:</span>
                        <span className="font-weight-bold" style={{ color: '#ff6b35' }}>
                          {formatCurrency(calculateClosingTotal())}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Session Summary */}
                  <div className="col-md-6">
                    <h6 className="font-weight-bold mb-3">Session Summary</h6>
                    <div className="card border-0" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                      <div className="card-body">
                        {/* Cash Reconciliation */}
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Starting Cash</span>
                          <span className="font-weight-bold">{formatCurrency(session?.startingCash)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Cash Sales</span>
                          <span className="font-weight-bold text-success">+{formatCurrency(session?.cash)}</span>
                        </div>
                        <hr style={{ margin: '8px 0' }} />
                        <div className="d-flex justify-content-between mb-2">
                          <span className="font-weight-bold">Expected Cash</span>
                          <span className="font-weight-bold">{formatCurrency((session?.startingCash || 0) + (session?.cash || 0))}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="font-weight-bold">Actual Cash</span>
                          <span className="font-weight-bold">{formatCurrency(calculateClosingTotal())}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="font-weight-bold">Difference</span>
                          <span className={`font-weight-bold ${(calculateClosingTotal() - ((session?.startingCash || 0) + (session?.cash || 0))) >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(calculateClosingTotal() - ((session?.startingCash || 0) + (session?.cash || 0)))}
                          </span>
                        </div>
                        <hr style={{ margin: '8px 0' }} />
                        {/* Payment Breakdown */}
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">Total Sales</span>
                          <span className="font-weight-bold">{formatCurrency(session?.totalSales)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted"><i className="fas fa-money-bill-wave text-success mr-1"></i>Cash</span>
                          <span className="font-weight-bold">{formatCurrency(session?.cash)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted"><i className="fas fa-credit-card text-primary mr-1"></i>Card</span>
                          <span className="font-weight-bold">{formatCurrency(session?.cardSales)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted"><i className="fas fa-mobile-alt text-info mr-1"></i>Stripe</span>
                          <span className="font-weight-bold">{formatCurrency(session?.stripeSales)}</span>
                        </div>
                        <hr style={{ margin: '8px 0' }} />
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Total Transactions</span>
                          <span className="font-weight-bold">{session?.totalTransactions || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Recent Transactions */}
                    <h6 className="font-weight-bold mt-4 mb-3">Recent Transactions</h6>
                    <div style={{ maxHeight: 200, overflow: 'auto' }}>
                      {sessionSales.length === 0 ? (
                        <p className="text-muted small">No transactions yet</p>
                      ) : (
                        sessionSales.slice(0, 10).map(sale => (
                          <div key={sale.id} className="d-flex justify-content-between py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                            <div>
                              <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>#{sale.id?.slice(-6)}</div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {sale.items?.length} item(s) • {sale.paymentMethod}
                              </div>
                            </div>
                            <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                              {formatCurrency(sale.total)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={() => setShowCloseModal(false)}>
                  Continue Selling
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-warning font-weight-bold"
                  style={{ borderRadius: '12px', minWidth: 150 }}
                  onClick={() => {
                    setShowCloseModal(false);
                    setScreenSaverActive(true);
                    setScreenSaverPin('');
                    setScreenSaverSelectedEmployeeId('');
                    setScreenSaverError('');
                    setShowTimeclockChoice(false);
                    setScreenSaverVerifiedEmp(null);
                    try {
                      sessionStorage.setItem('screenSaverActive', 'true');
                    } catch (e) {}
                  }}
                >
                  <i className="fas fa-lock mr-2"></i>
                  Lock Screen
                </button>
                <button 
                  type="button" 
                  className="btn btn-lg btn-danger font-weight-bold"
                  style={{ borderRadius: '12px', minWidth: 150 }}
                  onClick={handleClosePos}
                >
                  <i className="fas fa-power-off mr-2"></i>
                  Close Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Open Tabs List Modal */}
      {showOpenTabsModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-scrollable" style={{ minWidth: "80%" }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">
                  <i className="fas fa-clipboard-list mr-2 text-primary"></i>Open Tabs
                </h5>
                <button type="button" className="close" onClick={() => setShowOpenTabsModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* View Toggle */}
                <div className="d-flex mb-3" style={{ background: '#f8f9fa', borderRadius: '12px', padding: '4px' }}>
                  <button
                    className={`btn flex-fill ${openTabsViewMode === 'list' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setOpenTabsViewMode('list')}
                    style={{ borderRadius: '10px' }}
                  >
                    <i className="fas fa-list mr-2"></i>List
                  </button>
                  <button
                    className={`btn flex-fill ${openTabsViewMode === 'floorplan' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setOpenTabsViewMode('floorplan')}
                    style={{ borderRadius: '10px' }}
                  >
                    <i className="fas fa-th-large mr-2"></i>Floor Plan
                  </button>
                </div>

                {openTabsViewMode === 'list' && (
                  <>
                    {/* Search */}
                {openTabs.length > 0 && (
                  <div className="input-group mb-3">
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-white border-right-0">
                        <i className="fas fa-search text-muted"></i>
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-left-0"
                      placeholder="Search tabs by name, customer, or POS..."
                      value={tabSearchQuery}
                      onChange={(e) => setTabSearchQuery(e.target.value)}
                    />
                    {tabSearchQuery && (
                      <div className="input-group-append">
                        <button className="btn btn-outline-secondary" onClick={() => setTabSearchQuery('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {openTabs.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No open tabs</p>
                    <p className="text-muted" style={{ fontSize: '0.8rem' }}>Add items to the cart and click "Open Tab" to create one</p>
                  </div>
                ) : filteredTabs.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-search fa-2x text-muted mb-2"></i>
                    <p className="text-muted">No tabs match "{tabSearchQuery}"</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {filteredTabs.map(tab => (
                      <div key={tab.id} className="list-group-item list-group-item-action" style={{ borderRadius: 12, marginBottom: 8, border: '1px solid #e9ecef' }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <h6 className="font-weight-bold m-0 mr-2">{tab.tabName}</h6>
                              <span className={`badge badge-${tab.paymentMethod === 'card' ? 'info' : tab.paymentMethod === 'define_later' ? 'secondary' : 'success'}`} style={{ fontSize: '0.7rem' }}>
                                {tab.paymentMethod === 'card' ? 'Card' : tab.paymentMethod === 'define_later' ? 'Define Later' : 'Cash'}
                              </span>
                              {tab.status === 'card_presenting' && (
                                <span className="badge badge-warning ml-1" style={{ fontSize: '0.7rem' }}>
                                  <i className="fas fa-spinner fa-spin mr-1"></i>Presenting Card
                                </span>
                              )}
                              {tab.status === 'card_presented' && (
                                <span className="badge badge-success ml-1" style={{ fontSize: '0.7rem' }}>
                                  <i className="fas fa-check mr-1"></i>Card On File
                                </span>
                              )}
                              {tab.receiptPrinted && (
                                <span className="badge badge-dark ml-1" style={{ fontSize: '0.7rem' }}>
                                  <i className="fas fa-print mr-1"></i>Receipt Printed
                                </span>
                              )}
                              {tab.tableName && (
                                <span className="badge badge-secondary ml-1" style={{ fontSize: '0.7rem' }}>
                                  <i className="fas fa-chair mr-1"></i>{tab.tableName}
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                              {tab.itemsQty || 0} item(s) • {tab.customerName || 'Walk-in'}
                              {tab.confirmationNumber && (
                                <span className="ml-2 text-monospace" style={{ fontSize: '0.75rem', opacity: 0.7 }}>{tab.confirmationNumber}</span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                              {tab.posName && (
                                <span className="mr-2"><i className="fas fa-desktop mr-1"></i>{tab.posName}</span>
                              )}
                              {tab.cardBrand && tab.cardLast4 && (
                                <span><i className="fas fa-credit-card mr-1"></i>{tab.cardBrand} •••• {tab.cardLast4}</span>
                              )}
                              <span className="ml-2" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                                <i className="far fa-clock mr-1"></i>{formatElapsed(tab.createdAt)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-weight-bold" style={{ fontSize: '1.1rem', color: '#ff6b35' }}>
                              {formatCurrency(tab.total)}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end mt-2" style={{ gap: 8 }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            style={{ borderRadius: 8 }}
                            onClick={() => { setShowOpenTabsModal(false); viewOpenTab(tab); }}
                          >
                            <i className="fas fa-eye mr-1"></i>View
                          </button>
                          <button
                            className="btn btn-sm btn-outline-success"
                            style={{ borderRadius: 8 }}
                            onClick={() => { setShowOpenTabsModal(false); viewOpenTab(tab); }}
                          >
                            <i className="fas fa-check mr-1"></i>Complete
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{ borderRadius: 8 }}
                            onClick={() => cancelOpenTab(tab)}
                          >
                            <i className="fas fa-times mr-1"></i>Cancel
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </>
              )}

              {openTabsViewMode === 'floorplan' && (
                <div
                  className="card"
                  style={{
                    height: '55vh',
                    background: '#f8f9fa',
                    backgroundImage: 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {tables.length === 0 ? (
                    <div className="d-flex flex-column justify-content-center align-items-center h-100">
                      <i className="fas fa-table fa-4x text-muted mb-3"></i>
                      <h5 className="text-muted">No tables configured</h5>
                    </div>
                  ) : (
                    tables.map(table => {
                      const tabOnTable = openTabs.find(t => t.tableId === table.id);
                      const size = table.shape === 'rectangle' ? { w: 80, h: 50 } : { w: 60, h: 60 };
                      return (
                        <div
                          key={table.id}
                          style={{
                            position: 'absolute',
                            left: table.positionX || 50,
                            top: table.positionY || 50,
                            width: size.w,
                            height: size.h,
                            background: tabOnTable ? '#dc3545' : '#ff6b35',
                            borderRadius: table.shape === 'rounded' ? '50%' : table.shape === 'triangle' ? '0' : '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: tabOnTable ? 'pointer' : 'default',
                            userSelect: 'none',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          }}
                          onClick={() => {
                            if (tabOnTable) {
                              setShowOpenTabsModal(false);
                              viewOpenTab(tabOnTable);
                            }
                          }}
                          title={`${table.name} - ${table.type}${tabOnTable ? ` - ${tabOnTable.tabName}` : ' (Available)'}`}
                        >
                          <span>{table.name}</span>
                          {tabOnTable && (
                            <span style={{ fontSize: '8px', opacity: 0.9, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 4px' }}>
                              {tabOnTable.tabName}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={() => setShowOpenTabsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Open Tab Modal */}
      {showCreateTabModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 450 }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Open New Tab</h5>
                <button type="button" className="close" onClick={closeCreateTabModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>Tab Total</div>
                  <div className="font-weight-bold" style={{ fontSize: '2rem', color: '#ff6b35' }}>
                    {formatCurrency(cartTotal)}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {cartCount} item(s)
                  </div>
                </div>

                <div className="form-group">
                  <label className="font-weight-bold">Tab Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={tabNameInput}
                    onChange={(e) => setTabNameInput(e.target.value)}
                    placeholder="e.g. Table 5 - John"
                    autoFocus
                  />
                </div>

                {/* Table Selector */}
                <div className="form-group">
                  <label className="font-weight-bold">Table <span className="text-muted font-weight-normal">(optional)</span></label>
                  <div className="d-flex" style={{ gap: 8 }}>
                    <select
                      className="form-control"
                      value={selectedTableId || ''}
                      onChange={(e) => setSelectedTableId(e.target.value || null)}
                    >
                      <option value="">No table selected</option>
                      {sections.map(section => (
                        <optgroup key={section.id} label={section.name}>
                          {tables.filter(t => t.sectionId === section.id).map(table => (
                            <option key={table.id} value={table.id}>{table.name}</option>
                          ))}
                        </optgroup>
                      ))}
                      {tables.filter(t => !t.sectionId).map(table => (
                        <option key={table.id} value={table.id}>{table.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => setShowTablePickerModal(true)}
                    >
                      <i className="fas fa-th-large mr-1"></i>Floor Plan
                    </button>
                  </div>
                </div>

                {/* Customer Selector */}
                <div className="form-group">
                  <label className="font-weight-bold">Customer</label>
                  <div className="position-relative" ref={tabCustomerDropdownRef}>
                    <div
                      className="d-flex align-items-center px-2 py-1"
                      style={{
                        border: '1px solid #ced4da',
                        borderRadius: 4,
                        background: '#fff',
                        cursor: 'pointer',
                        minHeight: 38,
                      }}
                      onClick={() => {
                        setShowTabCustomerDropdown(!showTabCustomerDropdown);
                        if (!showTabCustomerDropdown) setTabCustomerSearch('');
                      }}
                    >
                      <i className="fas fa-user text-muted mr-2" style={{ fontSize: 12 }}></i>
                      <span className="text-truncate flex-grow-1" style={{ fontSize: '0.875rem' }}>
                        {tabSelectedCustomer ? tabSelectedCustomer.name : (selectedCustomer ? selectedCustomer.name : 'Walk-in Customer')}
                      </span>
                      <i className={`fas fa-chevron-${showTabCustomerDropdown ? 'up' : 'down'} text-muted ml-2`} style={{ fontSize: 10 }}></i>
                    </div>

                    {showTabCustomerDropdown && (
                      <div
                        className="position-absolute w-100 mt-1"
                        style={{
                          background: '#fff',
                          border: '1px solid #ced4da',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          maxHeight: 280,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div className="p-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                          <div className="input-group input-group-sm">
                            <div className="input-group-prepend">
                              <span className="input-group-text bg-white border-right-0">
                                <i className="fas fa-search text-muted" style={{ fontSize: 12 }}></i>
                              </span>
                            </div>
                            <input
                              type="text"
                              className="form-control border-left-0"
                              placeholder="Search customers..."
                              value={tabCustomerSearch}
                              onChange={(e) => setTabCustomerSearch(e.target.value)}
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="overflow-auto" style={{ maxHeight: 220 }}>
                          <div
                            className="px-3 py-2 cursor-pointer"
                            style={{ cursor: 'pointer', fontSize: '0.875rem' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            onClick={() => {
                              setTabSelectedCustomer(null);
                              setShowTabCustomerDropdown(false);
                            }}
                          >
                            <i className="fas fa-walking mr-2 text-muted"></i>
                            Walk-in Customer
                          </div>
                          {customers
                            .filter(c => !tabCustomerSearch || c.name?.toLowerCase().includes(tabCustomerSearch.toLowerCase()) || c.email?.toLowerCase().includes(tabCustomerSearch.toLowerCase()) || c.phone?.includes(tabCustomerSearch))
                            .map(cust => (
                              <div
                                key={cust.id}
                                className="px-3 py-2 cursor-pointer"
                                style={{ cursor: 'pointer', fontSize: '0.875rem', borderTop: '1px solid #f8f9fa' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                onClick={() => {
                                  setTabSelectedCustomer(cust);
                                  setShowTabCustomerDropdown(false);
                                }}
                              >
                                <div className="font-weight-bold">{cust.name}</div>
                                {(cust.email || cust.phone) && (
                                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {cust.email}{cust.email && cust.phone ? ' • ' : ''}{cust.phone}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="font-weight-bold">Payment Method</label>
                  <div className="d-flex" style={{ gap: 12 }}>
                    <button
                      className={`btn flex-fill ${tabPaymentMethod === 'cash' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        borderRadius: '12px',
                        backgroundColor: tabPaymentMethod === 'cash' ? '#28a745' : 'transparent',
                        borderColor: tabPaymentMethod === 'cash' ? '#28a745' : '#dee2e6',
                        color: tabPaymentMethod === 'cash' ? '#fff' : '#6c757d',
                      }}
                      onClick={() => setTabPaymentMethod('cash')}
                    >
                      <i className="fas fa-money-bill-wave mr-2"></i>Cash
                    </button>
                    <button
                      className={`btn flex-fill ${tabPaymentMethod === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        borderRadius: '12px',
                        backgroundColor: tabPaymentMethod === 'card' ? '#ff6b35' : 'transparent',
                        borderColor: tabPaymentMethod === 'card' ? '#ff6b35' : '#dee2e6',
                        color: tabPaymentMethod === 'card' ? '#fff' : '#6c757d',
                      }}
                      onClick={() => setTabPaymentMethod('card')}
                    >
                      <i className="fas fa-credit-card mr-2"></i>Card
                    </button>
                    <button
                      className={`btn flex-fill ${tabPaymentMethod === 'define_later' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{
                        borderRadius: '12px',
                        backgroundColor: tabPaymentMethod === 'define_later' ? '#6c757d' : 'transparent',
                        borderColor: tabPaymentMethod === 'define_later' ? '#6c757d' : '#dee2e6',
                        color: tabPaymentMethod === 'define_later' ? '#fff' : '#6c757d',
                      }}
                      onClick={() => setTabPaymentMethod('define_later')}
                    >
                      <i className="fas fa-question-circle mr-2"></i>Define Later
                    </button>
                  </div>
                </div>

                {tabPaymentMethod === 'card' && !pos?.stripeTerminalId && (
                  <div className="alert alert-warning" style={{ borderRadius: 12 }}>
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    No Stripe reader paired. Please pair a reader first.
                  </div>
                )}

                {tabPaymentMethod === 'card' && pos?.stripeTerminalId && (
                  <div className="alert alert-info" style={{ borderRadius: 12 }}>
                    <i className="fas fa-info-circle mr-2"></i>
                    The customer will be asked to present their card on the terminal for authorization only. Payment will be captured when you complete the tab.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={closeCreateTabModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-primary font-weight-bold"
                  style={{ backgroundColor: '#28a745', borderColor: '#28a745', borderRadius: '12px', minWidth: 150 }}
                  disabled={!tabNameInput.trim() || tabCreating || (tabPaymentMethod === 'card' && !pos?.stripeTerminalId)}
                  onClick={createOpenTabFromCart}
                >
                  {tabCreating ? (
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                  ) : (
                    <i className="fas fa-clipboard-list mr-2"></i>
                  )}
                  Open Tab
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Picker Modal (mini floorplan) */}
      {showTablePickerModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 700 }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Select Table</h5>
                <button type="button" className="close" onClick={() => setShowTablePickerModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Section selector */}
                <div className="d-flex align-items-center overflow-auto pb-2 mb-2">
                  <button
                    className={`btn btn-sm mr-2 ${pickerSectionId === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPickerSectionId(null)}
                  >
                    All
                  </button>
                  {sections.map(section => (
                    <button
                      key={section.id}
                      className={`btn btn-sm mr-2 ${pickerSectionId === section.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setPickerSectionId(section.id)}
                    >
                      {section.name}
                    </button>
                  ))}
                </div>

                {/* Mini Canvas */}
                <div
                  className="card"
                  style={{
                    height: '400px',
                    background: '#f8f9fa',
                    backgroundImage: 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {tables.filter(t => !pickerSectionId || t.sectionId === pickerSectionId).length === 0 ? (
                    <div className="d-flex flex-column justify-content-center align-items-center h-100">
                      <i className="fas fa-table fa-4x text-muted mb-3"></i>
                      <h5 className="text-muted">No tables in this section</h5>
                    </div>
                  ) : (
                    tables.filter(t => !pickerSectionId || t.sectionId === pickerSectionId).map(table => {
                      const size = table.shape === 'rectangle' ? { w: 80, h: 50 } : { w: 60, h: 60 };
                      const isOccupied = !!openTabs.find(t => t.tableId === table.id);
                      return (
                        <div
                          key={table.id}
                          style={{
                            position: 'absolute',
                            left: table.positionX || 50,
                            top: table.positionY || 50,
                            width: size.w,
                            height: size.h,
                            background: isOccupied ? '#dc3545' : (selectedTableId === table.id ? '#28a745' : '#ff6b35'),
                            borderRadius: table.shape === 'rounded' ? '50%' : table.shape === 'triangle' ? '0' : '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isOccupied ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            opacity: isOccupied ? 0.7 : 1,
                          }}
                          onClick={() => {
                            if (!isOccupied) {
                              setSelectedTableId(table.id);
                              setShowTablePickerModal(false);
                            }
                          }}
                          title={`${table.name} - ${table.type}${isOccupied ? ' (Occupied)' : ''}`}
                        >
                          <span>{table.name}</span>
                          <span style={{ fontSize: '8px', opacity: 0.9 }}>{table.maxPartySize}</span>
                        </div>
                      );
                    })
                  )}
                </div>
                <p className="text-muted mt-2 text-center">
                  <small><i className="fas fa-info-circle mr-1"></i>Click an available table to select it. Red tables are occupied.</small>
                </p>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={() => setShowTablePickerModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Detail / Complete Modal */}
      {showTabDetailModal && selectedOpenTab && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '80%', width: '80%', height: '90%', margin: 'auto' }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', height: '100%', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title font-weight-bold mb-1">
                    <i className="fas fa-clipboard-list mr-2 text-primary"></i>
                    {selectedOpenTab.tabName}
                    {(() => {
                      const meta = getFulfillmentStatusMeta(selectedOpenTab.fulfillmentStatus);
                      const isCustom = meta.variant === 'teal' || meta.variant === 'cyan';
                      return (
                        <span className={`badge badge-${isCustom ? '' : meta.variant} ml-2`}
                          style={{ fontSize: '0.7rem', verticalAlign: 'middle', ...(isCustom ? { backgroundColor: meta.bg, color: meta.color } : {}) }}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </h5>
                  <div className="text-muted" style={{ fontSize: '0.75rem', marginLeft: 28 }}>
                    {selectedOpenTab.confirmationNumber && (
                      <span className="text-monospace mr-2">{selectedOpenTab.confirmationNumber}</span>
                    )}
                    <span><i className="far fa-clock mr-1"></i>{formatElapsed(selectedOpenTab.createdAt)}</span>
                    {selectedOpenTab.tableName && (
                      <span className="ml-2"><i className="fas fa-chair mr-1"></i>{selectedOpenTab.tableName}</span>
                    )}
                    {selectedOpenTab.posName && (
                      <span className="ml-2"><i className="fas fa-desktop mr-1"></i>{selectedOpenTab.posName}</span>
                    )}
                  </div>
                </div>
                <button type="button" className="close" onClick={() => { setShowTabDetailModal(false); setSelectedOpenTab(null); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ overflowY: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div className="row" style={{ flex: 1, overflow: 'hidden' }}>
                  {/* Left: Items */}
                  <div className="col-md-7" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    <h6 className="font-weight-bold mb-3">
                      Items ({selectedOpenTab.items?.length || 0})
                      <span className="badge badge-light ml-2" style={{ fontSize: '0.75rem' }}>
                        {selectedOpenTab.paymentMethod === 'card' ? 'Card' : selectedOpenTab.paymentMethod === 'define_later' ? 'Define Later' : 'Cash'}
                      </span>
                      {selectedOpenTab.posName && (
                        <span className="badge badge-secondary ml-1" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-desktop mr-1"></i>{selectedOpenTab.posName}
                        </span>
                      )}
                      {selectedOpenTab.cardBrand && (
                        <span className="badge badge-info ml-1" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-credit-card mr-1"></i>{selectedOpenTab.cardBrand} •••• {selectedOpenTab.cardLast4}
                        </span>
                      )}
                      {selectedOpenTab.receiptPrinted && (
                        <span className="badge badge-dark ml-1" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-print mr-1"></i>Receipt Printed
                        </span>
                      )}
                    </h6>
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                      {selectedOpenTab.items?.map((item, idx) => (
                        <div key={idx} className="d-flex justify-content-between align-items-start mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: 8 }}>
                          <div className="flex-grow-1">
                            <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{item.productName}</div>
                            {item.options?.length > 0 && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                + {item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}
                              </div>
                            )}
                             <div className="mt-1">
                              {(() => {
                                const meta = getFulfillmentStatusMeta(item.fulfillmentStatus);
                                const isCustom = meta.variant === 'teal' || meta.variant === 'cyan';
                                return (
                                  <span
                                    className={`badge badge-${isCustom ? '' : meta.variant}`}
                                    style={{ fontSize: '0.65rem', cursor: 'pointer', ...(isCustom ? { backgroundColor: meta.bg, color: meta.color } : {}) }}
                                    onClick={() => updateTabItemFulfillment(selectedOpenTab, idx)}
                                    title="Click to change item status"
                                  >
                                    {meta.label} <i className="fas fa-pen" style={{ fontSize: '0.5rem', marginLeft: 2 }}></i>
                                  </span>
                                );
                              })()}
                              {item.workzoneId && (
                                <span className="badge badge-light ml-1" style={{ fontSize: '0.6rem', color: '#666' }}>
                                  <i className="fas fa-map-marker-alt mr-1" style={{ fontSize: '0.5rem' }}></i>
                                  {(() => {
                                    const wz = workzones.find(w => w.id === item.workzoneId);
                                    return wz ? wz.name : item.workzoneId.slice(0, 8);
                                  })()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right" style={{ minWidth: 80 }}>
                            <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{formatCurrency(item.total)}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>× {item.qty}</div>
                          </div>
                          <button
                            className="btn btn-sm btn-link text-danger ml-2"
                            style={{ padding: '0 4px', fontSize: '0.75rem' }}
                            onClick={() => removeItemsFromTab([idx])}
                            title="Remove item"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary btn-block mt-2"
                      style={{ borderRadius: 8 }}
                      onClick={() => { setShowAddToTabModal(true); setTabCart([]); setTabAddProductSearch(''); }}
                    >
                      <i className="fas fa-plus mr-1"></i>Add Items
                    </button>
                  </div>

                  {/* Right: Totals & Actions */}
                  <div className="col-md-5" style={{ borderLeft: '1px solid #e9ecef', overflowY: 'auto', height: '100%' }}>
                    <h6 className="font-weight-bold mb-3">Totals</h6>

                    {splitChecks.length === 0 && (
                      <>
                        {/* Tip */}
                        <div className="mb-2">
                          <div className="d-flex align-items-center mb-1">
                            <span className="text-muted flex-grow-1" style={{ fontSize: '0.85rem' }}>Tip</span>
                            <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                              {formatCurrency(tabTipIsPercent
                                ? (selectedOpenTab.subtotal || 0) * ((parseFloat(tabTipInput) || 0) / 100)
                                : (parseFloat(tabTipInput) || 0))}
                            </span>
                          </div>
                          {/* Preset percentages from location config */}
                          <div className="d-flex flex-wrap mb-1" style={{ gap: 6 }}>
                            {(location?.terminalTipping?.percentages || [15, 18, 20]).map(pct => (
                              <button key={pct} type="button"
                                className={`btn btn-sm ${tabTipIsPercent && parseFloat(tabTipInput) === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ borderRadius: 16, flex: 1, fontSize: '0.7rem', backgroundColor: tabTipIsPercent && parseFloat(tabTipInput) === pct ? '#ff6b35' : undefined, borderColor: tabTipIsPercent && parseFloat(tabTipInput) === pct ? '#ff6b35' : undefined }}
                                onClick={() => { setTabTipIsPercent(true); setTabTipInput(pct.toString()); }}
                              >
                                {pct}%
                              </button>
                            ))}
                          </div>
                          <div className="d-flex align-items-center">
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm"
                              style={{ width: 80 }}
                              value={tabTipInput}
                              placeholder={tabTipIsPercent ? '%' : '$'}
                              onChange={e => setTabTipInput(e.target.value)}
                            />
                            <span className="mx-1" style={{ color: tabTipIsPercent ? '#ff6b35' : '#666', fontSize: '0.75rem' }}>%</span>
                            <div className="custom-control custom-switch mx-1">
                              <input type="checkbox" className="custom-control-input" id="tabTipToggle"
                                checked={!tabTipIsPercent}
                                onChange={e => { setTabTipIsPercent(!e.target.checked); setTabTipInput(''); }} />
                              <label className="custom-control-label" htmlFor="tabTipToggle"></label>
                            </div>
                            <span style={{ color: !tabTipIsPercent ? '#ff6b35' : '#666', fontSize: '0.75rem' }}>$</span>
                          </div>
                        </div>

                        {/* Discount */}
                        <div className="mb-2">
                          <div className="d-flex align-items-center mb-1">
                            <span className="text-muted flex-grow-1" style={{ fontSize: '0.85rem' }}>Discount</span>
                            <span className="font-weight-bold text-danger" style={{ fontSize: '0.9rem' }}>
                              -{formatCurrency(parseFloat(tabDiscountInput) || 0)}
                            </span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            style={{ width: 120 }}
                            value={tabDiscountInput}
                            placeholder="0.00"
                            onChange={e => setTabDiscountInput(e.target.value)}
                          />
                        </div>

                        <hr />
                      </>
                    )}

                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>Subtotal</span>
                      <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(selectedOpenTab.subtotal || 0)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>Tax ({taxRate}%)</span>
                      <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                        {formatCurrency((selectedOpenTab.subtotal || 0) * (taxRate / 100))}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>Tip</span>
                      <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                        {formatCurrency(splitChecks.length > 0
                          ? splitChecks.reduce((sum, s) => sum + (s.tip || 0), 0)
                          : (tabTipIsPercent
                            ? (selectedOpenTab.subtotal || 0) * ((parseFloat(tabTipInput) || 0) / 100)
                            : (parseFloat(tabTipInput) || 0)))}
                      </span>
                    </div>
                    {parseFloat(tabDiscountInput) > 0 && (
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Discount</span>
                        <span className="font-weight-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatCurrency(parseFloat(tabDiscountInput) || 0)}</span>
                      </div>
                    )}
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span className="font-weight-bold">Total</span>
                      <span className="font-weight-bold" style={{ fontSize: '1.25rem', color: '#ff6b35' }}>
                        {formatCurrency(
                          (selectedOpenTab.subtotal || 0) +
                          ((selectedOpenTab.subtotal || 0) * (taxRate / 100)) +
                          (splitChecks.length > 0
                            ? splitChecks.reduce((sum, s) => sum + (s.tip || 0), 0)
                            : (tabTipIsPercent
                              ? (selectedOpenTab.subtotal || 0) * ((parseFloat(tabTipInput) || 0) / 100)
                              : (parseFloat(tabTipInput) || 0))) -
                          (parseFloat(tabDiscountInput) || 0)
                        )}
                      </span>
                    </div>

                    {splitChecks.length > 0 && (
                      <div className="mb-3">
                        <h6 className="font-weight-bold mb-2">Split Checks</h6>
                        {splitChecks.map((split, idx) => {
                          const splitTotal = (split.total || 0) + (split.tip || 0);
                          const isPaid = split.isPaid === true;
                          const hasCard = (split.status === 'card_presented' || split.status === 'requires_capture') && !isPaid;
                          const isPresenting = split.status === 'card_presenting' && !isPaid;
                          const isStuck = !!split.stripePaymentIntentId && !isPaid && !hasCard && !isPresenting;
                          const splitPaymentMethod = split.paymentMethod || 'card';
                          return (
                            <div key={split.id || idx} className="mb-2 p-2" style={{ background: isPaid ? '#d4edda' : hasCard ? '#cce5ff' : '#f8f9fa', borderRadius: 8 }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{split.label || `Check ${idx + 1}`}</span>
                                <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(splitTotal)}</span>
                              </div>
                              {(split.tip || 0) > 0 && (
                                <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem' }}>
                                  <span className="text-muted">Tip</span>
                                  <span className="font-weight-bold">{formatCurrency(split.tip)}</span>
                                </div>
                              )}
                              <div className="d-flex justify-content-between align-items-center mt-1">
                                <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                  {isPaid ? <span className="text-success"><i className="fas fa-check mr-1"></i>Paid ({splitPaymentMethod})</span> : hasCard ? <span className="text-primary"><i className="fas fa-credit-card mr-1"></i>Card Presented</span> : isPresenting ? <span className="text-warning"><i className="fas fa-spinner fa-spin mr-1"></i>Presenting...</span> : <span className="text-muted">Pending ({splitPaymentMethod})</span>}
                                </span>
                                {!isPaid && (
                                  <div className="d-flex" style={{ gap: 6 }}>
                                    {/* Payment method toggle */}
                                    <div className="btn-group btn-group-sm">
                                      <button className={`btn btn-sm ${splitPaymentMethod === 'cash' ? 'btn-success' : 'btn-outline-secondary'}`} style={{ fontSize: '0.65rem', padding: '2px 6px' }} onClick={async () => {
                                        try {
                                          const db = getFirestore();
                                          await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', split.id), { paymentMethod: 'cash', updatedAt: new Date() });
                                          await loadSplitChecks(selectedOpenTab.id);
                                        } catch (e) { console.error(e); }
                                      }} disabled={tabCompleting || isPresenting}>Cash</button>
                                      <button className={`btn btn-sm ${splitPaymentMethod === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ fontSize: '0.65rem', padding: '2px 6px' }} onClick={async () => {
                                        try {
                                          const db = getFirestore();
                                          await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', split.id), { paymentMethod: 'card', updatedAt: new Date() });
                                          await loadSplitChecks(selectedOpenTab.id);
                                        } catch (e) { console.error(e); }
                                      }} disabled={tabCompleting || isPresenting}>Card</button>
                                    </div>
                                    {hasCard ? (
                                      <button className="btn btn-sm btn-primary" style={{ fontSize: '0.7rem', borderRadius: 6 }} onClick={() => captureSplitPaymentCheck(split.id, splitTotal)} disabled={tabCompleting}>Capture</button>
                                    ) : isStuck ? (
                                      <button className="btn btn-sm btn-outline-warning" style={{ fontSize: '0.7rem', borderRadius: 6 }} onClick={async () => {
                                        try {
                                          const db = getFirestore();
                                          await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', split.id), {
                                            stripePaymentIntentId: deleteField(),
                                            status: 'pending',
                                            updatedAt: new Date(),
                                          });
                                          await loadSplitChecks(selectedOpenTab.id);
                                          openSplitTipModal(split);
                                        } catch (e) { console.error(e); Swal.fire('Error', e.message, 'error'); }
                                      }} disabled={tabCompleting || pendingSplitIdForCard === split.id}>Re-present Card</button>
                                    ) : splitPaymentMethod === 'card' ? (
                                      <button className="btn btn-sm btn-outline-success" style={{ fontSize: '0.7rem', borderRadius: 6 }} onClick={() => openSplitTipModal(split)} disabled={tabCompleting || pendingSplitIdForCard === split.id}>Pay Card</button>
                                    ) : (
                                      <button className="btn btn-sm btn-outline-success" style={{ fontSize: '0.7rem', borderRadius: 6 }} onClick={async () => {
                                        try {
                                          const db = getFirestore();
                                          await updateDoc(doc(db, 'users', user.uid, 'openTabs', selectedOpenTab.id, 'splitChecks', split.id), { isPaid: true, paidAt: new Date(), updatedAt: new Date() });
                                          await loadSplitChecks(selectedOpenTab.id);
                                          await syncTabTipFromSplits(selectedOpenTab.id);
                                        } catch (e) { console.error(e); Swal.fire('Error', e.message, 'error'); }
                                      }} disabled={tabCompleting}>Pay Cash</button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-4">
                      {selectedOpenTab.fulfillmentStatus !== 'complete' && (
                        <button className="btn btn-block btn-primary btn-lg font-weight-bold mb-2" style={{ backgroundColor: '#007bff', borderColor: '#007bff', color: '#fff', borderRadius: '12px' }} onClick={() => _showFulfillmentStatusPicker(selectedOpenTab)}>
                          <i className="fas fa-utensils mr-2"></i>Fulfillment Actions
                        </button>
                      )}
                      {splitChecks.length === 0 ? (
                        <button className="btn btn-block btn-lg font-weight-bold" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', color: '#fff', borderRadius: '12px' }} onClick={() => { setCustomerTipPercent(0); setCustomerTipAmount(0); setCustomerTipMode('none'); setTabTipInput(''); setTabTipIsPercent(true); setTabDiscountInput(''); setShowCustomerTipModal(true); }}>
                          <i className="fas fa-check mr-2"></i>Complete Tab
                        </button>
                      ) : (
                        <button className="btn btn-block btn-lg font-weight-bold" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', color: '#fff', borderRadius: '12px' }} onClick={() => { if (splitChecks.every(s => s.isPaid === true)) { completeOpenTab(); } else { Swal.fire('Not Ready', 'All split checks must be paid before completing the tab.', 'warning'); } }} disabled={!splitChecks.every(s => s.isPaid === true)}>
                          <i className="fas fa-check mr-2"></i>Complete Tab
                        </button>
                      )}
                      {splitChecks.length === 0 && (
                        <button className="btn btn-block btn-outline-warning mt-2" style={{ borderRadius: '12px' }} onClick={() => setShowSplitBillModal(true)}>
                          <i className="fas fa-code-branch mr-2"></i>Split Bill
                        </button>
                      )}
                      <button className="btn btn-block btn-outline-secondary mt-2" style={{ borderRadius: '12px' }} onClick={() => printTabReceipt(selectedOpenTab)}>
                        <i className="fas fa-print mr-2"></i>{selectedOpenTab.receiptPrinted ? 'Reprint Receipt' : 'Print Receipt'}
                      </button>
                      {splitChecks.length > 0 && (
                        <button className="btn btn-block btn-outline-danger mt-2" style={{ borderRadius: '12px' }} onClick={clearSplitChecks} disabled={splitChecks.some(s => s.isPaid === true || s.stripePaymentIntentId)}>
                          <i className="fas fa-trash-alt mr-2"></i>Clear Splits
                        </button>
                      )}
                      <button className="btn btn-block btn-outline-danger mt-2" style={{ borderRadius: '12px' }} onClick={() => cancelOpenTab(selectedOpenTab)}>
                        <i className="fas fa-times mr-2"></i>Cancel Tab
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Items to Tab Modal */}
      {showAddToTabModal && selectedOpenTab && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '80%', width: '80%', height: '90%', margin: 'auto' }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', height: '100%', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Add Items to "{selectedOpenTab.tabName}"</h5>
                <button type="button" className="close" onClick={() => { setShowAddToTabModal(false); setTabCart([]); setTabAddProductSearch(''); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ overflowY: 'auto' }}>
                <div className="mb-3 d-flex align-items-center">
                  <input
                    type="text"
                    className="form-control mr-2"
                    placeholder="Search products..."
                    value={tabAddProductSearch}
                    onChange={(e) => setTabAddProductSearch(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="btn btn-outline-primary"
                    style={{ borderRadius: 10, whiteSpace: 'nowrap' }}
                    onClick={() => {
                      setCustomItemDescription('');
                      setCustomItemPrice('');
                      setCustomItemQty(1);
                      setCustomItemInstructions('');
                      setCustomItemWorkzoneId('');
                      setShowCustomItemModal(true);
                    }}
                  >
                    <i className="fas fa-plus-square mr-1"></i>Custom
                  </button>
                </div>
                <div>
                  {/* Deals Section */}
                  {(() => {
                    const dealsProducts = products.filter(p => {
                      const matchesSearch = !tabAddProductSearch || p.name?.toLowerCase().includes(tabAddProductSearch.toLowerCase());
                      return p.onSale === true && matchesSearch;
                    });
                    if (dealsProducts.length === 0) return null;
                    return (
                      <div className="mb-3">
                        <h6 className="font-weight-bold text-danger" style={{ fontSize: '0.8rem' }}><i className="fas fa-tags mr-1"></i>Deals</h6>
                        <div className="row">
                          {dealsProducts.map(product => {
                            const inTabCart = tabCart.find(item => item.productId === product.id);
                            return (
                              <div key={product.id} className="col-6 col-md-4 col-lg-3 mb-2">
                                <button
                                  className={`btn btn-block btn-sm ${inTabCart ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  style={{
                                    borderRadius: 10,
                                    backgroundColor: inTabCart ? '#ff6b35' : undefined,
                                    borderColor: inTabCart ? '#ff6b35' : undefined,
                                    color: inTabCart ? '#fff' : undefined,
                                    fontSize: '0.8rem',
                                  }}
                                  onClick={() => {
                                    if (inTabCart) {
                                      setTabCart(prev => prev.filter(item => item.productId !== product.id));
                                    } else {
                                      startTabAddProduct(product);
                                    }
                                  }}
                                >
                                  {inTabCart && <i className="fas fa-check mr-1"></i>}
                                  {product.name}
                                  {product.optionIds?.length > 0 && <span className="ml-1"><i className="fas fa-sliders-h" style={{ fontSize: '0.6rem' }}></i></span>}
                                  <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>
                                    {product.onSale && product.salePrice !== undefined ? (
                                      <>
                                        <span style={{ textDecoration: 'line-through', marginRight: 3 }}>{formatCurrency(product.price)}</span>
                                        {formatCurrency(product.salePrice)}
                                      </>
                                    ) : (
                                      formatCurrency(product.price)
                                    )}
                                  </div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {categories.map(cat => {
                    const catProducts = products.filter(p => {
                      const matchesCategory = p.category === (cat.name || cat.id);
                      const matchesSearch = !tabAddProductSearch || p.name?.toLowerCase().includes(tabAddProductSearch.toLowerCase());
                      return matchesCategory && matchesSearch;
                    });
                    if (catProducts.length === 0) return null;
                    return (
                      <div key={cat.id} className="mb-3">
                        <h6 className="font-weight-bold text-muted" style={{ fontSize: '0.8rem' }}>{cat.name || cat.id}</h6>
                        <div className="row">
                          {catProducts.map(product => {
                            const inTabCart = tabCart.find(item => item.productId === product.id);
                            return (
                              <div key={product.id} className="col-6 col-md-4 col-lg-3 mb-2">
                                <button
                                  className={`btn btn-block btn-sm ${inTabCart ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  style={{
                                    borderRadius: 10,
                                    backgroundColor: inTabCart ? '#ff6b35' : undefined,
                                    borderColor: inTabCart ? '#ff6b35' : undefined,
                                    color: inTabCart ? '#fff' : undefined,
                                    fontSize: '0.8rem',
                                  }}
                                  onClick={() => {
                                    if (inTabCart) {
                                      setTabCart(prev => prev.filter(item => item.productId !== product.id));
                                    } else {
                                      startTabAddProduct(product);
                                    }
                                  }}
                                >
                                  {inTabCart && <i className="fas fa-check mr-1"></i>}
                                  {product.name}
                                  {product.optionIds?.length > 0 && <span className="ml-1"><i className="fas fa-sliders-h" style={{ fontSize: '0.6rem' }}></i></span>}
                                  <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>
                                    {product.onSale && product.salePrice !== undefined ? (
                                      <>
                                        <span style={{ textDecoration: 'line-through', marginRight: 3 }}>{formatCurrency(product.price)}</span>
                                        {formatCurrency(product.salePrice)}
                                      </>
                                    ) : (
                                      formatCurrency(product.price)
                                    )}
                                  </div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {tabCart.length > 0 && (
                  <div className="mt-3 p-2" style={{ background: '#f8f9fa', borderRadius: 12 }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>{tabCart.length} item(s)</span>
                      <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(tabCartSubtotal)}</span>
                    </div>
                    {tabCart.map((item, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center mb-2 p-2" style={{ background: '#fff', borderRadius: 8 }}>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="font-weight-bold text-truncate" style={{ fontSize: '0.8rem' }}>{item.productName}</div>
                          {item.options?.length > 0 && (
                            <div className="text-muted text-truncate" style={{ fontSize: '0.7rem' }}>
                              + {item.options.map(o => typeof o === 'string' ? o : o.itemName || o.name).join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 6 }}>
                          <button className="btn btn-sm btn-light" style={{ width: 28, height: 28, padding: 0, fontSize: '0.7rem' }} onClick={() => setTabCart(prev => prev.map((it, i) => i === idx ? { ...it, qty: Math.max(1, it.qty - 1), total: it.price * Math.max(1, it.qty - 1) } : it))}>-</button>
                          <span className="font-weight-bold" style={{ fontSize: '0.8rem', minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                          <button className="btn btn-sm btn-light" style={{ width: 28, height: 28, padding: 0, fontSize: '0.7rem' }} onClick={() => setTabCart(prev => prev.map((it, i) => i === idx ? { ...it, qty: it.qty + 1, total: it.price * (it.qty + 1) } : it))}>+</button>
                          <button className="btn btn-sm btn-link text-danger p-0 ml-1" style={{ fontSize: '0.7rem' }} onClick={() => setTabCart(prev => prev.filter((_, i) => i !== idx))}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Options picker for tab add */}
                {showTabAddOptions && tabAddProduct && (
                  <div className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="font-weight-bold m-0">Options — {tabAddProduct.name}</h6>
                      <button className="btn btn-sm btn-link text-muted p-0" onClick={() => { setShowTabAddOptions(false); setTabAddProduct(null); }}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    {productOptions
                      .filter(opt => tabAddProduct.optionIds?.includes(opt.id))
                      .map(option => (
                        <div key={option.id} className="mb-2">
                          <div className="font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            {option.name}
                            {option.optionRequired && <span className="text-danger"> *</span>}
                          </div>
                          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                            {option.options?.map(optItem => {
                              const isSelected = tabAddSelectedOptions[option.id]?.has(optItem.name);
                              return (
                                <button key={optItem.name} type="button"
                                  className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  style={{
                                    borderRadius: 16,
                                    backgroundColor: isSelected ? '#ff6b35' : undefined,
                                    borderColor: isSelected ? '#ff6b35' : undefined,
                                    color: isSelected ? '#fff' : undefined,
                                    fontSize: '0.75rem',
                                  }}
                                  onClick={() => {
                                    const newSelected = { ...tabAddSelectedOptions };
                                    const newFees = { ...tabAddExtraFees };
                                    if (option.allowMultipleSelection) {
                                      newSelected[option.id] = new Set(newSelected[option.id] || []);
                                      if (newSelected[option.id].has(optItem.name)) {
                                        newSelected[option.id].delete(optItem.name);
                                        delete newFees[optItem.name];
                                      } else {
                                        newSelected[option.id].add(optItem.name);
                                        newFees[optItem.name] = optItem.extraFee || 0;
                                      }
                                    } else {
                                      newSelected[option.id] = new Set([optItem.name]);
                                      option.options?.forEach(oi => delete newFees[oi.name]);
                                      newFees[optItem.name] = optItem.extraFee || 0;
                                    }
                                    setTabAddSelectedOptions(newSelected);
                                    setTabAddExtraFees(newFees);
                                  }}
                                >
                                  {optItem.name}{optItem.extraFee > 0 ? ` +${formatCurrency(optItem.extraFee)}` : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    <div className="text-right mt-2">
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 10 }}
                        onClick={() => {
                          // Validate required options
                          const applicableOptions = productOptions.filter(opt => tabAddProduct.optionIds?.includes(opt.id));
                          for (const option of applicableOptions) {
                            if (option.optionRequired && (!tabAddSelectedOptions[option.id] || tabAddSelectedOptions[option.id].size === 0)) {
                              Swal.fire('Error', `Please select required option: ${option.name}`, 'error');
                              return;
                            }
                          }
                          setShowTabAddOptions(false);
                          setShowTabAddQty(true);
                        }}
                      >
                        Continue <i className="fas fa-arrow-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Qty + Instructions for tab add */}
                {showTabAddQty && tabAddProduct && (
                  <div className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: 12 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="font-weight-bold m-0">{tabAddProduct.name}</h6>
                      <button className="btn btn-sm btn-link text-muted p-0" onClick={() => { setShowTabAddQty(false); setTabAddProduct(null); }}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    <div className="form-group mb-2">
                      <label style={{ fontSize: '0.8rem' }}>Quantity</label>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <button className="btn btn-sm btn-light" onClick={() => setTabAddQty(Math.max(1, tabAddQty - 1))}>-</button>
                        <span className="font-weight-bold" style={{ minWidth: 24, textAlign: 'center' }}>{tabAddQty}</span>
                        <button className="btn btn-sm btn-light" onClick={() => setTabAddQty(tabAddQty + 1)}>+</button>
                      </div>
                    </div>
                    {tabAddProduct.acceptSpecialRequirement && (
                      <div className="form-group mb-2">
                        <label style={{ fontSize: '0.8rem' }}>Special Instructions</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          placeholder="Any special requests..."
                          value={tabAddInstructions}
                          onChange={(e) => setTabAddInstructions(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="text-right">
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 10 }}
                        onClick={finalizeTabAddProduct}
                      >
                        <i className="fas fa-plus mr-1"></i>Add to Tab Cart
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={() => { setShowAddToTabModal(false); setTabCart([]); }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-primary font-weight-bold"
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '12px' }}
                  disabled={tabCart.length === 0 || saving}
                  onClick={addItemsToOpenTab}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                  ) : (
                    <i className="fas fa-plus mr-2"></i>
                  )}
                  Add to Tab
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer-Facing Tip Selection Modal (for completing tabs) */}
      {showCustomerTipModal && selectedOpenTab && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 3000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
            <div className="modal-content" style={{ borderRadius: '24px', border: 'none', maxHeight: '85vh', overflowY: 'auto' }}>
              <div className="modal-body p-4">
                {/* Header */}
                <div className="text-center mb-4">
                  <h4 className="font-weight-bold mb-1">{selectedOpenTab.tabName}</h4>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    {selectedOpenTab.items?.length || 0} item(s) • {selectedOpenTab.paymentMethod === 'card' ? 'Card' : selectedOpenTab.paymentMethod === 'define_later' ? 'Define Later' : 'Cash'}
                  </p>
                </div>

                {/* Items Summary */}
                <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 16 }}>
                  {selectedOpenTab.items?.map((item, idx) => (
                    <div key={idx} className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center">
                        <span className="font-weight-bold mr-2" style={{ fontSize: '0.9rem' }}>{item.qty}×</span>
                        <span style={{ fontSize: '0.9rem' }}>{item.productName}</span>
                      </div>
                      <span className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>

                <div className="divider" style={{ borderTop: '1px dashed #dee2e6', margin: '16px 0' }}></div>

                {/* Subtotal & Tax */}
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted" style={{ fontSize: '0.95rem' }}>Subtotal</span>
                  <span className="font-weight-bold" style={{ fontSize: '0.95rem' }}>{formatCurrency(selectedOpenTab.subtotal || 0)}</span>
                </div>
                {taxRate > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>Tax ({taxRate}%)</span>
                    <span className="font-weight-bold" style={{ fontSize: '0.95rem' }}>
                      {formatCurrency((selectedOpenTab.subtotal || 0) * (taxRate / 100))}
                    </span>
                  </div>
                )}

                <div className="divider" style={{ borderTop: '1px dashed #dee2e6', margin: '16px 0' }}></div>

                {/* Tip Selection */}
                <div className="text-center mb-3">
                  <h5 className="font-weight-bold mb-3">Add a Tip?</h5>
                  <div className="row mb-3" style={{ gap: 8 }}>
                    <div className="col">
                      <button
                        className={`btn btn-block ${customerTipMode === 'none' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        style={{
                          borderRadius: 14,
                          backgroundColor: customerTipMode === 'none' ? '#6c757d' : undefined,
                          borderColor: customerTipMode === 'none' ? '#6c757d' : undefined,
                          color: customerTipMode === 'none' ? '#fff' : undefined,
                        }}
                        onClick={() => { setCustomerTipMode('none'); setCustomerTipPercent(0); setCustomerTipAmount(0); setTabTipInput(''); }}
                      >
                        <div className="font-weight-bold">No Tip</div>
                      </button>
                    </div>
                    {(location?.terminalTipping?.percentages || [15, 18, 20]).map(pct => (
                      <div className="col" key={pct}>
                        <button
                          className={`btn btn-block ${customerTipMode === 'percent' && customerTipPercent === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{
                            borderRadius: 14,
                            backgroundColor: customerTipMode === 'percent' && customerTipPercent === pct ? '#ff6b35' : undefined,
                            borderColor: customerTipMode === 'percent' && customerTipPercent === pct ? '#ff6b35' : undefined,
                            color: customerTipMode === 'percent' && customerTipPercent === pct ? '#fff' : undefined,
                          }}
                          onClick={() => {
                            setCustomerTipMode('percent');
                            setCustomerTipPercent(pct);
                            setCustomerTipAmount((selectedOpenTab.subtotal || 0) * (pct / 100));
                            setTabTipInput(pct.toString());
                            setTabTipIsPercent(true);
                          }}
                        >
                          <div className="font-weight-bold">{pct}%</div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                            {formatCurrency((selectedOpenTab.subtotal || 0) * (pct / 100))}
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Custom Tip */}
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <div className="input-group" style={{ maxWidth: 200 }}>
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        className="form-control text-center"
                        placeholder="Custom tip"
                        value={customerTipMode === 'amount' ? (customerTipAmount > 0 ? customerTipAmount.toFixed(2) : '') : ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '' || raw === '.') {
                            setCustomerTipMode('amount');
                            setCustomerTipAmount(0);
                            setCustomerTipPercent(0);
                            setTabTipInput(raw);
                            setTabTipIsPercent(false);
                            return;
                          }
                          const val = parseFloat(raw) || 0;
                          setCustomerTipMode('amount');
                          setCustomerTipAmount(val);
                          setCustomerTipPercent(0);
                          setTabTipInput(raw);
                          setTabTipIsPercent(false);
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="divider" style={{ borderTop: '1px dashed #dee2e6', margin: '16px 0' }}></div>

                {/* Total */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <span className="font-weight-bold" style={{ fontSize: '1.1rem' }}>Total</span>
                  <span className="font-weight-bold" style={{ fontSize: '1.5rem', color: '#ff6b35' }}>
                    {formatCurrency(
                      (selectedOpenTab.subtotal || 0) +
                      ((selectedOpenTab.subtotal || 0) * (taxRate / 100)) +
                      customerTipAmount -
                      (parseFloat(tabDiscountInput) || 0)
                    )}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="d-flex" style={{ gap: 12 }}>
                  <button
                    className="btn btn-lg btn-light flex-fill font-weight-bold"
                    style={{ borderRadius: '14px' }}
                    onClick={() => { setShowCustomerTipModal(false); }}
                  >
                    Back
                  </button>
                  <button
                    className="btn btn-lg btn-primary flex-fill font-weight-bold"
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '14px' }}
                    disabled={tabCompleting}
                    onClick={completeOpenTab}
                  >
                    {tabCompleting ? (
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                    ) : (
                      <i className="fas fa-check mr-2"></i>
                    )}
                    Confirm & Pay
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Split Bill Modal */}
      {showSplitBillModal && selectedOpenTab && (
        <SplitBillModal
          tab={selectedOpenTab}
          onClose={() => setShowSplitBillModal(false)}
          onCreateSplits={createSplitChecksFromTab}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Split Tip Modal */}
      {showSplitTipModal && selectedSplitForTip && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title font-weight-bold">Tip for {selectedSplitForTip.label || 'Check'}</h5>
                <button type="button" className="close" onClick={() => { setShowSplitTipModal(false); setSelectedSplitForTip(null); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-3">
                  <div className="text-muted small">Check Total</div>
                  <div className="font-weight-bold" style={{ fontSize: '1.5rem', color: '#ff6b35' }}>
                    {formatCurrency((selectedSplitForTip.total || 0) + (selectedSplitForTip.tip || 0))}
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
                  {(location?.terminalTipping?.percentages || [15, 18, 20]).map(pct => (
                    <button key={pct} type="button"
                      className={`btn btn-sm flex-fill ${splitTipIsPercent && parseFloat(splitTipInput) === pct ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ borderRadius: 16, fontSize: '0.8rem', backgroundColor: splitTipIsPercent && parseFloat(splitTipInput) === pct ? '#ff6b35' : undefined, borderColor: splitTipIsPercent && parseFloat(splitTipInput) === pct ? '#ff6b35' : undefined }}
                      onClick={() => { setSplitTipIsPercent(true); setSplitTipInput(pct.toString()); }}>
                      {pct}%
                    </button>
                  ))}
                </div>
                <div className="d-flex align-items-center mb-3">
                  <input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 100 }}
                    value={splitTipInput} placeholder={splitTipIsPercent ? '%' : '$'}
                    onChange={e => setSplitTipInput(e.target.value)} />
                  <span className="mx-2" style={{ color: splitTipIsPercent ? '#ff6b35' : '#666' }}>%</span>
                  <div className="custom-control custom-switch mx-2">
                    <input type="checkbox" className="custom-control-input" id="splitTipToggle"
                      checked={!splitTipIsPercent}
                      onChange={e => { setSplitTipIsPercent(!e.target.checked); setSplitTipInput(''); }} />
                    <label className="custom-control-label" htmlFor="splitTipToggle"></label>
                  </div>
                  <span style={{ color: !splitTipIsPercent ? '#ff6b35' : '#666' }}>$</span>
                </div>
                {splitTipInput && (
                  <div className="text-center">
                    <div className="text-muted small">Tip Amount</div>
                    <div className="font-weight-bold" style={{ color: '#ff6b35' }}>
                      {formatCurrency(splitTipIsPercent
                        ? (selectedSplitForTip.subtotal || 0) * ((parseFloat(splitTipInput) || 0) / 100)
                        : (parseFloat(splitTipInput) || 0))}
                    </div>
                    <div className="text-muted small">New Total: {formatCurrency((selectedSplitForTip.total || 0) + (splitTipIsPercent ? (selectedSplitForTip.subtotal || 0) * ((parseFloat(splitTipInput) || 0) / 100) : (parseFloat(splitTipInput) || 0)))}</div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-lg btn-light" style={{ borderRadius: 12 }} onClick={() => { setShowSplitTipModal(false); setSelectedSplitForTip(null); }}>Cancel</button>
                <button className="btn btn-lg btn-primary font-weight-bold" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12 }} onClick={applySplitTipAndPresentCard}>
                  <i className="fas fa-credit-card mr-2"></i>Present Card
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal */}
      {showCustomItemModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Custom Item</h5>
                <button type="button" className="close" onClick={() => setShowCustomItemModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Description */}
                <div className="form-group">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Description</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Special drink - off menu"
                    value={customItemDescription}
                    onChange={(e) => setCustomItemDescription(e.target.value)}
                    autoFocus
                  />
                </div>
                {/* Price */}
                <div className="form-group">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Price</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      value={customItemPrice}
                      onChange={(e) => setCustomItemPrice(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                {/* Quantity */}
                <div className="form-group">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Quantity</label>
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-light"
                      style={{ width: 36, height: 36 }}
                      onClick={() => setCustomItemQty(prev => Math.max(1, prev - 1))}
                      disabled={customItemQty <= 1}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="mx-3 font-weight-bold" style={{ fontSize: '1.1rem', minWidth: 24, textAlign: 'center' }}>
                      {customItemQty}
                    </span>
                    <button
                      className="btn btn-sm btn-light"
                      style={{ width: 36, height: 36 }}
                      onClick={() => setCustomItemQty(prev => prev + 1)}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                {/* Workzone / Sent to */}
                {workzones.length > 0 && (
                  <div className="form-group">
                    <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Sent to</label>
                    <select
                      className="form-control"
                      value={customItemWorkzoneId}
                      onChange={(e) => setCustomItemWorkzoneId(e.target.value)}
                    >
                      <option value="">None</option>
                      {workzones.map(wz => (
                        <option key={wz.id} value={wz.id}>{wz.name || wz.id}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Special Instructions */}
                <div className="form-group">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Special Instructions (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="e.g. Make it spicy, no onions"
                    value={customItemInstructions}
                    onChange={(e) => setCustomItemInstructions(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-lg btn-light"
                  style={{ borderRadius: '12px' }}
                  onClick={() => setShowCustomItemModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-primary font-weight-bold"
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '12px' }}
                  disabled={!customItemDescription.trim() || parseFloat(customItemPrice) <= 0}
                  onClick={() => addCustomItemToCart(showAddToTabModal ? 'tab' : 'main')}
                >
                  <i className="fas fa-plus mr-2"></i>Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==========================================
   STRIPE MANUAL CARD FORM (Stripe Elements)
   ========================================== */
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

/* ==========================================
   STRIPE MANUAL CARD FORM (Stripe Connect aware)
   ========================================== */
const ManualCardForm = ({ amount, pos, sessionId, user, feeEnabled, onSuccess, onError }) => {
  const [stripeInstance, setStripeInstance] = useState(null);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const acctId = userDoc.data()?.stripeAccountId || null;
        const platformKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
        const instance = acctId
          ? await loadStripe(platformKey, { stripeAccount: acctId })
          : await loadStripe(platformKey);

        if (cancelled) return;
        setStripeInstance(instance);
        setStripeAccountId(acctId);

        // Original flow, unchanged: create the PaymentIntent up front (no
        // paymentMethodId yet) when this location doesn't have the
        // Convenience Fee enabled. When it IS enabled, we deliberately wait
        // — the card must be tokenized first so we can check card.funding
        // (credit vs debit) before deciding the final amount, since card
        // network rules only allow surcharging credit cards.
        if (!feeEnabled) {
          const functions = getFunctions();
          const createCardPaymentIntentFn = httpsCallable(functions, 'createCardPaymentIntent');
          const timestamp = new Date();
          const confirmationNumber = `POS-${timestamp.getTime().toString().slice(-6)}`;
          const piResult = await createCardPaymentIntentFn({
            amount,
            currency: 'usd',
            description: `POS Sale #${confirmationNumber}`,
            locationId: pos?.locationId,
            metadata: {
              posId: pos?.id,
              posSessionId: sessionId,
              userId: user?.uid,
              locationId: pos?.locationId,
            },
          });
          if (cancelled) return;
          setClientSecret(piResult.data.clientSecret);
          setPaymentIntentId(piResult.data.paymentIntentId);
        }
      } catch (e) {
        console.error('Error initializing card form:', e);
        onError(e.message || 'Failed to initialize payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="text-center py-3">
        <span className="spinner-border spinner-border-sm mr-2"></span>
        Loading secure payment form...
      </div>
    );
  }

  if (!stripeInstance || (!feeEnabled && !clientSecret)) {
    return <div className="text-danger">Failed to load payment form</div>;
  }

  return (
    <Elements stripe={stripeInstance}>
      <ManualCardFormInner
        amount={amount}
        pos={pos}
        sessionId={sessionId}
        user={user}
        stripeAccountId={stripeAccountId}
        feeEnabled={feeEnabled}
        clientSecret={clientSecret}
        paymentIntentId={paymentIntentId}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

const ManualCardFormInner = ({ amount, pos, sessionId, user, feeEnabled, clientSecret, paymentIntentId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        onError(pmError.message || 'Invalid card details');
        setProcessing(false);
        return;
      }

      if (!feeEnabled) {
        // Original flow, unchanged: confirm the already-created
        // PaymentIntent directly with the tokenized payment method.
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });

        if (error) {
          onError(error.message || 'Card payment failed');
          setProcessing(false);
        } else if (paymentIntent.status === 'succeeded') {
          onSuccess({
            paymentIntentId,
            cardBrand: paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.brand,
            cardLast4: paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.last4,
            amount,
            convenienceFeeAmount: 0,
          });
        } else if (paymentIntent.status === 'requires_action') {
          onError('This card requires additional authentication. Please use terminal or cash instead.');
          setProcessing(false);
        } else {
          onError(`Payment status: ${paymentIntent.status}`);
          setProcessing(false);
        }
        return;
      }

      // Tokenize the card FIRST — this lets us check card.funding (credit
      // vs debit) server-side before deciding whether the Convenience Fee
      // applies, since card network rules only allow surcharging credit
      // cards, never debit/prepaid.
      const functions = getFunctions();
      const createCardPaymentIntentFn = httpsCallable(functions, 'createCardPaymentIntent');
      const timestamp = new Date();
      const confirmationNumber = `POS-${timestamp.getTime().toString().slice(-6)}`;

      const piResult = await createCardPaymentIntentFn({
        amount,
        currency: 'usd',
        description: `POS Sale #${confirmationNumber}`,
        locationId: pos?.locationId,
        paymentMethodId: paymentMethod.id,
        metadata: {
          posId: pos?.id,
          posSessionId: sessionId,
          userId: user?.uid,
          locationId: pos?.locationId,
        },
      });

      const { clientSecret: newClientSecret, paymentIntentId: newPaymentIntentId, amount: chargedAmount, convenienceFeeAmount } = piResult.data;
      if (!newClientSecret || !newPaymentIntentId) {
        onError(piResult.data?.error || 'Failed to create payment intent');
        setProcessing(false);
        return;
      }

      // The backend already created AND confirmed the PaymentIntent using
      // the tokenized payment method above. Only a 3DS/other authentication
      // challenge (status == requires_action) needs further client handling.
      let finalStatus = piResult.data.status;
      if (finalStatus === 'requires_action') {
        const { error: actionError, paymentIntent: handled } = await stripe.confirmCardPayment(newClientSecret);
        if (actionError) {
          onError(actionError.message || 'Card authentication failed');
          setProcessing(false);
          return;
        }
        finalStatus = handled.status;
      }

      if (finalStatus === 'succeeded') {
        onSuccess({
          paymentIntentId: newPaymentIntentId,
          cardBrand: paymentMethod.card?.brand,
          cardLast4: paymentMethod.card?.last4,
          amount: chargedAmount,
          convenienceFeeAmount,
        });
      } else {
        onError(`Payment status: ${finalStatus}`);
        setProcessing(false);
      }
    } catch (e) {
      console.error('Manual card payment error:', e);
      onError(e.message || 'Payment failed');
      setProcessing(false);
    }
  };

  return (
    <div>
      <p className="text-muted small mb-2">
        <i className="fas fa-shield-alt mr-1"></i>
        Card details are entered securely via Stripe. Nothing is stored locally.
      </p>
      <div
        className="form-control mb-3"
        style={{ padding: '12px 16px', height: 'auto', borderRadius: 8 }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <button
        className="btn btn-primary btn-block btn-lg font-weight-bold"
        style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '12px' }}
        onClick={handleSubmit}
        disabled={processing || !stripe}
      >
        {processing ? (
          <span className="spinner-border spinner-border-sm mr-2"></span>
        ) : (
          <i className="fas fa-check mr-2"></i>
        )}
        Complete Sale
      </button>
    </div>
  );
};

export default PosPage;
